import { ChannelMessage_Post_MentionSchema, Err } from "@towns-protocol/proto";
import { Client, Membership, MessageType, Observable, RiverDbManager, UnauthenticatedClient, contractAddressFromSpaceId, errorContains, getUserIdFromStreamId, isDMChannelStreamId, isGDMChannelStreamId, isSpaceStreamId, makeAppRegistryRpcClient, makeDefaultChannelStreamId, makeRiverRpcClient, makeSignerContext, makeSpaceStreamId, makeStreamRpcClient, makeUniqueChannelStreamId, makeUserStreamId, toMembership, transformAttachments, userIdFromAddress } from "@towns-protocol/sdk";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { AppRegistryDapp, FIXED_PRICING, ITippingShim, Permission, SpaceAddressFromSpaceId, SpaceDapp, SpaceIdFromSpaceAddress, convertRuleDataV2ToV1, decodeRuleDataV2, getFilteredRolesFromSpace, getSpaceReviewEventData } from "@towns-protocol/web3";
import { bin_fromBase64, bin_fromHexString, bin_toBase64, bin_toHexString, check, dlogger, isTestEnv } from "@towns-protocol/utils";
import { QueryClient, QueryClientProvider, queryOptions as queryOptions$1, useMutation, useQueries as useQueries$1, useQuery, useQueryClient as useQueryClient$1 } from "@tanstack/react-query";
import { datadogLogs } from "@datadog/browser-logs";
import { ethers } from "ethers";
import { request } from "graphql-request";
import { createPublicClient, encodeFunctionData, erc20Abi, fromHex, getAddress, hexToBigInt, http, isAddress, isHex, numberToHex, toHex, zeroAddress } from "viem";
import { debounce, isEqual } from "lodash-es";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { multicall, readContract } from "viem/actions";
import IAppAccountABI from "@towns-protocol/generated/dev/abis/IAppAccount.abi";
import { IAppRegistry__factory } from "@towns-protocol/generated/dev/typings/factories/IAppRegistry__factory";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import EventEmitter from "events";
import { UserOps, encodeERC7821Execute, getTransactionHashOrUserOpHash, isInsufficientTipBalanceException, isUserOpResponse } from "@towns/userops";
import { nanoid } from "nanoid";
import { toUtf8String } from "ethers/lib/utils";
import Dexie from "dexie";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";
import * as allChains from "viem/chains";
import { create as create$1, fromBinary, toBinary } from "@bufbuild/protobuf";
import { useShallow } from "zustand/react/shallow";

//#region src/client/TownsClientTypes.ts
let TransactionStatus = /* @__PURE__ */ function(TransactionStatus$1) {
	TransactionStatus$1["None"] = "None";
	TransactionStatus$1["Pending"] = "Pending";
	TransactionStatus$1["Success"] = "Success";
	TransactionStatus$1["Failed"] = "Failed";
	return TransactionStatus$1;
}({});
function createTransactionContext(props) {
	if (props.status === TransactionStatus.Success) if (props.transaction && props.receipt) return {
		status: props.status,
		data: props.data,
		transaction: props.transaction,
		receipt: props.receipt,
		error: void 0
	};
	else throw new Error("Invalid transaction context: missing transaction or receipt");
	return {
		status: props.status,
		data: props.data,
		transaction: props.transaction,
		receipt: void 0,
		error: props.error
	};
}
function logTxnResult(name, txn) {
	if (txn.status === TransactionStatus.Success) console.log(`[${name}]`, "Success", txn.data, txn);
	else if (txn.status === TransactionStatus.Failed) console.error(`[${name}]`, "Failed", txn.error, txn);
}
let JoinFlowStatus = /* @__PURE__ */ function(JoinFlowStatus$1) {
	JoinFlowStatus$1["MintingMembership"] = "minting-membership";
	JoinFlowStatus$1["WaitingForMembership"] = "waiting-for-membership";
	JoinFlowStatus$1["MembershipMinted"] = "membership-minted";
	JoinFlowStatus$1["JoiningRoom"] = "joining-room";
	JoinFlowStatus$1["JoiningAutojoinChannels"] = "joining-autojoin-channels";
	JoinFlowStatus$1["Done"] = "done";
	return JoinFlowStatus$1;
}({});
let CreateSpaceFlowStatus = /* @__PURE__ */ function(CreateSpaceFlowStatus$1) {
	CreateSpaceFlowStatus$1["MintingSpace"] = "minting-space";
	CreateSpaceFlowStatus$1["CreatingSpace"] = "creating-space";
	CreateSpaceFlowStatus$1["CreatingChannel"] = "creating-channel";
	CreateSpaceFlowStatus$1["CreatingUser"] = "creating-user";
	CreateSpaceFlowStatus$1["Error"] = "error";
	return CreateSpaceFlowStatus$1;
}({});

//#endregion
//#region src/components/UserLookupContext.tsx
const UserLookupContext = React.createContext(null);

//#endregion
//#region src/utils/isDefined.ts
function isDefined(value) {
	return value !== void 0 && value !== null;
}

//#endregion
//#region src/query/query-keys.ts
var QuerKeysEnum = /* @__PURE__ */ function(QuerKeysEnum$1) {
	QuerKeysEnum$1["FirstBySpaceIds"] = "spaceIds";
	QuerKeysEnum$1["ThenByRoleIds"] = "spaceRoleIds";
	QuerKeysEnum$1["ThenByChannelIds"] = "channelIds";
	return QuerKeysEnum$1;
}(QuerKeysEnum || {});
const blockchainKeys = {
	spaceAndChannel: (spaceId, channelId) => [
		"spacesAndChannels",
		QuerKeysEnum.FirstBySpaceIds,
		spaceId,
		QuerKeysEnum.ThenByChannelIds,
		channelId
	],
	hasPermission: (args) => ["hasPermission", args],
	roles: (spaceId) => [
		"roles",
		QuerKeysEnum.FirstBySpaceIds,
		spaceId
	],
	roleDetails: (spaceId, roleId) => [
		"rolesDetails",
		QuerKeysEnum.FirstBySpaceIds,
		spaceId,
		QuerKeysEnum.ThenByRoleIds,
		roleId
	],
	channelPermissionOverrides: (spaceId, roleId, channelId) => [
		"channelPermissionOverrides",
		QuerKeysEnum.FirstBySpaceIds,
		spaceId,
		QuerKeysEnum.ThenByRoleIds,
		roleId,
		channelId
	],
	spaceInfo: (spaceId) => [
		"spaceInfo",
		QuerKeysEnum.FirstBySpaceIds,
		spaceId
	],
	membershipStatus: (spaceId, userId) => [
		"membershipStatus",
		spaceId,
		userId
	],
	guardianStatus: (walletAddress) => ["guardianStatus", walletAddress],
	ownershipCooldown: () => ["ownershipCooldown"],
	spaceOwner: (spaceId) => ["spaceOwner", spaceId ?? "waitingForSpaceId"],
	allSpacesUserIsOwner: (userAddress) => ["allSpacesUserIsOwner", userAddress],
	channelInfo: (channelId, updatedAtKey) => [
		"channelInfo",
		"channelId",
		channelId,
		"updatedAtKey",
		updatedAtKey
	],
	multipleRoleDetails: (spaceId, roleIds) => {
		const queryKey = [
			"multipleRolesDetails",
			QuerKeysEnum.FirstBySpaceIds,
			spaceId,
			QuerKeysEnum.ThenByRoleIds
		];
		if (roleIds) queryKey.push(roleIds);
		return queryKey;
	},
	entitledChannels: (spaceId) => ["syncEntitledChannels", spaceId],
	membershipPrice: (spaceId) => ["membershipPrice", spaceId ?? "waitingForSpaceId"],
	membershipInfo: (spaceId) => ["membershipInfo", spaceId],
	linkedWallets: (walletAddress) => ["linkedWallets", walletAddress],
	linkedWalletsWithDelegations: (walletAddress) => ["linkedWalletsWithDelegations", walletAddress],
	rootKeyFromLinkedWallet: (walletAddress) => ["rootKeyFromLinkedWallet", walletAddress],
	hasMemberNft: (spaceId) => ["hasMemberNft", spaceId ?? "waitingForSpaceId"],
	walletAddressIsBanned: (userId, spaceId) => [
		"walletAddressIsBanned",
		userId,
		spaceId
	],
	bannedWalletAddresses: (spaceId) => ["bannedWalletAddresses", spaceId],
	pricingModules: () => ["pricingModules"],
	supportedXChainIds: () => ["supportedXChainIds"],
	prepaidSupply: (spaceId) => ["prepaidSupply", spaceId ?? "waitingForSpaceId"],
	minimumMembershipPrice: () => ["minimumMembershipPrice"],
	membershipMintLimit: () => ["membershipMintLimit"],
	membershipFreeAllocation: (spaceId) => ["membershipFreeAllocation", spaceId ?? "waitingForSpaceId"],
	membershipFee: () => ["membershipFee"],
	riverPoints: (walletAddress) => ["riverPoints", walletAddress],
	spaceTotalTips: (spaceId, currencies) => [
		"spaceTotalTips",
		spaceId,
		...currencies ? currencies.slice().sort() : []
	],
	tokenIdOfOwner: (spaceId, ownerAddress) => [
		"tokenIdOfOwner",
		spaceId ?? "waitingForSpaceId",
		ownerAddress
	],
	protocolFee: (spaceId) => ["protocolFee", spaceId ?? "waitingForSpaceId"],
	spaceReviews: (spaceId) => ["spaceReviews", spaceId],
	delegationDataByOperator: (operatorAddress) => [
		"delegation",
		"operator",
		operatorAddress
	],
	delegationDataBySpace: (spaceId) => [
		"delegation",
		"space",
		spaceId
	],
	townsTokenBalance: (address) => [
		"townsToken",
		"balance",
		address
	].filter(isDefined),
	myStakingRewards: () => [
		"staking",
		"rewards",
		"0xself"
	],
	myStakingDeposits: () => [
		"staking",
		"deposits",
		"list",
		"0xself"
	],
	stakingDepositDetails: (depositId) => [
		"staking",
		"deposits",
		"details",
		depositId.toString()
	],
	stakingDepositUnlockDate: (depositId) => [
		"rewardsDistribution",
		"delegationProxyById",
		depositId?.toString() ?? "waitingForDepositId"
	],
	operatorsYield: () => ["operators", "yield"],
	stakingReputationByUser: (userId) => [
		"staking",
		"reputation",
		userId
	].filter(Boolean),
	stakingRankedTopStakingTowns: () => [
		"staking",
		"reputation",
		"towns",
		"top"
	],
	stakingTopTowns: () => [
		"staking",
		"reputation",
		"town",
		"top"
	],
	stakingReputationByTown: (spaceId) => [
		"staking",
		"reputation",
		"town",
		spaceId ?? "waitingForSpaceId"
	],
	myBots: () => [
		"developer",
		"bots",
		"0xself"
	],
	botSettings: (appClientAddress) => [
		"developer",
		"bots",
		"settings",
		appClientAddress
	],
	botData: (appAddress) => [
		"bots",
		"data",
		appAddress
	],
	botStatus: (appClientAddress) => [
		"bots",
		"status",
		appClientAddress
	],
	agentPromotionStatus: (appAddress) => [
		"bots",
		"agent-promotion-status",
		appAddress
	],
	agentFeedbackSummary: (agentId) => [
		"bots",
		"agent-feedback-summary",
		agentId?.toString() ?? "undefined"
	],
	depositsDelegation: (depositIds) => [
		"deposits",
		"delegation",
		...depositIds.sort()
	],
	botInstalledInAccounts: (appAddress) => [
		"bots",
		"installed-in-accounts",
		appAddress
	],
	accountInstalledApps: (accountAddress) => [
		"accounts",
		"installed-apps",
		accountAddress
	],
	membershipRenewalPrice: (spaceId, tokenId) => [
		"membershipRenewalPrice",
		spaceId,
		tokenId
	],
	subscriptions: (walletAddress) => ["subscriptions", walletAddress],
	singleSubscription: (walletAddress, spaceAddress, tokenId) => [
		"singleSubscription",
		walletAddress,
		spaceAddress,
		tokenId
	],
	appExpiration: (spaceId, appAddress) => [
		"bots",
		"expiration",
		spaceId,
		appAddress
	],
	primaryWallet: (userId) => ["primaryWallet", userId],
	accountModulesInstalled: (walletAddress) => ["accountModulesInstalled", walletAddress]
};

//#endregion
//#region src/utils/time.ts
const SECOND_MS = 1e3;
const MINUTE_MS = SECOND_MS * 60;
const HOUR_MS = MINUTE_MS * 60;

//#endregion
//#region src/query/queryClient.ts
const defaultStaleTime = 15 * MINUTE_MS;
const staleTime24Hours = 24 * HOUR_MS;
let config;
if (isTestEnv()) config = { defaultOptions: { queries: { retry: false } } };
const queryClient = new QueryClient(config);
function useQuery$1(key, queryFn$1, options) {
	const { data, error, isError, isFetching, isPending, isLoading, isLoadingError, isRefetchError, isSuccess, status, refetch } = useQuery({
		queryKey: key,
		queryFn: queryFn$1,
		staleTime: defaultStaleTime,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		refetchOnMount: false,
		...options ?? {}
	});
	return {
		data,
		error,
		isError,
		isFetching,
		isPending,
		isLoading,
		isLoadingError,
		isRefetchError,
		isSuccess,
		status,
		refetch
	};
}
function useMutation$1(options, queryClient$1) {
	return useMutation(options, queryClient$1);
}

//#endregion
//#region src/generated/graphql/graphql.ts
const SpaceInfoDocument = {
	kind: "Document",
	definitions: [{
		kind: "OperationDefinition",
		operation: "query",
		name: {
			kind: "Name",
			value: "SpaceInfo"
		},
		variableDefinitions: [{
			kind: "VariableDefinition",
			variable: {
				kind: "Variable",
				name: {
					kind: "Name",
					value: "id"
				}
			},
			type: {
				kind: "NonNullType",
				type: {
					kind: "NamedType",
					name: {
						kind: "Name",
						value: "String"
					}
				}
			}
		}],
		selectionSet: {
			kind: "SelectionSet",
			selections: [{
				kind: "Field",
				name: {
					kind: "Name",
					value: "space"
				},
				arguments: [{
					kind: "Argument",
					name: {
						kind: "Name",
						value: "id"
					},
					value: {
						kind: "Variable",
						name: {
							kind: "Name",
							value: "id"
						}
					}
				}],
				selectionSet: {
					kind: "SelectionSet",
					selections: [
						{
							kind: "Field",
							name: {
								kind: "Name",
								value: "name"
							}
						},
						{
							kind: "Field",
							name: {
								kind: "Name",
								value: "longDescription"
							}
						},
						{
							kind: "Field",
							name: {
								kind: "Name",
								value: "shortDescription"
							}
						},
						{
							kind: "Field",
							name: {
								kind: "Name",
								value: "id"
							}
						},
						{
							kind: "Field",
							name: {
								kind: "Name",
								value: "owner"
							}
						},
						{
							kind: "Field",
							name: {
								kind: "Name",
								value: "tokenId"
							}
						},
						{
							kind: "Field",
							name: {
								kind: "Name",
								value: "uri"
							}
						},
						{
							kind: "Field",
							name: {
								kind: "Name",
								value: "paused"
							}
						},
						{
							kind: "Field",
							name: {
								kind: "Name",
								value: "createdAt"
							}
						}
					]
				}
			}]
		}
	}]
};

//#endregion
//#region src/base-reads/queries/space-info.ts
function spaceInfoFromGraphQL(space) {
	return {
		address: space.id,
		createdAt: ethers.BigNumber.from(space.createdAt).toString(),
		networkId: SpaceIdFromSpaceAddress(space.id),
		name: space.name,
		owner: ethers.utils.getAddress(space.owner),
		disabled: space.paused ?? false,
		shortDescription: space.shortDescription ?? "",
		longDescription: space.longDescription ?? "",
		tokenId: ethers.BigNumber.from(space.tokenId).toString(),
		uri: space.uri
	};
}
async function getSpaceInfo(args) {
	const { spaceDapp, subgraphEndpoint, spaceNetworkId } = args;
	const spaceAddress = SpaceAddressFromSpaceId(spaceNetworkId);
	if (!subgraphEndpoint) {
		datadogLogs.logger.warn("[getSpaceInfo] subgraphEndpoint not set", {
			query_upstream: "rpc",
			space_network_id: spaceNetworkId
		});
		const space = await spaceDapp.getSpaceInfo(spaceNetworkId);
		return space;
	}
	try {
		const data = await request(subgraphEndpoint, SpaceInfoDocument, { id: spaceAddress });
		if (!data || !data.space || !data.space.name) throw new Error(`No data returned from subgraph for space ${spaceNetworkId}`);
		const spaceInfo = spaceInfoFromGraphQL(data.space);
		console.log("[getSpaceInfo]", {
			query_upstream: "subgraph",
			space_network_id: spaceNetworkId
		});
		return spaceInfo;
	} catch (err) {
		datadogLogs.logger.error("[getSpaceInfo] error fetching space info from subgraphEndpoint", {
			query_upstream: "rpc",
			space_network_id: spaceNetworkId,
			error: err
		});
		const space = await spaceDapp.getSpaceInfo(spaceNetworkId);
		return space;
	}
}

//#endregion
//#region src/utils/provider-utils.ts
function makeProviderFromChain(chain) {
	const rpcUrl = chain.rpcUrls.default.http[0];
	return new ethers.providers.StaticJsonRpcProvider(rpcUrl, {
		chainId: chain.id,
		name: chain.name
	});
}
function makeProviderFromConfig(chain) {
	return new ethers.providers.StaticJsonRpcProvider(chain.rpcUrl, {
		chainId: chain.chainId,
		name: chain?.name ?? ""
	});
}

//#endregion
//#region src/base-reads/queries/get-membership-status.ts
async function getMembershipStatus(args) {
	const { spaceDapp, spaceNetworkId, queryArgs } = args;
	const { wallets } = queryArgs;
	const space = spaceDapp.getSpace(spaceNetworkId);
	if (!space) throw new Error(`Space ${spaceNetworkId} not found`);
	return space.getMembershipStatus(wallets);
}

//#endregion
//#region src/base-reads/queries/get-subscriptions.ts
async function getSubscriptions(args) {
	const { paymasterProxyUrl, walletAddress, paymasterProxyAuthSecret } = args;
	if (!paymasterProxyUrl) throw new Error("Paymaster proxy URL is required to get subscriptions");
	const response = await fetch(`${paymasterProxyUrl}/api/subscriptions/list/${walletAddress}`, { headers: { Authorization: `Bearer ${paymasterProxyAuthSecret}` } });
	if (!response.ok) throw new Error(`Failed to get subscriptions: ${response.statusText}`);
	const json = await response.json();
	if (!isSubscriptionsResponse(json)) throw new Error(`Invalid subscription response: ${JSON.stringify(json)}`);
	return json.data.subscriptions;
}
function isSubscriptionsResponse(data) {
	return typeof data === "object" && data !== null && "data" in data && typeof data.data === "object" && data.data !== null && "subscriptions" in data.data && Array.isArray(data.data.subscriptions) && data.data.subscriptions.every(isSubscription);
}
function isSingleSubscriptionResponse(data) {
	return typeof data === "object" && data !== null && "data" in data && typeof data.data === "object" && data.data !== null && "subscription" in data.data;
}
function isSubscription(data) {
	return typeof data === "object" && data !== null && "space" in data && "active" in data && "lastRenewalTime" in data && "nextRenewalTime" in data && "spent" in data && "tokenId" in data && "entityId" in data;
}
async function getSingleSubscription(args) {
	const { paymasterProxyUrl, walletAddress, spaceAddress, tokenId, paymasterProxyAuthSecret } = args;
	if (!paymasterProxyUrl) throw new Error("Paymaster proxy URL is required to get subscriptions");
	if (!isAddress(walletAddress)) throw new Error("Invalid wallet address");
	if (!isAddress(spaceAddress)) throw new Error("Invalid space address");
	let tokenIdHex = tokenId;
	if (!isHex(tokenId)) tokenIdHex = toHex(Number(tokenId));
	const response = await fetch(`${paymasterProxyUrl}/api/subscriptions/single/${walletAddress}/${spaceAddress}/${tokenIdHex}`, { headers: { Authorization: `Bearer ${paymasterProxyAuthSecret}` } });
	const json = await response.json();
	if (!response.ok) {
		if (json.message === "Subscription not found") {
			console.log("[getSingleSubscription] Subscription not found");
			return null;
		}
		throw new Error(`Failed to get subscriptions: ${response.statusText}`);
	}
	if (!isSingleSubscriptionResponse(json)) throw new Error(`Invalid subscription response: ${JSON.stringify(json)}`);
	if (json.data.subscription.tokenId === "0x0") {
		console.log("[getSingleSubscription] No subscription found for tokenId: ", tokenId);
		return null;
	}
	return json.data.subscription;
}

//#endregion
//#region src/base-reads/queries/is-entitled.ts
async function isEntitled(spaceDapp, { spaceId, channelId, user, permission, invalidateCache, xchainConfig }) {
	let isEntitled$1 = false;
	if (channelId && spaceId) isEntitled$1 = await spaceDapp.isEntitledToChannel(spaceId, channelId, user, permission, xchainConfig, invalidateCache);
	else if (spaceId) {
		if (permission === Permission.JoinSpace) return await isEntitledToJoinSpace(spaceDapp, {
			spaceId,
			rootKey: user,
			invalidateCache,
			xchainConfig
		});
		isEntitled$1 = await spaceDapp.isEntitledToSpace(spaceId, user, permission, invalidateCache);
	} else isEntitled$1 = true;
	return isEntitled$1;
}
async function isEntitledToJoinSpace(spaceDapp, { xchainConfig, spaceId, rootKey, invalidateCache }) {
	if (!spaceId) throw new Error("spaceId is required for permission JoinSpace");
	const entitledWallet = await spaceDapp.getEntitledWalletForJoiningSpace(spaceId, rootKey, xchainConfig, invalidateCache);
	console.log(`[isEntitledToJoinSpace] is user entitlted for Permission.JoinSpace`, {
		entitledWallet,
		isEntitled: !!entitledWallet,
		spaceId
	});
	return !!entitledWallet;
}

//#endregion
//#region src/base-reads/queries/get-wallets.ts
async function getLinkedWallets(spaceDapp, walletAddress) {
	const walletLink = spaceDapp.getWalletLink();
	return await walletLink.getLinkedWallets(walletAddress);
}
async function getRootKeyFromLinkedWallet(spaceDapp, walletAddress) {
	const walletLink = spaceDapp.getWalletLink();
	return await walletLink.getRootKeyForWallet(walletAddress);
}
async function getLinkedWalletsWithDelegations(spaceDapp, xchainConfig, walletAddress) {
	return await spaceDapp.getLinkedWalletsWithDelegations(walletAddress, xchainConfig);
}
async function walletAddressForMembership(args) {
	const { spaceDapp, spaceId, walletAddress, xchainConfig } = args;
	const wallets = await getLinkedWalletsWithDelegations(spaceDapp, xchainConfig, walletAddress);
	for (const walletAddress$1 of wallets) {
		const membershipStatus = await spaceDapp.getMembershipStatus(spaceId, [walletAddress$1]);
		if (membershipStatus?.isMember) return walletAddress$1;
	}
	return void 0;
}

//#endregion
//#region src/base-reads/queries/is-banned.ts
async function walletAddressIsBanned(args) {
	const { spaceDapp, xchainConfig, spaceId, walletAddress, opts } = args;
	const wallets = (await getLinkedWalletsWithDelegations(spaceDapp, xchainConfig, walletAddress)).concat(walletAddress);
	const promises = wallets.map((walletAddress$1) => {
		return spaceDapp.walletAddressIsBanned(spaceId, walletAddress$1, opts).then((result) => result === true ? Promise.resolve(true) : Promise.reject(false));
	});
	try {
		await Promise.any(promises);
		return true;
	} catch {
		return false;
	}
}

//#endregion
//#region src/base-reads/queries/get-membership-info.ts
async function getMembershipInfo(args) {
	const { spaceDapp, spaceId } = args;
	return spaceDapp.getMembershipInfo(spaceId);
}

//#endregion
//#region src/base-reads/queries/get-membership-free-allocation.ts
async function getMembershipFreeAllocation(args) {
	const { spaceDapp, spaceId } = args;
	return spaceDapp.getMembershipFreeAllocation(spaceId);
}

//#endregion
//#region src/base-reads/queries/get-protocol-fee.ts
async function getProtocolFee(args) {
	const { spaceDapp, spaceId } = args;
	const space = spaceDapp.getSpace(spaceId);
	if (!space) throw new Error(`Space ${spaceId} not found`);
	return space.getProtocolFee();
}

//#endregion
//#region src/base-reads/queries/get-platform-mint-limit.ts
async function getPlatformMintLimit(args) {
	const { spaceDapp } = args;
	return spaceDapp.platformRequirements.getMembershipMintLimit();
}

//#endregion
//#region src/base-reads/queries/get-token-id-of-owner.ts
async function getTokenIdOfOwner(args) {
	const { spaceDapp, spaceId, ownerAddress } = args;
	return spaceDapp.getTokenIdOfOwner(spaceId, ownerAddress);
}

//#endregion
//#region src/base-reads/queries/get-space-owner.ts
async function getSpaceOwner(args) {
	const { spaceDapp, spaceId } = args;
	const space = spaceDapp.getSpace(spaceId);
	if (!space) throw new Error(`Space ${spaceId} not found`);
	return space.Ownable.read.owner();
}

//#endregion
//#region src/base-reads/queries/get-pricing-modules.ts
async function getPricingModules(args) {
	const { spaceDapp } = args;
	return spaceDapp.listPricingModules();
}

//#endregion
//#region src/base-reads/queries/get-banned-wallet-addresses.ts
async function getBannedWalletAddresses(args) {
	const { spaceDapp, spaceId } = args;
	return spaceDapp.bannedWalletAddresses(spaceId);
}

//#endregion
//#region src/base-reads/queries/get-platform-min-membership-price.ts
async function getPlatformMinMembershipPrice(args) {
	const { spaceDapp } = args;
	return spaceDapp.platformRequirements.getMembershipMinPrice();
}

//#endregion
//#region src/base-reads/queries/get-platform-membership-fee.ts
async function getPlatformMembershipFee(args) {
	const { spaceDapp } = args;
	return spaceDapp.platformRequirements.getMembershipFee();
}

//#endregion
//#region src/store/use-offline-store.ts
const OFFLINE_STORE_NAME = "towns/offlineStore";
const generateOfflineUserKey = (spaceId, walletAddress) => {
	return `${spaceId}-${walletAddress}`;
};
const useOfflineStore = create()(persist((set) => ({
	skipIsRegisteredCheck: {},
	setSkipIsRegisteredCheck(userAddress) {
		set((state) => {
			if (state.skipIsRegisteredCheck[userAddress] === true) return state;
			return {
				...state,
				skipIsRegisteredCheck: {
					...state.skipIsRegisteredCheck,
					[userAddress]: true
				}
			};
		});
	},
	offlineSpaceInfoMap: {},
	setOfflineSpaceInfo(spaceInfo) {
		set((state) => {
			if (isEqual(state.offlineSpaceInfoMap[spaceInfo.networkId], spaceInfo)) return state;
			return {
				...state,
				offlineSpaceInfoMap: {
					...state.offlineSpaceInfoMap,
					[spaceInfo.networkId]: spaceInfo
				}
			};
		});
	},
	offlineChannelMetadataMap: {},
	setOfflineChannelInfo(value) {
		set((state) => {
			if (isEqual(state.offlineChannelMetadataMap[value.channel.channelNetworkId], value)) return state;
			return {
				...state,
				offlineChannelMetadataMap: {
					...state.offlineChannelMetadataMap,
					[value.channel.channelNetworkId]: value
				}
			};
		});
	},
	offlineWalletAddressMap: {},
	setOfflineWalletAddress: (userId, abstractAccountAddress) => set((state) => {
		if (state.offlineWalletAddressMap[userId] === abstractAccountAddress) return state;
		return {
			...state,
			offlineWalletAddressMap: {
				...state.offlineWalletAddressMap,
				[userId]: abstractAccountAddress
			}
		};
	}),
	removeOfflineWalletAddress: (userId) => set((state) => {
		const { [userId]: _,...rest } = state.offlineWalletAddressMap;
		return {
			...state,
			offlineWalletAddressMap: rest
		};
	}),
	offlineUserBioMap: {},
	setOfflineUserBio: (userId, bio) => {
		set((state) => {
			if (state.offlineUserBioMap[userId] === bio) return state;
			return {
				...state,
				offlineUserBioMap: {
					...state.offlineUserBioMap,
					[userId]: bio
				}
			};
		});
	},
	offlineMembershipStatusMap: {},
	setOfflineMembershipStatus: (args) => {
		set((state) => {
			const membershipStatus = {
				...args.membershipStatus,
				expiryTime: args.membershipStatus.expiryTime !== void 0 ? fromBigIntToHex(args.membershipStatus.expiryTime) : void 0
			};
			return {
				...state,
				offlineMembershipStatusMap: {
					...state.offlineMembershipStatusMap,
					[args.userId]: {
						...state.offlineMembershipStatusMap[args.userId],
						[args.spaceId]: membershipStatus
					}
				}
			};
		});
	},
	removeOfflineMembershipStatus: (args) => {
		set((state) => {
			const offlineMembershipStatus = state.offlineMembershipStatusMap[args.userId];
			if (!offlineMembershipStatus) return state;
			const { [args.spaceId]: _,...rest } = offlineMembershipStatus;
			return {
				...state,
				offlineMembershipStatusMap: {
					...state.offlineMembershipStatusMap,
					[args.userId]: { ...rest }
				}
			};
		});
	}
}), {
	name: OFFLINE_STORE_NAME,
	storage: createJSONStorage(() => localStorage),
	version: 3,
	migrate: (persistedState, previousVersion) => {
		if (previousVersion < 3) {
			if (persistedState && typeof persistedState === "object" && persistedState !== null) {
				const state = persistedState;
				if ("offlineMembershipStatusMap" in state) {
					const { offlineMembershipStatusMap: _,...cleanState } = state;
					return {
						...cleanState,
						offlineMembershipStatusMap: {}
					};
				}
			}
		}
		return persistedState;
	}
}));
function getOfflineMembershipStatus(args) {
	const { userId, spaceId } = args;
	const { offlineMembershipStatusMap } = useOfflineStore.getState();
	const offlineMembershipStatus = offlineMembershipStatusMap[userId]?.[spaceId];
	if (!offlineMembershipStatus) return;
	return {
		...offlineMembershipStatus,
		expiryTime: offlineMembershipStatus.expiryTime ? fromHexToBigInt(offlineMembershipStatus.expiryTime) : void 0
	};
}
function fromBigIntToHex(value) {
	if (typeof value === "bigint") return numberToHex(value);
	if (isHex(value)) return value;
	throw new Error("Value is not a bigint");
}
function fromHexToBigInt(value) {
	if (typeof value === "bigint") return value;
	if (isHex(value)) return hexToBigInt(value);
	throw new Error("Value is not a hex");
}

//#endregion
//#region src/base-reads/queries/get-root-key-from-linked-wallet-cached.ts
async function getRootKeyFromLinkedWalletCached(args) {
	const { spaceDapp, walletAddress } = args;
	const offlineWalletAddressMap = useOfflineStore.getState().offlineWalletAddressMap;
	const cachedRootKey = Object.keys(offlineWalletAddressMap).find((key) => offlineWalletAddressMap[key] === walletAddress);
	if (cachedRootKey) return cachedRootKey;
	const walletLink = spaceDapp.getWalletLink();
	const returnVal = await walletLink.getRootKeyForWallet(walletAddress);
	return returnVal;
}

//#endregion
//#region src/base-reads/queries/get-roles.ts
async function getRoles(args) {
	const { spaceDapp, spaceId } = args;
	return getFilteredRolesFromSpace(spaceDapp, spaceId);
}

//#endregion
//#region src/base-reads/queries/get-role-details.ts
async function getRoleDetails(args) {
	const { spaceDapp, spaceId, roleId } = args;
	return spaceDapp.getRole(spaceId, roleId);
}
async function getMultipleRoleDetails(args) {
	const { spaceDapp, spaceId, roleIds } = args;
	const getRolePromises = [];
	for (const roleId of roleIds) getRolePromises.push(spaceDapp.getRole(spaceId, roleId));
	const roles = await Promise.all(getRolePromises);
	return roles.filter((role) => role !== null);
}

//#endregion
//#region src/base-reads/queries/get-permission-overrides.ts
async function getPermissionsByRoleId(args) {
	const { spaceDapp, spaceId, roleId } = args;
	return spaceDapp.getPermissionsByRoleId(spaceId, roleId);
}
async function getChannelPermissionOverrides(args) {
	const { spaceDapp, spaceId, roleId, channelId } = args;
	return spaceDapp.getChannelPermissionOverrides(spaceId, roleId, channelId.startsWith("0x") ? channelId : `0x${channelId}`);
}

//#endregion
//#region src/base-reads/queries/get-channel-settings.ts
async function getChannelSettings(args) {
	const { spaceDapp, spaceId, channelId } = args;
	return spaceDapp.getChannelDetails(spaceId, channelId);
}

//#endregion
//#region src/base-reads/queries/get-space-delegation.ts
async function getSpaceDelegationsByOperator(args) {
	const { spaceDapp, operatorAddress } = args;
	const total = await spaceDapp.baseRegistry.spaceDelegation.read.getTotalDelegation(operatorAddress);
	const spaces = await spaceDapp.baseRegistry.spaceDelegation.read.getSpaceDelegationsByOperator(operatorAddress);
	return {
		totalDelegation: total.toString(),
		delegatedSpaces: spaces
	};
}
async function getSpaceDelegationOperator(args) {
	const { spaceDapp, spaceId } = args;
	const operator = await spaceDapp.baseRegistry.spaceDelegation.read.getSpaceDelegation(spaceId);
	return isAddress(operator) ? operator : void 0;
}

//#endregion
//#region src/base-reads/queries/get-space-tips.ts
async function getSpaceTips(args) {
	const { spaceDapp, spaceId, currencies } = args;
	const space = spaceDapp.getSpace(spaceId);
	if (!space) return currencies.map(() => ({
		count: 0n,
		amount: 0n,
		currency: zeroAddress
	}));
	return Promise.all(currencies.map((currency) => space.totalTips({ currency }).then((args$1) => ({
		...args$1,
		currency
	}))));
}

//#endregion
//#region src/base-reads/queries/get-app-expiration.ts
async function getAppExpiration(args) {
	const { spaceDapp, spaceId, appAddress } = args;
	const space = spaceDapp.getSpace(spaceId);
	if (!space) return void 0;
	const expiry = await space.AppAccount.read.getAppExpiration(appAddress);
	return expiry;
}

//#endregion
//#region src/base-reads/queries/get-staking-deposits-by-owner.ts
async function getStakingDepositsByOwner(args) {
	const { spaceDapp, owner } = args;
	try {
		const depositIds = await spaceDapp.baseRegistry.rewardsDistributionV2.read.getDepositsByDepositor(owner);
		return depositIds.map((id) => id.toBigInt());
	} catch (error) {
		console.error("Error fetching deposit IDs:", error);
		return [];
	}
}

//#endregion
//#region src/base-reads/queries/get-staking-deposit-details.ts
async function getStakingDepositDetails(args) {
	const { spaceDapp, depositId } = args;
	try {
		const deposit = await spaceDapp.baseRegistry.rewardsDistributionV2.read.depositById(depositId);
		return {
			depositId,
			amount: deposit.amount,
			delegatee: deposit.delegatee,
			beneficiary: deposit.beneficiary,
			commissionEarningPower: deposit.commissionEarningPower,
			pendingWithdrawal: deposit.pendingWithdrawal
		};
	} catch (error) {
		console.error(`Error fetching deposit ${depositId}:`, error);
		return null;
	}
}

//#endregion
//#region src/base-reads/queries/get-staking-rewards.ts
async function getStakingRewards(args) {
	const { spaceDapp, beneficiary } = args;
	try {
		const result = await spaceDapp.baseRegistry.rewardsDistributionV2.read.currentReward(beneficiary);
		return result;
	} catch (error) {
		console.error("[getStakingRewards] Error fetching current reward:", error);
		throw error;
	}
}

//#endregion
//#region src/base-reads/queries/get-towns-token-balance.ts
async function getTownsTokenBalance(args) {
	const { spaceDapp, address } = args;
	if (!spaceDapp?.townsToken) throw new Error("Towns token contract not available");
	try {
		const balance = await spaceDapp.townsToken.read.balanceOf(address);
		return balance.toBigInt();
	} catch (error) {
		console.error("Failed to fetch token balance:", error);
		throw error;
	}
}

//#endregion
//#region src/base-reads/queries/get-river-points.ts
const SECONDS_MS = 1e3;
async function getRiverPoints(args) {
	const { dapp, address } = args;
	if (!dapp || !address) return;
	const [riverPoints, currentStreak, lastCheckIn] = await Promise.all([
		dapp.balanceOf(address).then((value) => {
			return Math.round(Number(ethers.utils.formatEther(value)));
		}),
		dapp.getCurrentStreak(address).then((value) => Number(value)),
		dapp.getLastCheckIn(address).then((value) => {
			return Number(value) * SECONDS_MS;
		})
	]);
	return {
		riverPoints,
		currentStreak,
		lastCheckIn
	};
}

//#endregion
//#region src/base-reads/queries/get-primary-wallet.ts
async function getPrimaryWallet(args) {
	const { spaceDapp, userId } = args;
	return spaceDapp.walletLink.getDefaultWallet(userId);
}

//#endregion
//#region src/base-reads/queries/check-if-linked.ts
async function checkIfLinked(args) {
	const { spaceDapp, rootKey, wallet } = args;
	if (!isAddress(rootKey)) throw new Error("rootKey is not a valid address");
	if (!isAddress(wallet)) throw new Error("wallet is not a valid address");
	return spaceDapp.walletLink.checkIfLinked(rootKey, wallet);
}

//#endregion
//#region src/base-reads/queries/get-installed-apps.ts
async function getInstalledApps(args) {
	const { client, accountAddress, appRegistryAddress } = args;
	const installedAppsWithClient = new Map();
	try {
		if (!client || !accountAddress || !appRegistryAddress) return installedAppsWithClient;
		let apps;
		try {
			apps = await readContract(client, {
				address: accountAddress,
				abi: IAppAccountABI,
				functionName: "getInstalledApps"
			});
		} catch {
			return installedAppsWithClient;
		}
		if (apps.length === 0) return installedAppsWithClient;
		const appIdResults = await multicall(client, {
			contracts: apps.map((appAddress) => ({
				address: appRegistryAddress,
				abi: IAppRegistry__factory.abi,
				functionName: "getLatestAppId",
				args: [appAddress]
			})),
			allowFailure: true
		});
		const validResults = [];
		appIdResults.forEach((r, i) => {
			if (r.status === "success" && r.result) validResults.push({
				appId: r.result,
				appAddress: apps[i],
				index: i
			});
		});
		if (validResults.length === 0) return installedAppsWithClient;
		const appResults = await multicall(client, {
			contracts: validResults.map(({ appId }) => ({
				address: appRegistryAddress,
				abi: IAppRegistry__factory.abi,
				functionName: "getAppById",
				args: [appId]
			})),
			allowFailure: true
		});
		appResults.forEach((result, i) => {
			if (result.status === "success" && result.result) {
				const appData = result.result;
				const clientAddress = appData.client;
				if (clientAddress) installedAppsWithClient.set(clientAddress.toLowerCase(), {
					appId: validResults[i].appId.toLowerCase(),
					appAddress: validResults[i].appAddress.toLowerCase()
				});
			}
		});
	} catch (error) {
		console.error("[getInstalledApps] error getting installed apps", error);
		return new Map();
	}
	return installedAppsWithClient;
}

//#endregion
//#region src/base-reads/BaseReadsProvider.tsx
const BaseReadsContext = createContext(void 0);
const BaseReadsProvider = ({ children, subgraphEndpoint, baseChainConfig, baseChain, paymasterProxyUrl, paymasterProxyAuthSecret, xchainConfig }) => {
	const baseProvider = useMemo(() => {
		return makeProviderFromChain(baseChain);
	}, [baseChain]);
	const spaceDapp = useMemo(() => {
		return new SpaceDapp(baseChainConfig, baseProvider);
	}, [baseChainConfig, baseProvider]);
	const appRegistryDapp = useMemo(() => {
		return new AppRegistryDapp(baseChainConfig, baseProvider);
	}, [baseChainConfig, baseProvider]);
	const publicClient = useMemo(() => {
		return createPublicClient({
			chain: baseChain,
			transport: http(baseChain.rpcUrls.default.http[0])
		});
	}, [baseChain]);
	const value = useMemo(() => {
		return createBaseReadsContext({
			spaceDapp,
			appRegistryDapp,
			publicClient,
			subgraphEndpoint,
			paymasterProxyUrl: paymasterProxyUrl || "",
			paymasterProxyAuthSecret: paymasterProxyAuthSecret || "",
			xchainConfig,
			baseProvider
		});
	}, [
		spaceDapp,
		appRegistryDapp,
		publicClient,
		subgraphEndpoint,
		paymasterProxyUrl,
		paymasterProxyAuthSecret,
		xchainConfig,
		baseProvider
	]);
	return /* @__PURE__ */ jsx(BaseReadsContext.Provider, {
		value,
		children
	});
};
const useBaseReads = () => {
	const context = useContext(BaseReadsContext);
	if (context === void 0) throw new Error("useBaseReads must be used within a BaseReadsProvider");
	return context;
};
const createBaseReadsContext = (contextBaseParams) => {
	return {
		...contextBaseParams,
		getSpaceInfo: (spaceNetworkId) => getSpaceInfo({
			spaceDapp: contextBaseParams.spaceDapp,
			subgraphEndpoint: contextBaseParams.subgraphEndpoint,
			spaceNetworkId
		}),
		getMembershipStatus: (spaceNetworkId, wallets) => getMembershipStatus({
			spaceDapp: contextBaseParams.spaceDapp,
			subgraphEndpoint: contextBaseParams.subgraphEndpoint,
			spaceNetworkId,
			queryArgs: { wallets }
		}),
		getSubscriptions: (walletAddress) => getSubscriptions({
			paymasterProxyUrl: contextBaseParams.paymasterProxyUrl,
			paymasterProxyAuthSecret: contextBaseParams.paymasterProxyAuthSecret,
			walletAddress
		}),
		getSingleSubscription: (args) => getSingleSubscription({
			paymasterProxyUrl: contextBaseParams.paymasterProxyUrl,
			paymasterProxyAuthSecret: contextBaseParams.paymasterProxyAuthSecret,
			...args
		}),
		getLinkedWallets: (walletAddress) => getLinkedWallets(contextBaseParams.spaceDapp, walletAddress),
		getRootKeyFromLinkedWallet: (walletAddress) => getRootKeyFromLinkedWallet(contextBaseParams.spaceDapp, walletAddress),
		getLinkedWalletsWithDelegations: (walletAddress) => getLinkedWalletsWithDelegations(contextBaseParams.spaceDapp, contextBaseParams.xchainConfig, walletAddress),
		isEntitled: (args) => isEntitled(contextBaseParams.spaceDapp, {
			...args,
			xchainConfig: contextBaseParams.xchainConfig
		}),
		walletAddressForMembership: (spaceId, walletAddress) => walletAddressForMembership({
			spaceDapp: contextBaseParams.spaceDapp,
			spaceId,
			walletAddress,
			xchainConfig: contextBaseParams.xchainConfig
		}),
		walletAddressIsBanned: (spaceId, walletAddress, opts) => walletAddressIsBanned({
			spaceDapp: contextBaseParams.spaceDapp,
			xchainConfig: contextBaseParams.xchainConfig,
			spaceId,
			walletAddress,
			opts
		}),
		getMembershipInfo: (spaceId) => getMembershipInfo({
			spaceDapp: contextBaseParams.spaceDapp,
			spaceId
		}),
		getMembershipFreeAllocation: (spaceId) => getMembershipFreeAllocation({
			spaceDapp: contextBaseParams.spaceDapp,
			spaceId
		}),
		getProtocolFee: (spaceId) => getProtocolFee({
			spaceDapp: contextBaseParams.spaceDapp,
			spaceId
		}),
		getPlatformMintLimit: () => getPlatformMintLimit({ spaceDapp: contextBaseParams.spaceDapp }),
		getTokenIdOfOwner: (spaceId, ownerAddress) => getTokenIdOfOwner({
			spaceDapp: contextBaseParams.spaceDapp,
			spaceId,
			ownerAddress
		}),
		getSpaceOwner: (spaceId) => getSpaceOwner({
			spaceDapp: contextBaseParams.spaceDapp,
			spaceId
		}),
		getPricingModules: () => getPricingModules({ spaceDapp: contextBaseParams.spaceDapp }),
		getBannedWalletAddresses: (spaceId) => getBannedWalletAddresses({
			spaceDapp: contextBaseParams.spaceDapp,
			spaceId
		}),
		getPlatformMinMembershipPrice: () => getPlatformMinMembershipPrice({ spaceDapp: contextBaseParams.spaceDapp }),
		getPlatformMembershipFee: () => getPlatformMembershipFee({ spaceDapp: contextBaseParams.spaceDapp }),
		getRootKeyFromLinkedWalletCached: (walletAddress) => getRootKeyFromLinkedWalletCached({
			spaceDapp: contextBaseParams.spaceDapp,
			walletAddress
		}),
		getRoles: (spaceId) => getRoles({
			spaceDapp: contextBaseParams.spaceDapp,
			spaceId
		}),
		getRoleDetails: (spaceId, roleId) => getRoleDetails({
			spaceDapp: contextBaseParams.spaceDapp,
			spaceId,
			roleId
		}),
		getMultipleRoleDetails: (spaceId, roleIds) => getMultipleRoleDetails({
			spaceDapp: contextBaseParams.spaceDapp,
			spaceId,
			roleIds
		}),
		getPermissionsByRoleId: (spaceId, roleId) => getPermissionsByRoleId({
			spaceDapp: contextBaseParams.spaceDapp,
			spaceId,
			roleId
		}),
		getChannelPermissionOverrides: (spaceId, roleId, channelId) => getChannelPermissionOverrides({
			spaceDapp: contextBaseParams.spaceDapp,
			spaceId,
			roleId,
			channelId
		}),
		getChannelSettings: (spaceId, channelId) => getChannelSettings({
			spaceDapp: contextBaseParams.spaceDapp,
			spaceId,
			channelId
		}),
		getSpaceDelegationsByOperator: (operatorAddress) => getSpaceDelegationsByOperator({
			spaceDapp: contextBaseParams.spaceDapp,
			operatorAddress
		}),
		getSpaceDelegationOperator: (spaceId) => getSpaceDelegationOperator({
			spaceDapp: contextBaseParams.spaceDapp,
			spaceId
		}),
		getSpaceTips: (spaceId, currencies) => getSpaceTips({
			spaceDapp: contextBaseParams.spaceDapp,
			spaceId,
			currencies
		}),
		getAppExpiration: (spaceId, appAddress) => getAppExpiration({
			spaceDapp: contextBaseParams.spaceDapp,
			spaceId,
			appAddress
		}),
		getStakingDepositsByOwner: (owner) => getStakingDepositsByOwner({
			spaceDapp: contextBaseParams.spaceDapp,
			owner
		}),
		getStakingDepositDetails: (depositId) => getStakingDepositDetails({
			spaceDapp: contextBaseParams.spaceDapp,
			depositId
		}),
		getStakingRewards: (beneficiary) => getStakingRewards({
			spaceDapp: contextBaseParams.spaceDapp,
			beneficiary
		}),
		getTownsTokenBalance: (address) => getTownsTokenBalance({
			spaceDapp: contextBaseParams.spaceDapp,
			address
		}),
		getRiverPoints: (address) => getRiverPoints({
			dapp: contextBaseParams.spaceDapp.airdrop,
			address
		}),
		getPrimaryWallet: (userId) => getPrimaryWallet({
			spaceDapp: contextBaseParams.spaceDapp,
			userId
		}),
		checkIfLinked: (rootKey, wallet) => checkIfLinked({
			spaceDapp: contextBaseParams.spaceDapp,
			rootKey,
			wallet
		}),
		getInstalledApps: (accountAddress) => getInstalledApps({
			client: contextBaseParams.publicClient,
			accountAddress,
			appRegistryAddress: contextBaseParams.appRegistryDapp.registry.address
		})
	};
};

//#endregion
//#region src/hooks/CasablancClient/useCasablancaStream.ts
function useCasablancaStream(streamId) {
	const { casablancaClient } = useTownsContext();
	const [stream, setStream] = useState(() => streamId ? casablancaClient?.stream(streamId) : void 0);
	useEffect(() => {
		if (!casablancaClient || !streamId) return;
		const stream$1 = casablancaClient.stream(streamId);
		if (stream$1) {
			setStream(stream$1);
			return;
		}
		const onStreamInitialized = (inStreamId) => {
			if (inStreamId === streamId) {
				const stream$2 = casablancaClient.stream(streamId);
				setStream(stream$2);
				casablancaClient.off("streamInitialized", onStreamInitialized);
			}
		};
		casablancaClient.on("streamInitialized", onStreamInitialized);
		return () => {
			casablancaClient.off("streamInitialized", onStreamInitialized);
		};
	}, [casablancaClient, streamId]);
	return stream;
}

//#endregion
//#region src/base-reads/hooks/use-membership-status.ts
const bustMembershipStatus = async (args) => {
	const { userId, spaceId } = args;
	const { removeOfflineMembershipStatus } = useOfflineStore.getState();
	removeOfflineMembershipStatus({
		userId,
		spaceId
	});
	await queryClient.invalidateQueries({ queryKey: blockchainKeys.membershipStatus(spaceId, userId) });
};
const debouncedBustMembershipStatus = debounce((args) => bustMembershipStatus(args), 2e3);
const queryFn = async (args) => {
	const { userId, spaceId, wallets, getMembershipStatus: getMembershipStatus$1 } = args;
	const offlineStore = useOfflineStore.getState();
	const { setOfflineMembershipStatus, removeOfflineMembershipStatus } = offlineStore;
	const offlineMembershipStatus = getSafeOfflineMembershipStatus({
		userId,
		spaceId
	});
	if (offlineMembershipStatus) return offlineMembershipStatus;
	else removeOfflineMembershipStatus({
		userId,
		spaceId
	});
	const result = await getMembershipStatus$1(spaceId, wallets);
	if (result.isMember && result.tokenId && isDefinedExpiryTime(result.expiryTime)) setOfflineMembershipStatus({
		userId,
		spaceId,
		membershipStatus: {
			isMember: result.isMember,
			tokenId: result.tokenId,
			expiryTime: result.expiryTime
		}
	});
	return result;
};
/**
* Hook to get membership status for a user in a space.
* - polls every 30s when no expiry time is known
* - schedules refetch for exactly when membership expires
* - uses offline cache to reduce unnecessary network calls (if the user is a member w/ unexpired token)
*/
function useMembershipStatus(args) {
	const { spaceNetworkId, userId, wallets } = args;
	const { getMembershipStatus: getMembershipStatus$1 } = useBaseReads();
	const queryKey = blockchainKeys.membershipStatus(spaceNetworkId, userId);
	useBustMembershipStatusOnMembeshipChange(userId, spaceNetworkId);
	const queryResult = useQuery$1(queryKey, () => queryFn({
		userId,
		spaceId: spaceNetworkId,
		wallets,
		getMembershipStatus: getMembershipStatus$1
	}), {
		enabled: !!wallets.length && wallets.some((w) => w !== void 0) && userId.length > 0 && spaceNetworkId.length > 0,
		refetchInterval: (args$1) => {
			const { state } = args$1;
			if (state.data?.expiryTime) {
				const timeUntilExpiry = timeUntilExpiryOrNegativeIfExpired(state.data.expiryTime);
				if (timeUntilExpiry <= 0) return 3e4;
				if (timeUntilExpiry <= 5 * 60 * 1e3) return 6e4;
				if (timeUntilExpiry <= 60 * 60 * 1e3) return 5 * 60 * 1e3;
				return Math.min(timeUntilExpiry + 1e3, 30 * 60 * 1e3);
			}
			return 3e4;
		}
	});
	return queryResult;
}
/**
* Hook to get membership status for multiple spaces.
*
* TODO:
* This shouldn't be too bad b/c membership statuses are cached locally until they expire
* But if a user has a bunch of expired memberships, this is going to make a lot of RPC calls
* We should start indexing memberships on subgraph
*/
function useMultipleMembershipStatus(args) {
	const { spaceIds, userId, wallets, batchSize = 5 } = args;
	const { getMembershipStatus: getMembershipStatus$1 } = useBaseReads();
	const enabled = !!wallets.length && wallets.some((w) => w !== void 0) && userId.length > 0;
	const [membershipStatuses, setMembershipStatuses] = useState(new Map());
	const isFetchingRef = useRef(false);
	useEffect(() => {
		if (!enabled || isFetchingRef.current) return;
		const fetchMemberships = async () => {
			isFetchingRef.current = true;
			setMembershipStatuses(new Map());
			const results = new Map();
			try {
				for (let i = 0; i < spaceIds.length; i += batchSize) {
					const batch = spaceIds.slice(i, i + batchSize);
					console.log("[useMultipleMembershipStatus] Fetching batch", {
						start: i,
						end: Math.min(i + batchSize, spaceIds.length),
						total: spaceIds.length
					});
					const batchResults = await Promise.allSettled(batch.map(async (spaceId) => {
						const queryKey = blockchainKeys.membershipStatus(spaceId, userId);
						const cachedData = queryClient.getQueryData(queryKey);
						if (cachedData !== void 0) {
							console.log("[useMultipleMembershipStatus] Using cached", spaceId);
							return {
								spaceId,
								data: cachedData
							};
						}
						console.log("[useMultipleMembershipStatus] Fetching", spaceId);
						const data = await queryClient.fetchQuery({
							queryKey,
							queryFn: () => queryFn({
								userId,
								spaceId,
								wallets,
								getMembershipStatus: getMembershipStatus$1
							})
						});
						return {
							spaceId,
							data
						};
					}));
					batchResults.forEach((result, index) => {
						const spaceId = batch[index];
						if (result.status === "fulfilled") results.set(spaceId, result.value.data);
						else {
							console.error("[useMultipleMembershipStatus] Failed to fetch", spaceId, result.reason);
							results.set(spaceId, {
								isMember: false,
								isExpired: true
							});
						}
					});
					setMembershipStatuses(new Map(results));
				}
				console.log("[useMultipleMembershipStatus] All fetched", { total: results.size });
			} finally {
				isFetchingRef.current = false;
			}
		};
		fetchMemberships();
	}, [
		enabled,
		spaceIds,
		userId,
		wallets,
		getMembershipStatus$1,
		batchSize
	]);
	const isLoading = membershipStatuses.size < spaceIds.length;
	return {
		data: membershipStatuses,
		isLoading
	};
}
async function fetchMembershipStatus(args) {
	const result = await queryFn({ ...args });
	queryClient.setQueryData(blockchainKeys.membershipStatus(args.spaceId, args.userId), result);
	return result;
}
function useBustMembershipStatusOnMembeshipChange(userId, spaceId) {
	const userStream = useCasablancaStream(userId ? makeUserStreamId(userId) : void 0);
	const space = useSpaceData();
	const spaceChannels = space?.channelGroups.flatMap((cg) => cg.channels);
	useEffect(() => {
		function onUserStreamMembershipChanged(streamId) {
			if (spaceId === streamId || spaceChannels?.find((c) => c.id === streamId)) debouncedBustMembershipStatus({
				userId,
				spaceId
			});
		}
		userStream?.on("userStreamMembershipChanged", onUserStreamMembershipChanged);
		return () => {
			userStream?.off("userStreamMembershipChanged", onUserStreamMembershipChanged);
		};
	}, [
		spaceId,
		userId,
		userStream,
		spaceChannels,
		space?.id
	]);
}
function expiryTimeToMs(expiryTime) {
	if (expiryTime > Number.MAX_SAFE_INTEGER) {
		console.error(`Expiry time ${expiryTime} exceeds safe integer range`);
		return -1;
	}
	return Number(expiryTime) * 1e3;
}
function timeUntilExpiryOrNegativeIfExpired(expiryTime) {
	const expiryTimeMs = expiryTimeToMs(expiryTime);
	const now = Date.now();
	return expiryTimeMs - now;
}
function isMembershipExpired(expiryTime) {
	if (expiryTime === 0n) return false;
	const expiryTimeMs = expiryTimeToMs(expiryTime);
	const now = Date.now();
	return expiryTimeMs < now;
}
function getSafeOfflineMembershipStatus(args) {
	const { userId, spaceId } = args;
	const offlineMembershipStatus = getOfflineMembershipStatus({
		userId,
		spaceId
	});
	const { isMember, tokenId, expiryTime } = offlineMembershipStatus ?? {};
	if (isMember && tokenId && isDefinedExpiryTime(expiryTime) && !isMembershipExpired(expiryTime)) return {
		isMember,
		tokenId,
		expiryTime
	};
}
const isDefinedExpiryTime = (expiryTime) => {
	return expiryTime !== void 0;
};

//#endregion
//#region src/base-reads/hooks/use-guardian-status.ts
function getGuardianStatus(args) {
	const { spaceDapp, walletAddress } = args;
	return Promise.all([getIsGuardianEnabled({
		spaceDapp,
		walletAddress
	}), getCooldown({
		spaceDapp,
		walletAddress
	})]);
}
function useGuardianStatus(args) {
	const { walletAddress, enabled } = args;
	const { spaceDapp } = useBaseReads();
	return useQuery$1(blockchainKeys.guardianStatus(walletAddress ?? ""), () => {
		if (walletAddress) return getGuardianStatus({
			spaceDapp,
			walletAddress
		});
	}, {
		enabled: !!walletAddress && enabled,
		refetchInterval: 60 * 1e3 * 5,
		select: (data) => {
			if (!data) return;
			const [isGuardianEnabled, guardianCooldown] = data;
			return {
				isGuardianEnabled,
				guardianCooldown
			};
		}
	});
}
function useDefaultOwnershipCooldown() {
	const { spaceDapp } = useBaseReads();
	return useQuery$1(blockchainKeys.ownershipCooldown(), () => spaceDapp.spaceOwner.getDefaultCooldown(), { staleTime: Infinity });
}
function getCooldown(args) {
	const { spaceDapp, walletAddress } = args;
	return spaceDapp.spaceOwner.guardianCooldown(walletAddress);
}
function getIsGuardianEnabled(args) {
	const { spaceDapp, walletAddress } = args;
	return spaceDapp.spaceOwner.isGuardianEnabled(walletAddress);
}
async function invalidateGuardianStatus(args) {
	const { walletAddress } = args;
	await queryClient.invalidateQueries({
		queryKey: blockchainKeys.guardianStatus(walletAddress),
		refetchType: "all"
	});
}
async function fetchGuardianStatus(args) {
	const { spaceDapp, walletAddress } = args;
	return queryClient.fetchQuery({
		queryKey: blockchainKeys.guardianStatus(walletAddress),
		queryFn: () => getGuardianStatus({
			spaceDapp,
			walletAddress
		})
	});
}

//#endregion
//#region src/base-reads/queries/get-membership-renewal-price.ts
async function getMembershipRenewalPrice(args) {
	const { spaceDapp, spaceId, tokenId } = args;
	const space = spaceDapp.getSpace(spaceId);
	if (!space) throw new Error(`Space ${spaceId} not found`);
	return space.getMembershipRenewalPrice(tokenId);
}

//#endregion
//#region src/base-reads/hooks/use-membership-renewal-price.ts
function useMembershipRenewalPrice(args) {
	const { spaceId, tokenId } = args;
	const { spaceDapp } = useBaseReads();
	return useQuery$1(blockchainKeys.membershipRenewalPrice(spaceId, tokenId), () => getMembershipRenewalPrice({
		spaceDapp,
		spaceId,
		tokenId
	}), {
		enabled: !!spaceId && !!tokenId,
		staleTime: staleTime24Hours
	});
}

//#endregion
//#region src/base-reads/hooks/use-get-subscriptions.ts
function transformSubscription(subscription) {
	return {
		...subscription,
		entityId: Number(subscription.entityId)
	};
}
function useGetSubscriptions(args) {
	const { walletAddress } = args;
	const { getSubscriptions: getSubscriptions$1 } = useBaseReads();
	return useQuery$1(blockchainKeys.subscriptions(walletAddress ?? ""), async () => {
		if (!walletAddress || !isAddress(walletAddress)) throw new Error("Invalid wallet address");
		return getSubscriptions$1(walletAddress).then((data) => data.map(transformSubscription));
	}, {
		enabled: !!walletAddress,
		staleTime: staleTime24Hours
	});
}
function useGetSingleSubscription(args) {
	const { walletAddress, spaceAddress, tokenId, enabled = true } = args;
	const { getSingleSubscription: getSingleSubscription$1 } = useBaseReads();
	return useQuery$1(walletAddress && spaceAddress && tokenId ? blockchainKeys.singleSubscription(walletAddress, spaceAddress, tokenId) : blockchainKeys.singleSubscription("ignored", "ignored", "0xignored"), async () => {
		if (!walletAddress || !isAddress(walletAddress)) throw new Error("Invalid wallet address");
		if (!spaceAddress || !isAddress(spaceAddress)) throw new Error("Invalid space address");
		if (!tokenId) throw new Error("Invalid token id");
		const allSubs = queryClient.getQueryData(blockchainKeys.subscriptions(walletAddress));
		const existingSubscription = allSubs?.find((subscription) => subscription.space.toLowerCase() === spaceAddress.toLowerCase() && subscription.tokenId === tokenId);
		if (existingSubscription) return existingSubscription;
		const singleSubscription = await getSingleSubscription$1({
			walletAddress,
			spaceAddress,
			tokenId
		});
		if (!singleSubscription) return;
		const transformedSubscription = transformSubscription(singleSubscription);
		if (allSubs) queryClient.setQueryData(blockchainKeys.subscriptions(walletAddress), [...allSubs, transformedSubscription]);
		return transformedSubscription;
	}, {
		enabled: enabled && !!walletAddress && !!spaceAddress && !!tokenId,
		staleTime: staleTime24Hours
	});
}

//#endregion
//#region src/base-reads/hooks/use-membership-info.ts
/**
* Grab membership price, limit, currency, and feeRecipient for a space.
*/
function useMembershipInfo(spaceId, _enabled = true) {
	const { getMembershipInfo: getMembershipInfo$1 } = useBaseReads();
	const queryKey = useMemo(() => blockchainKeys.membershipInfo(spaceId), [spaceId]);
	const isEnabled = useMemo(() => Boolean(spaceId.length > 0 && _enabled), [spaceId, _enabled]);
	const getMembershipInfoQuery = useCallback(() => {
		if (!isEnabled) return void 0;
		return getMembershipInfo$1(spaceId);
	}, [
		getMembershipInfo$1,
		isEnabled,
		spaceId
	]);
	return useQuery$1(queryKey, getMembershipInfoQuery, {
		enabled: isEnabled,
		staleTime: 5 * 60 * 1e3,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false
	});
}
function getMembershipInfoQueryData(spaceId) {
	return queryClient.getQueryData(blockchainKeys.membershipInfo(spaceId));
}

//#endregion
//#region src/base-reads/hooks/use-membership-free-allocation.ts
function useMembershipFreeAllocation(spaceId) {
	const { getMembershipFreeAllocation: getMembershipFreeAllocation$1 } = useBaseReads();
	return useQuery$1(blockchainKeys.membershipFreeAllocation(spaceId), async () => {
		if (!spaceId) return;
		return getMembershipFreeAllocation$1(spaceId);
	}, { enabled: !!spaceId });
}

//#endregion
//#region src/base-reads/hooks/use-protocol-fee.ts
function useProtocolFee({ spaceId }) {
	const { getProtocolFee: getProtocolFee$1 } = useBaseReads();
	return useQuery$1(blockchainKeys.protocolFee(spaceId), async () => {
		if (!spaceId) return;
		return getProtocolFee$1(spaceId);
	}, { enabled: !!spaceId });
}

//#endregion
//#region src/base-reads/hooks/use-platform-mint-limit.ts
function usePlatformMintLimit() {
	const { getPlatformMintLimit: getPlatformMintLimit$1 } = useBaseReads();
	return useQuery$1(blockchainKeys.membershipMintLimit(), async () => {
		const limit = await getPlatformMintLimit$1();
		return limit.toNumber();
	});
}

//#endregion
//#region src/base-reads/hooks/use-token-id-of-owner.ts
function useTokenIdOfOwner(spaceId, ownerAddress) {
	const { getTokenIdOfOwner: getTokenIdOfOwner$1 } = useBaseReads();
	return useQuery$1(blockchainKeys.tokenIdOfOwner(spaceId, ownerAddress), () => {
		if (!spaceId || !ownerAddress) return;
		return getTokenIdOfOwner$1(spaceId, ownerAddress);
	}, { enabled: !!spaceId && !!ownerAddress });
}

//#endregion
//#region src/base-reads/hooks/use-is-space-owner.ts
/**
* Hook to check if a wallet address is the owner of a space.
*
* Note: The subgraph may have slightly outdated owner data. This query provides
* an accurate on-chain read. Consider updating the subgraph to index ownership
* transfers to potentially eliminate the need for this query.
*/
function useIsSpaceOwner(spaceId, walletAddress) {
	const { getSpaceOwner: getSpaceOwner$1 } = useBaseReads();
	const normalizedSpaceId = spaceId ? isAddress(spaceId) ? SpaceIdFromSpaceAddress(spaceId) : spaceId : void 0;
	const getIsOwner = useCallback(async () => {
		if (walletAddress && normalizedSpaceId) {
			const ownerAddress = await getSpaceOwner$1(normalizedSpaceId);
			const isOwner = ownerAddress.toLowerCase() === walletAddress.toLowerCase();
			return {
				isOwner,
				ownerAddress
			};
		}
		return {
			isOwner: false,
			ownerAddress: void 0
		};
	}, [
		getSpaceOwner$1,
		walletAddress,
		normalizedSpaceId
	]);
	const { isLoading, error, data } = useQuery$1(blockchainKeys.spaceOwner(spaceId), getIsOwner, {
		enabled: !!walletAddress && isAddress(walletAddress),
		staleTime: Infinity
	});
	return {
		isLoading,
		isOwner: data?.isOwner,
		ownerAddress: data?.ownerAddress,
		error
	};
}
function invalidateSpaceOwner(spaceId) {
	return queryClient.invalidateQueries({ queryKey: blockchainKeys.spaceOwner(spaceId) });
}

//#endregion
//#region src/base-reads/hooks/use-pricing-modules.ts
function usePricingModules(args = {}) {
	const { enabled = true } = args;
	const { getPricingModules: getPricingModules$1 } = useBaseReads();
	return useQuery$1(blockchainKeys.pricingModules(), () => {
		return getPricingModules$1();
	}, {
		enabled,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
		staleTime: staleTime24Hours,
		gcTime: staleTime24Hours
	});
}
function useMembershipInfoWithPricingModule(spaceId, _enabled = true) {
	const { data: membershipInfo, isLoading: isLoadingMembershipInfo } = useMembershipInfo(spaceId ?? "", _enabled);
	const { data: pricingModules, isLoading: isLoadingPricingModules } = usePricingModules({ enabled: _enabled });
	return useMemo(() => {
		return getMergedData({
			membershipInfoData: membershipInfo,
			membershipInfoLoading: isLoadingMembershipInfo,
			pricingModulesData: pricingModules,
			pricingModulesLoading: isLoadingPricingModules
		});
	}, [
		isLoadingMembershipInfo,
		isLoadingPricingModules,
		membershipInfo,
		pricingModules
	]);
}
function getPricingModuleQueryData(args) {
	const membershipInfoQuery = queryClient.getQueryData(blockchainKeys.membershipInfo(args.spaceId));
	const pricingModulesQuery = queryClient.getQueryData(blockchainKeys.pricingModules());
	return getMergedData({
		membershipInfoData: membershipInfoQuery,
		pricingModulesData: pricingModulesQuery
	});
}
function getMergedData(args) {
	const membershipInfo = args.membershipInfoData;
	const pricingModules = args.pricingModulesData;
	if (args.membershipInfoLoading || args.pricingModulesLoading) return {
		isLoading: true,
		data: void 0
	};
	if (!membershipInfo || !pricingModules) return {
		isLoading: false,
		data: void 0
	};
	const membershipPricingModuleAddress = membershipInfo.pricingModule;
	const membershipPricingModule = pricingModules.find((m) => m.module === membershipPricingModuleAddress);
	if (!membershipPricingModule) return {
		isLoading: false,
		data: void 0
	};
	const isFixed = membershipPricingModule.name === FIXED_PRICING;
	return {
		isLoading: false,
		data: {
			isFixed,
			address: membershipPricingModuleAddress,
			...membershipPricingModule,
			...membershipInfo
		}
	};
}

//#endregion
//#region src/base-reads/hooks/use-banned-wallet-addresses.ts
const useBannedWalletAddresses = (spaceId) => {
	const { getBannedWalletAddresses: getBannedWalletAddresses$1 } = useBaseReads();
	const { data, isLoading } = useQuery$1(blockchainKeys.bannedWalletAddresses(spaceId ?? ""), async () => {
		if (!spaceId) return;
		return getBannedWalletAddresses$1(spaceId);
	}, {
		enabled: !!spaceId,
		refetchOnMount: true
	});
	return {
		userIds: data,
		isLoading
	};
};

//#endregion
//#region src/base-reads/hooks/use-platform-min-membership-price.ts
function usePlatformMinMembershipPrice({ select } = {}) {
	const { getPlatformMinMembershipPrice: getPlatformMinMembershipPrice$1 } = useBaseReads();
	return useQuery$1(blockchainKeys.minimumMembershipPrice(), () => getPlatformMinMembershipPrice$1(), { select });
}
function getPlatformMinMembershipPriceFromQueryCache() {
	return queryClient.getQueryData(blockchainKeys.minimumMembershipPrice());
}

//#endregion
//#region src/base-reads/hooks/use-platform-membership-fee.ts
function usePlatformMembershipFee({ select } = {}) {
	const { getPlatformMembershipFee: getPlatformMembershipFee$1 } = useBaseReads();
	return useQuery$1(blockchainKeys.membershipFee(), () => getPlatformMembershipFee$1(), { select });
}
function usePlatformMembershipPriceForSupplyInEth(supply) {
	const query = usePlatformMembershipFee({ select: (data) => ethers.utils.formatEther(data.mul(supply)) });
	return query;
}
function getPlatformMembershipFeeFromQueryCache() {
	return queryClient.getQueryData(blockchainKeys.membershipFee());
}

//#endregion
//#region src/base-reads/hooks/use-get-root-key-from-linked-wallet.ts
/**
* Grab the root key for a linked wallet
*/
function useGetRootKeyFromLinkedWallet({ walletAddress }) {
	const { getRootKeyFromLinkedWalletCached: getRootKeyFromLinkedWalletCached$1 } = useBaseReads();
	return useQuery$1(blockchainKeys.rootKeyFromLinkedWallet(walletAddress ?? "waitingForWalletAddress"), async () => {
		if (!walletAddress) return;
		return getRootKeyFromLinkedWalletCached$1(walletAddress);
	}, {
		enabled: !!walletAddress && isAddress(walletAddress),
		gcTime: staleTime24Hours,
		staleTime: staleTime24Hours,
		select: (data) => {
			if (data === zeroAddress) return;
			return data;
		}
	});
}

//#endregion
//#region src/store/use-credential-store.ts
function credentialsFromSignerContext(primaryWalletAddress, delegateWallet, casablancaContext) {
	return {
		privateKey: delegateWallet.privateKey,
		creatorAddress: ethers.utils.getAddress(bin_toHexString(casablancaContext.creatorAddress)),
		delegateSig: casablancaContext.delegateSig ? bin_toHexString(casablancaContext.delegateSig) : void 0,
		loggedInWalletAddress: primaryWalletAddress
	};
}
function credentialsToSignerContext(credentials) {
	const pk = credentials.privateKey.slice(2);
	const context = {
		signerPrivateKey: () => pk,
		creatorAddress: bin_fromHexString(credentials.creatorAddress),
		delegateSig: credentials.delegateSig ? bin_fromHexString(credentials.delegateSig) : void 0
	};
	return context;
}
const CREDENTIAL_STORE_NAME = "towns/credentials";
const useCredentialStore = create(persist((set) => ({
	casablancaCredentialsMap: {},
	setCasablancaCredentials: (homeServerUrl, casablancaCredentials) => set((state) => ({
		...state,
		casablancaCredentialsMap: {
			...state.casablancaCredentialsMap,
			[homeServerUrl]: casablancaCredentials
		}
	})),
	clearCasablancaCredentials: (homeServerUrl, old) => {
		set((state) => {
			if (isEqual(state.casablancaCredentialsMap[homeServerUrl], old)) state.casablancaCredentialsMap[homeServerUrl] = null;
			return state;
		});
	}
}), {
	name: CREDENTIAL_STORE_NAME,
	storage: createJSONStorage(() => localStorage),
	version: 1
}));

//#endregion
//#region src/hooks/login.ts
let AuthStatus = /* @__PURE__ */ function(AuthStatus$1) {
	/** User is not authenticated or connected to the river client. */
	AuthStatus$1["None"] = "None";
	/** Transition state: None -> EvaluatingCredentials -> [Credentialed OR ConnectedToRiver]
	*  if a river user is found, will connect to river client, otherwise will just validate credentials.
	*/
	AuthStatus$1["EvaluatingCredentials"] = "EvaluatingCredentials";
	/** User authenticated with a valid credential but without an active river stream client. */
	AuthStatus$1["Credentialed"] = "Credentialed";
	/** User authenticated with a valid credential and with an active river river client. */
	AuthStatus$1["ConnectedToRiver"] = "ConnectedToRiver";
	/** Transition state: ConnectedToRiver -> DisconnectingFromRiver -> None */
	AuthStatus$1["DisconnectingFromRiver"] = "DisconnectingFromRiver";
	/** Transition state: Credentialed -> Deauthenticating -> None */
	AuthStatus$1["Deauthenticating"] = "Deauthenticating";
	return AuthStatus$1;
}({});

//#endregion
//#region src/store/use-casablanca-store.ts
const useCasablancaStore = create((set) => ({
	authStatus: AuthStatus.None,
	setAuthStatus: (authStatus) => set({ authStatus }),
	authError: null,
	setAuthError: (error) => {
		set({ authError: error ?? null });
	}
}));

//#endregion
//#region src/hooks/use-logout.ts
const useLogout = () => {
	const { environmentId } = useTownsContext();
	const { setAuthStatus } = useCasablancaStore();
	const { setCasablancaCredentials } = useCredentialStore();
	const { client } = useTownsContext();
	return useCallback(async function() {
		setAuthStatus(AuthStatus.DisconnectingFromRiver);
		if (client) try {
			await client.logout();
			console.log("Logged out");
		} catch (ex) {
			console.error("Error logging out:", ex?.stack);
			throw new Error("Error logging out");
		}
		try {
			const { offlineMembershipStatusMap } = useOfflineStore.getState();
			Object.keys(offlineMembershipStatusMap).forEach((userId) => {
				Object.keys(offlineMembershipStatusMap[userId] || {}).forEach((spaceId) => {
					useOfflineStore.getState().removeOfflineMembershipStatus({
						userId,
						spaceId
					});
				});
			});
			const { useSpaceDataStore: useSpaceDataStore$1 } = await import("./use-space-data-C8lrPqtY.js");
			useSpaceDataStore$1.setState({ spaceDataMap: void 0 });
			queryClient.clear();
			console.log("Cleared membership caches on logout");
		} catch (error) {
			console.error("Error clearing caches on logout:", error);
		}
		setAuthStatus(AuthStatus.None);
		if (environmentId) setCasablancaCredentials(environmentId, null);
	}, [
		setAuthStatus,
		client,
		environmentId,
		setCasablancaCredentials
	]);
};

//#endregion
//#region src/hooks/TownsContext/useResetFullyReadMarkers.ts
function useResetFullyReadMarkers() {
	return useCallback(() => {
		console.log("useResetFullyReadMarkers::resetting fully read markers isnt implemented");
	}, []);
}

//#endregion
//#region src/hooks/TownsContext/useSendReadReceipt.ts
function useSendReadReceipt(client) {
	return useCallback(async (marker, isUnread = false) => {
		if (!client) throw new Error("No client");
		const fullyReadMarkers = client.casablancaClient?.streamsView.my.unreadMarkers;
		if (!fullyReadMarkers) throw new Error("No fully read markers");
		fullyReadMarkers.set((state) => {
			const markerId = marker.threadParentId ?? marker.channelId;
			const { mentions: inMentions } = marker;
			const mentions = !isUnread ? 0 : inMentions ?? 0;
			if (isUnread || state.markers[markerId]?.isUnread === true) return {
				...state,
				markers: {
					...state.markers,
					[markerId]: {
						...state.markers[markerId],
						isUnread,
						eventId: marker.eventId,
						eventNum: marker.eventNum,
						beginUnreadWindow: isUnread ? marker.eventNum : state.markers[markerId].endUnreadWindow + 1n,
						markedReadAtTs: isUnread ? 0n : BigInt(Date.now()),
						mentions
					}
				}
			};
			else return state;
		});
		const channelMarkers = Object.entries(fullyReadMarkers.value.markers).reduce((acc, [key, value]) => {
			if (value.channelId === marker.channelId) acc[key] = value;
			return acc;
		}, {});
		try {
			await client?.setRoomFullyReadData(marker.channelId, channelMarkers);
		} catch (e) {
			console.error("Failed to set room account data", e);
		}
	}, [client]);
}

//#endregion
//#region src/types/error-types.ts
var SignerUndefinedError = class extends Error {
	constructor(message) {
		super(message ?? "Signer is undefined");
		this.name = "SignerUndefined";
	}
};
var WalletDoesNotMatchSignedInAccountError = class extends Error {
	constructor(message) {
		super(message ?? "Current wallet does not match the signed in account");
		this.name = "CurrentWalletNotEqualToLoggedInWallet";
	}
};
function toError(error) {
	if (error instanceof Error) return error;
	return new Error(JSON.stringify(error));
}
var MembershipRejectedError = class extends Error {
	constructor(message) {
		super(message ?? "Membership rejected");
		this.name = "MembershipRejected";
	}
};
var MembershipAlreadyMemberError = class extends Error {
	constructor(message) {
		super(message ?? "Already a member");
		this.name = "MembershipAlreadyMember";
	}
};
/**
* Adds an category to an error object
*/
function addCategoryToError(error, category) {
	const existingErrorCategory = getErrorCategory(error);
	if (typeof error === "object" && error !== null) Object.assign(error, { category: existingErrorCategory ?? category });
}
function isErrorWithCategory(error) {
	if (typeof error === "object" && error !== null) return "category" in error;
	return false;
}
function getErrorCategory(error) {
	if (isErrorWithCategory(error)) return error.category;
}
function addCategoryToErrorIfNotExists(error, category) {
	return addCategoryToError(error, getErrorCategory(error) ?? category);
}
function skipErrorDecoding(error) {
	return typeof error === "object" && error !== null && "code" in error && typeof error.code === "number" && (error.code === -32602 || error.code === -32500);
}

//#endregion
//#region src/hooks/use-casablanca-wallet-signin.ts
function useCasablancaWalletSignIn() {
	const { environmentId } = useTownsContext();
	const { setCasablancaCredentials } = useCredentialStore();
	const { setAuthError, setAuthStatus } = useCasablancaStore();
	const isMounted = useRef(true);
	useEffect(() => {
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, []);
	const getIsWalletRegisteredWithCasablanca = useCallback(async () => {
		return Promise.resolve(true);
	}, []);
	const loginWithWalletToCasablanca = useCallback(async (_statement, signer, { onSuccess, onError } = {}) => {
		if (!signer) throw new SignerUndefinedError();
		if (!environmentId) throw new Error("environmentId not set");
		const delegateWallet = ethers.Wallet.createRandom();
		const wallet = await signer.getAddress();
		try {
			const casablancaContext = await makeSignerContext(signer, delegateWallet);
			if (isMounted.current) {
				setCasablancaCredentials(environmentId, credentialsFromSignerContext(wallet, delegateWallet, casablancaContext));
				onSuccess?.({
					wallet,
					delegateWallet,
					casablancaContext
				});
			}
		} catch (e) {
			if (isMounted.current) {
				console.error("loginWithWalletToCasablanca error", e);
				setAuthError({
					code: e?.code ?? 0,
					message: e?.message ?? `${e}`,
					error: e
				});
				setAuthStatus(AuthStatus.None);
				onError?.(e);
			}
		}
	}, [
		environmentId,
		setAuthError,
		setAuthStatus,
		setCasablancaCredentials
	]);
	const registerWalletWithCasablanca = useCallback(async (statement, signer) => {
		return loginWithWalletToCasablanca(statement, signer);
	}, [loginWithWalletToCasablanca]);
	return {
		getIsWalletRegisteredWithCasablanca,
		loginWithWalletToCasablanca,
		registerWalletWithCasablanca
	};
}

//#endregion
//#region src/hooks/use-towns-client.ts
const useTownsErrorStore = create((set) => ({
	errors: [],
	appendError: (error) => set((state) => ({ errors: [...state.errors, error] }))
}));
function useTownsClient() {
	const { getIsWalletRegisteredWithCasablanca, loginWithWalletToCasablanca, registerWalletWithCasablanca } = useCasablancaWalletSignIn();
	const { client, clientSingleton, signerContext } = useTownsContext();
	const clientRunning = useMemo(() => signerContext !== void 0, [signerContext]);
	const logout = useLogout();
	const sendReadReceipt = useSendReadReceipt(client);
	const resetFullyReadMarkers = useResetFullyReadMarkers();
	const createTownsSpace = useCallback((context, defaultUsernames, onCreateSpaceFlowStatus) => {
		if (!clientSingleton) {
			console.error("clientSingleton is undefined");
			return Promise.resolve(void 0);
		}
		return clientSingleton.createTownsSpace(context, signerContext, defaultUsernames, onCreateSpaceFlowStatus);
	}, [clientSingleton, signerContext]);
	const createDMWithBot = useCallback((userId, appAddress) => {
		if (!clientSingleton) {
			console.error("clientSingleton is undefined");
			return Promise.resolve(void 0);
		}
		return clientSingleton.createDMWithBot(userId, appAddress, signerContext);
	}, [clientSingleton, signerContext]);
	return {
		chainId: client?.opts.baseChainId,
		client,
		clientRunning,
		clientSingleton,
		spaceDapp: clientSingleton?.spaceDapp,
		signerContext,
		createTownsSpace,
		createMediaStream: useWithCatch(clientSingleton?.createMediaStream),
		createTownsChannel: useWithCatch(clientSingleton?.createTownsChannel),
		updateChannelRoom: useWithCatch(clientSingleton?.updateChannelRoom),
		createDMChannel: useWithCatch(clientSingleton?.createDMChannel),
		createDMWithBot,
		createGDMChannel: useWithCatch(clientSingleton?.createGDMChannel),
		editMessage: useWithCatch(clientSingleton?.editMessage),
		getIsUsernameAvailable: useWithCatch(clientSingleton?.getIsUsernameAvailable),
		getIsWalletRegisteredWithCasablanca,
		getServerVersions: useWithCatch(clientSingleton?.getServerVersions),
		getUserProfileImage: useWithCatch(clientSingleton?.getUserProfileImage),
		updateUserBlock: useWithCatch(clientSingleton?.updateUserBlock),
		inviteUser: useWithCatch(clientSingleton?.inviteUser),
		joinRoom: useWithCatch(clientSingleton?.joinRoom),
		leaveRoom: useWithCatch(clientSingleton?.leave),
		loginWithWalletToCasablanca,
		logout,
		pinMessage: useWithCatch(clientSingleton?.pinMessage),
		unpinMessage: useWithCatch(clientSingleton?.unpinMessage),
		redactEvent: useWithCatch(clientSingleton?.redactEvent),
		registerWalletWithCasablanca,
		removeUser: useWithCatch(clientSingleton?.removeUser),
		resetFullyReadMarkers,
		scrollback: useWithCatch(clientSingleton?.scrollback),
		scrollbackToEvent: useWithCatch(clientSingleton?.scrollbackToEvent),
		sendMessage: useWithCatch(clientSingleton?.sendMessage),
		retrySendMessage: useWithCatch(clientSingleton?.retrySendMessage),
		sendReaction: useWithCatch(clientSingleton?.sendReaction),
		sendInteractionResponse: useWithCatch(clientSingleton?.sendInteractionResponse),
		sendMediaPayload: useWithCatch(clientSingleton?.sendMediaPayload),
		sendReadReceipt: useWithCatch(sendReadReceipt),
		sendTokenTransfer: useWithCatch(clientSingleton?.sendTokenTransfer),
		adminRedactMessage: useWithCatch(clientSingleton?.adminRedactMessage),
		setDisplayName: useWithCatch(clientSingleton?.setDisplayName),
		setRoomProperties: useWithCatch(clientSingleton?.setRoomProperties),
		setAvatarUrl: useWithCatch(clientSingleton?.setAvatarUrl),
		setHighPriorityStreams: useWithCatch(clientSingleton?.setHighPriorityStreams),
		setSpaceImage: useWithCatch(clientSingleton?.setSpaceImage),
		setUserProfileImage: useWithCatch(clientSingleton?.setUserProfileImage),
		setUserBio: useWithCatch(clientSingleton?.setUserBio),
		addSpaceReview: useWithCatch(clientSingleton?.addSpaceReview)
	};
}
const useWithCatch = (fn) => {
	const client = useTownsContext().clientSingleton;
	return useMemo(() => async (...args) => {
		if (fn && client) try {
			const value = await fn.apply(client, args);
			return value;
		} catch (err) {
			console.error("Not a retryable error", err);
			return;
		}
		else console.error("client or fn is undefined", {
			fn,
			client
		});
	}, [fn, client]);
};

//#endregion
//#region src/hooks/use-casablanca-credentials.ts
function useCasablancaCredentials() {
	const { environmentId } = useTownsContext();
	const credentials = useCredentialStore((state) => state.casablancaCredentialsMap[environmentId ?? ""] ?? void 0);
	const { authStatus, authError } = useCasablancaStore();
	const isAuthenticated = credentials?.delegateSig !== void 0;
	return {
		userId: credentials?.creatorAddress ? prepareAndValidateAddress(credentials?.creatorAddress) : credentials?.creatorAddress,
		loggedInWalletAddress: credentials?.loggedInWalletAddress,
		isAuthenticated,
		authStatus,
		authError
	};
}
function prepareAndValidateAddress(address) {
	if (!address) return;
	address = (address.length == 40 ? "0x" + address : address).toLowerCase();
	if (!isAddress(address)) throw new Error("invalid address");
	return address;
}

//#endregion
//#region src/hooks/use-connectivity.ts
const loginMsgToSign = `Click to sign in and accept the Towns Terms of Service.`;
const registerWalletMsgToSign = `Click to register and accept the Towns Terms of Service.`;
function useConnectivity() {
	const { isAuthenticated, authStatus, authError, loggedInWalletAddress } = useCasablancaCredentials();
	const { loginWithWalletToCasablanca, registerWalletWithCasablanca, getIsWalletRegisteredWithCasablanca, logout: _logout } = useTownsClient();
	const login = useCallback(async (signer, { onSuccess, onError } = {}) => {
		return await loginWithWalletToCasablanca(loginMsgToSign, signer, {
			onSuccess,
			onError
		});
	}, [loginWithWalletToCasablanca]);
	const register = useCallback(async (signer) => {
		return await registerWalletWithCasablanca(registerWalletMsgToSign, signer);
	}, [registerWalletWithCasablanca]);
	const getIsWalletRegistered = useCallback(() => {
		return getIsWalletRegisteredWithCasablanca();
	}, [getIsWalletRegisteredWithCasablanca]);
	const logout = useCallback(async () => {
		await _logout();
	}, [_logout]);
	return {
		login,
		logout,
		register,
		getIsWalletRegistered,
		loggedInWalletAddress,
		isAuthenticated,
		authStatus,
		authError
	};
}

//#endregion
//#region src/base-reads/hooks/use-wallets.ts
/**
* Get a list of wallet addresses that are linked to the root key, and any delegated wallets for those wallets (not in wallet linking contract)
*/
function useLinkedAndDelegatedWallets({ walletAddress, enabled }) {
	const { getLinkedWalletsWithDelegations: getLinkedWalletsWithDelegations$1 } = useBaseReads();
	return useQuery$1(blockchainKeys.linkedWalletsWithDelegations(walletAddress ?? ""), async () => {
		if (!walletAddress) {
			console.log("useLinkedAndDelegatedWallets - no wallet address");
			return [];
		}
		return getLinkedWalletsWithDelegations$1(walletAddress);
	}, {
		enabled: enabled && !!walletAddress,
		staleTime: 60 * 1e3 * 5,
		refetchOnMount: true
	});
}
/**
* Returns the linked wallets for the logged in wallet, excluding the logged in wallet address
* This hook historically never returned the logged in wallet address, so we're filtering it out here in case the data source changes
*/
function useMyLinkedWallets({ enabled = true } = {}) {
	const { loggedInWalletAddress } = useConnectivity();
	const { data, isLoading, error } = useMyWallets({ enabled });
	return useMemo(() => {
		return {
			data: data?.filter((w) => w.toLowerCase() !== loggedInWalletAddress?.toLowerCase()),
			isLoading,
			error
		};
	}, [
		data,
		isLoading,
		error,
		loggedInWalletAddress
	]);
}
function useMyWallets({ enabled = true } = {}) {
	const { loggedInWalletAddress } = useConnectivity();
	const { getLinkedWallets: getLinkedWallets$1 } = useBaseReads();
	return useQuery$1(blockchainKeys.linkedWallets(loggedInWalletAddress ?? ""), () => {
		if (!loggedInWalletAddress) {
			console.error("useMyWallets - no wallet address");
			return [];
		}
		return getLinkedWallets$1(loggedInWalletAddress);
	}, {
		enabled: enabled && !!loggedInWalletAddress,
		staleTime: 60 * 1e3 * 5,
		refetchOnMount: true
	});
}

//#endregion
//#region src/base-reads/hooks/use-roles.ts
/**
* Convenience function to get space roles.
*/
function useRoles(_spaceId) {
	const spaceId = _spaceId && _spaceId.length > 0 ? _spaceId : "";
	const queryClient$1 = useQueryClient$1();
	const { getRoles: getRoles$1 } = useBaseReads();
	const getRolesFromSpace = useCallback(async function() {
		if (!spaceId) return void 0;
		return getRoles$1(spaceId);
	}, [getRoles$1, spaceId]);
	useEffect(function() {
		const prefetchRoles = async function() {
			if (spaceId) await queryClient$1.prefetchQuery({
				queryKey: blockchainKeys.roles(spaceId),
				queryFn: getRolesFromSpace,
				staleTime: 15 * 1e3
			});
		};
		prefetchRoles();
	}, [
		getRolesFromSpace,
		queryClient$1,
		spaceId
	]);
	const { isLoading, data: spaceRoles, error } = useQuery$1(blockchainKeys.roles(spaceId), getRolesFromSpace, { enabled: spaceId.length > 0 });
	return {
		isLoading,
		spaceRoles,
		error
	};
}

//#endregion
//#region src/base-reads/hooks/use-role-details.ts
/**
* Convenience function to get space role details.
*/
function useRoleDetails(spaceId, roleId, _enabled = true) {
	const { getRoleDetails: getRoleDetails$1 } = useBaseReads();
	const isEnabled = spaceId.length > 0 && roleId != void 0 && roleId >= 0 && _enabled;
	const getRole = useCallback(async function() {
		if (!isEnabled) return void 0;
		return getRoleDetails$1(spaceId, roleId);
	}, [
		getRoleDetails$1,
		isEnabled,
		roleId,
		spaceId
	]);
	const { isLoading, data: roleDetails, error } = useQuery$1(blockchainKeys.roleDetails(spaceId, isEnabled ? roleId : -1), getRole, { enabled: isEnabled });
	return {
		isLoading,
		roleDetails,
		error
	};
}
function getRoleDetailsQueryData(spaceId, roleId) {
	return queryClient.getQueryData(blockchainKeys.roleDetails(spaceId, roleId));
}
function useMultipleRoleDetails(spaceId, roleIds) {
	const queryClient$1 = useQueryClient$1();
	const { getMultipleRoleDetails: getMultipleRoleDetails$1 } = useBaseReads();
	const isEnabled = spaceId.length > 0 && roleIds.length > 0;
	const { data, isLoading } = useQuery$1(blockchainKeys.multipleRoleDetails(spaceId, roleIds), () => {
		if (!isEnabled) return [];
		return getMultipleRoleDetails$1(spaceId, roleIds);
	}, { enabled: isEnabled });
	const invalidateQuery = useCallback(() => queryClient$1.invalidateQueries({ queryKey: blockchainKeys.multipleRoleDetails(spaceId) }), [queryClient$1, spaceId]);
	return {
		data,
		isLoading,
		invalidateQuery
	};
}
const usePrefetchMultipleRoleDetails = (spaceId, roleIds) => {
	const queryClient$1 = useQueryClient$1();
	const { getMultipleRoleDetails: getMultipleRoleDetails$1 } = useBaseReads();
	useEffect(() => {
		async function prefetch() {
			if (!spaceId) return;
			await queryClient$1.prefetchQuery({
				queryKey: blockchainKeys.multipleRoleDetails(spaceId, roleIds),
				queryFn: () => getMultipleRoleDetails$1(spaceId, roleIds),
				staleTime: defaultStaleTime
			});
		}
		prefetch();
	}, [
		queryClient$1,
		spaceId,
		roleIds,
		getMultipleRoleDetails$1
	]);
};

//#endregion
//#region src/base-reads/hooks/use-permission-overrides.ts
function usePermissionOverrides(spaceId, channelId, roleId) {
	const { getPermissionsByRoleId: getPermissionsByRoleId$1, getChannelPermissionOverrides: getChannelPermissionOverrides$1 } = useBaseReads();
	const isEnabled = spaceId.length > 0 && roleId != void 0 && roleId >= 0 && channelId.length > 0;
	const getDefaultPermissions = useCallback(async function() {
		if (!isEnabled) return void 0;
		return getPermissionsByRoleId$1(spaceId, roleId);
	}, [
		getPermissionsByRoleId$1,
		isEnabled,
		spaceId,
		roleId
	]);
	const getPermissions = useCallback(async function() {
		if (!isEnabled) return void 0;
		return getChannelPermissionOverrides$1(spaceId, roleId, channelId);
	}, [
		getChannelPermissionOverrides$1,
		isEnabled,
		spaceId,
		channelId,
		roleId
	]);
	const queryKey = blockchainKeys.channelPermissionOverrides(spaceId, typeof roleId === "number" ? roleId : -1, channelId);
	const { isLoading, data: permissions, error } = useQuery$1(queryKey, async () => {
		const [spacePermissions, permissionOverrides] = await Promise.all([getDefaultPermissions(), getPermissions()]);
		if (!Array.isArray(spacePermissions) || !Array.isArray(permissionOverrides)) throw new Error("Failed to fetch permissions");
		if (permissionOverrides.length > 0 || spacePermissions.length === 0) return permissionOverrides;
		return null;
	}, { enabled: isEnabled });
	return {
		isLoading,
		permissions,
		error
	};
}

//#endregion
//#region src/base-reads/hooks/use-channel-settings.ts
function useChannelSettings(spaceId, channelId, _enabled = true) {
	const { getChannelSettings: getChannelSettings$1 } = useBaseReads();
	const isEnabled = spaceId.length > 0 && channelId.length > 0 && _enabled;
	const { isLoading, data: channelSettings, error } = useQuery$1(blockchainKeys.spaceAndChannel(spaceId, channelId), () => getChannelSettings$1(spaceId, channelId), {
		enabled: isEnabled,
		refetchOnMount: true,
		staleTime: 1e3 * 60 * 5
	});
	return {
		isLoading,
		channelSettings,
		error
	};
}
function getChannelSettingsQueryData(args) {
	const { spaceId, channelId } = args;
	return queryClient.getQueryData(blockchainKeys.spaceAndChannel(spaceId, channelId));
}

//#endregion
//#region src/base-reads/hooks/use-space-delegation-data.ts
function useSpaceDelegationsByOperator(operatorAddress) {
	const { getSpaceDelegationsByOperator: getSpaceDelegationsByOperator$1 } = useBaseReads();
	const fetchDelegationData = async () => {
		if (!operatorAddress) return void 0;
		try {
			return getSpaceDelegationsByOperator$1(operatorAddress);
		} catch (error) {
			console.error("Error fetching delegation data in useSpaceDelegationData:", error);
			throw error;
		}
	};
	return useQuery$1(blockchainKeys.delegationDataByOperator(operatorAddress ?? "undefined"), fetchDelegationData, { enabled: !!operatorAddress });
}
function useSpaceDelegationOperator(spaceId) {
	const { getSpaceDelegationOperator: getSpaceDelegationOperator$1 } = useBaseReads();
	const enabled = !!spaceId;
	return useQuery$1(blockchainKeys.delegationDataBySpace(spaceId ?? "undefined"), async () => {
		if (!spaceId) throw new Error("missing params");
		return getSpaceDelegationOperator$1(spaceId);
	}, { enabled });
}

//#endregion
//#region src/base-reads/hooks/use-space-tips.ts
function useSpaceTips(args) {
	const { spaceId, currencies } = args;
	const { getSpaceTips: getSpaceTips$1 } = useBaseReads();
	return useQuery$1(blockchainKeys.spaceTotalTips(spaceId, currencies), () => getSpaceTips$1(spaceId, currencies), {
		enabled: !!currencies.length,
		refetchInterval: 1e4
	});
}

//#endregion
//#region src/base-reads/hooks/use-read-app-expiration.ts
function useReadAppExpiration(spaceId, appAddress) {
	const { getAppExpiration: getAppExpiration$1 } = useBaseReads();
	return useQuery$1(blockchainKeys.appExpiration(spaceId, appAddress), () => {
		if (!spaceId || !appAddress) return void 0;
		return getAppExpiration$1(spaceId, appAddress);
	}, { enabled: !!spaceId && !!appAddress });
}

//#endregion
//#region src/base-reads/hooks/use-staking-queries.ts
const useStakingDepositsByOwnerQuery = (params) => {
	const { owner, enabled = true } = params;
	const { getStakingDepositsByOwner: getStakingDepositsByOwner$1 } = useBaseReads();
	return useQuery$1(blockchainKeys.myStakingDeposits(), async () => {
		if (!owner) return [];
		return getStakingDepositsByOwner$1(owner);
	}, { enabled: enabled && !!owner });
};
const useStakingDepositDetailsQueries = (params) => {
	const { depositIds, enabled = true } = params;
	const { getStakingDepositDetails: getStakingDepositDetails$1 } = useBaseReads();
	return useQueries$1({ queries: (depositIds ?? []).map((depositId) => ({
		queryKey: blockchainKeys.stakingDepositDetails(depositId),
		queryFn: async () => {
			return getStakingDepositDetails$1(depositId);
		},
		enabled: enabled && !!depositId
	})) });
};
const useStakingRewardsQuery = (params) => {
	const { beneficiary, enabled = true } = params;
	const { getStakingRewards: getStakingRewards$1 } = useBaseReads();
	return useQuery$1(blockchainKeys.myStakingRewards(), async () => {
		if (!beneficiary) return void 0;
		return getStakingRewards$1(beneficiary);
	}, { enabled: enabled && !!beneficiary });
};

//#endregion
//#region src/base-reads/hooks/use-towns-token-balance.ts
const useTownsTokenBalance = ({ address, enabled = true, staleTime = defaultStaleTime }) => {
	const { getTownsTokenBalance: getTownsTokenBalance$1 } = useBaseReads();
	const { data, isLoading, error } = useQuery$1(blockchainKeys.townsTokenBalance(address), () => getTownsTokenBalance$1(address), {
		enabled: enabled && !!address,
		refetchOnReconnect: true,
		staleTime
	});
	const invalidateTownsTokenBalance = useCallback(async () => {
		await queryClient.invalidateQueries({ queryKey: blockchainKeys.townsTokenBalance(address) });
	}, [address]);
	const fetchTownsTokenBalance = useCallback(async () => {
		return queryClient.fetchQuery({
			queryKey: blockchainKeys.townsTokenBalance(address),
			retry: 3
		});
	}, [address]);
	return {
		data,
		isLoading,
		error,
		invalidateTownsTokenBalance,
		fetchTownsTokenBalance
	};
};

//#endregion
//#region src/base-reads/hooks/use-river-points-details.ts
function useRiverPointsDetails(address) {
	const { getRiverPoints: getRiverPoints$1 } = useBaseReads();
	return useQuery$1(blockchainKeys.riverPoints(address ?? ""), async () => {
		return getRiverPoints$1(address);
	}, {
		enabled: !!address,
		staleTime: 60 * 5 * 1e3,
		refetchOnWindowFocus: true,
		refetchOnMount: true
	});
}

//#endregion
//#region src/base-reads/hooks/use-has-permission.ts
function querySetup(props, isEntitledFn) {
	const { spaceId, channelId, walletAddress, permission } = props;
	const staleTime = (() => {
		switch (permission) {
			case Permission.Read: return 30 * 60 * 1e3;
			case Permission.Write:
			case Permission.JoinSpace: return 1 * 60 * 1e3;
			default: return 5 * 60 * 1e3;
		}
	})();
	return {
		queryKey: blockchainKeys.hasPermission(props),
		queryFn: async () => {
			if (walletAddress) {
				const isEntitled$1 = await isEntitledFn({
					spaceId,
					channelId,
					user: walletAddress,
					permission
				});
				return isEntitled$1;
			}
			return false;
		},
		staleTime
	};
}
function useHasPermission(props) {
	const { walletAddress } = props;
	const queryClient$1 = useQueryClient$1();
	const { isEntitled: isEntitled$1 } = useBaseReads();
	const { queryFn: queryFn$1, queryKey, staleTime } = querySetup(props, isEntitled$1);
	const invalidate = useCallback(() => {
		return queryClient$1.invalidateQueries({ queryKey });
	}, [queryClient$1, queryKey]);
	const getQueryData = useCallback(() => {
		return queryClient$1.getQueryData(queryKey);
	}, [queryClient$1, queryKey]);
	const { isLoading, error, data: hasPermission } = useQuery$1(queryKey, queryFn$1, {
		enabled: !!walletAddress && isAddress(walletAddress),
		refetchOnMount: true,
		staleTime
	});
	return {
		isLoading,
		hasPermission,
		invalidate,
		getQueryData,
		error
	};
}
function useFetchHasJoinPermission() {
	const { isEntitled: isEntitled$1 } = useBaseReads();
	const queryClient$1 = useQueryClient$1();
	return useCallback((props) => {
		const { queryFn: queryFn$1, queryKey } = querySetup({
			spaceId: props.spaceId,
			walletAddress: props.walletAddress,
			permission: Permission.JoinSpace
		}, isEntitled$1);
		return queryClient$1.fetchQuery({
			queryKey,
			queryFn: queryFn$1
		});
	}, [queryClient$1, isEntitled$1]);
}
const isHasPermissionQuery = (queryKey) => {
	return Array.isArray(queryKey) && queryKey.length >= 2 && queryKey[0] === "hasPermission" && typeof queryKey[1] === "object" && queryKey[1] !== null && "permission" in queryKey[1];
};
function useOnHasPermissionQueryUpdate(args) {
	const { spaceId, predicate, onMatchQuery } = args;
	useEffect(() => {
		const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
			if (event.type === "updated" || event.type === "added") {
				const query = event.query;
				const queryKey = query.queryKey;
				if (isHasPermissionQuery(queryKey)) {
					const args$1 = queryKey[1];
					if (args$1.spaceId === spaceId) {
						const allQueries = queryClient.getQueryCache().getAll();
						const permissionQueries = allQueries.filter((q) => {
							const key = q.queryKey;
							if (isHasPermissionQuery(key)) {
								const queryArgs = key[1];
								return queryArgs.spaceId === spaceId;
							}
							return false;
						});
						permissionQueries.forEach((query$1) => {
							const data = query$1.state.data;
							if (predicate(data)) onMatchQuery({
								spaceId,
								channelId: args$1.channelId,
								walletAddress: args$1.walletAddress,
								permission: args$1.permission
							});
						});
					}
				}
			}
		});
		return unsubscribe;
	}, [
		onMatchQuery,
		predicate,
		spaceId
	]);
}
/**
* Invalidate all has permission queries for a space, based on a predicate.
*
* @example invalidate all negative permissions
* void invalidateHasPermissionQueriesForSpace({
*     spaceId,
*     predicate: (data) => data === false,
*     clientSingleton,
* })
*/
async function invalidateHasPermissionQueriesForSpace(args) {
	const { spaceId, predicate, isEntitled: isEntitled$1 } = args;
	const queries = queryClient.getQueryCache().findAll({ predicate: (query) => {
		const queryKey = query.queryKey;
		if (isHasPermissionQuery(queryKey)) {
			const queryArgs = queryKey[1];
			return queryArgs.spaceId === spaceId;
		}
		return false;
	} });
	await Promise.all(queries.map(async (query) => {
		const queryKey = query.queryKey;
		if (isHasPermissionQuery(queryKey)) {
			const queryArgs = queryKey[1];
			const data = query.state.data;
			if (queryArgs.spaceId === spaceId && predicate(data) && queryArgs.walletAddress) queryClient.setQueryData(queryKey, await isEntitled$1({
				spaceId: queryArgs.spaceId,
				channelId: queryArgs.channelId,
				user: queryArgs.walletAddress,
				permission: queryArgs.permission,
				invalidateCache: true
			}));
		}
	}));
}

//#endregion
//#region src/base-reads/hooks/use-membership-price.ts
function fetchMembershipPrice(args) {
	const { spaceId, spaceDapp } = args;
	if (!spaceId) throw new Error(`Space with spaceId "${spaceId}" is not found.`);
	const space = spaceDapp.getSpace(spaceId);
	if (!space) throw new Error(`Space with spaceId "${spaceId}" is not found.`);
	return space.Membership.read.getMembershipPrice();
}
function useMembershipPrice(args) {
	const { spaceDapp } = useBaseReads();
	const { spaceId } = args;
	return useQuery$1(blockchainKeys.membershipPrice(spaceId), () => fetchMembershipPrice({
		spaceId,
		spaceDapp
	}), {
		enabled: !!spaceId,
		staleTime: 5 * 60 * 1e3,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false
	});
}
function getMembershipPrice(args) {
	const { spaceId, spaceDapp } = args;
	return queryClient.ensureQueryData({
		queryKey: blockchainKeys.membershipPrice(spaceId),
		queryFn: () => fetchMembershipPrice({
			spaceId,
			spaceDapp
		})
	});
}

//#endregion
//#region src/types/web3-types.ts
const NULL_ADDRESS = "0x0000000000000000000000000000000000000000";
function isNullAddress(address) {
	return address === void 0 || address === NULL_ADDRESS;
}
let WalletStatus = /* @__PURE__ */ function(WalletStatus$1) {
	WalletStatus$1["Connected"] = "connected";
	WalletStatus$1["Reconnecting"] = "reconnecting";
	WalletStatus$1["Connecting"] = "connecting";
	WalletStatus$1["Disconnected"] = "disconnected";
	return WalletStatus$1;
}({});
let BlockchainTransactionType = /* @__PURE__ */ function(BlockchainTransactionType$1) {
	BlockchainTransactionType$1["CreateSpace"] = "createSpace";
	BlockchainTransactionType$1["CreateChannel"] = "createChannel";
	BlockchainTransactionType$1["DeleteChannel"] = "deleteChannel";
	BlockchainTransactionType$1["EditChannel"] = "editChannel";
	BlockchainTransactionType$1["UpdateRole"] = "updateRole";
	BlockchainTransactionType$1["SetChannelPermissionOverrides"] = "setChannelPermissionOverrides";
	BlockchainTransactionType$1["ClearChannelPermissionOverrides"] = "clearChannelPermissionOverrides";
	BlockchainTransactionType$1["CreateRole"] = "createRole";
	BlockchainTransactionType$1["DeleteRole"] = "deleteRole";
	BlockchainTransactionType$1["UpdateSpaceInfo"] = "updateSpaceInfo";
	BlockchainTransactionType$1["LinkWallet"] = "linkWallet";
	BlockchainTransactionType$1["UnlinkWallet"] = "unlinkWallet";
	BlockchainTransactionType$1["BanUser"] = "banUser";
	BlockchainTransactionType$1["UnbanUser"] = "unbanUser";
	BlockchainTransactionType$1["JoinSpace"] = "joinSpace";
	BlockchainTransactionType$1["EditSpaceMembership"] = "editSpaceMembership";
	BlockchainTransactionType$1["PrepayMembership"] = "prepayMembership";
	BlockchainTransactionType$1["TransferNft"] = "transferNft";
	BlockchainTransactionType$1["Transfer"] = "transfer";
	BlockchainTransactionType$1["WithdrawTreasury"] = "withdrawTreasury";
	BlockchainTransactionType$1["TransferBaseEth"] = "transferBaseEth";
	BlockchainTransactionType$1["RefreshMetadata"] = "refreshMetadata";
	BlockchainTransactionType$1["Tip"] = "tip";
	BlockchainTransactionType$1["CheckIn"] = "checkIn";
	BlockchainTransactionType$1["Trade"] = "trade";
	BlockchainTransactionType$1["Review"] = "review";
	BlockchainTransactionType$1["AddSpaceDelegation"] = "addSpaceDelegation";
	BlockchainTransactionType$1["RenewMembership"] = "renewMembership";
	BlockchainTransactionType$1["ClaimReward"] = "claimReward";
	BlockchainTransactionType$1["StakeToTowns"] = "stakeToTowns";
	BlockchainTransactionType$1["IncreaseStakeToTowns"] = "increaseStakeToTowns";
	BlockchainTransactionType$1["InitiateWithdraw"] = "initiateWithdraw";
	BlockchainTransactionType$1["ClaimRewardAndStakeToTowns"] = "claimRewardAndStakeToTowns";
	BlockchainTransactionType$1["ClaimRewardAndIncreaseStakeToTowns"] = "claimRewardAndIncreaseStakeToTowns";
	BlockchainTransactionType$1["Withdraw"] = "withdraw";
	BlockchainTransactionType$1["RedelegateStake"] = "redelegateStake";
	BlockchainTransactionType$1["CreateApp"] = "createApp";
	BlockchainTransactionType$1["InstallApp"] = "installApp";
	BlockchainTransactionType$1["RegisterApp"] = "registerApp";
	BlockchainTransactionType$1["TransferOwnership"] = "transferOwnership";
	BlockchainTransactionType$1["ZeroBurn"] = "zeroBurn";
	BlockchainTransactionType$1["InstallSubscription"] = "installSubscription";
	BlockchainTransactionType$1["UninstallSubscription"] = "uninstallSubscription";
	BlockchainTransactionType$1["ActivateSubscription"] = "activateSubscription";
	BlockchainTransactionType$1["PauseSubscription"] = "pauseSubscription";
	BlockchainTransactionType$1["MigrateSubscriptionCurrency"] = "migrateSubscriptionCurrency";
	BlockchainTransactionType$1["UpdateAppPricing"] = "updateAppPricing";
	BlockchainTransactionType$1["UninstallApp"] = "uninstallApp";
	BlockchainTransactionType$1["UpdateAppPermissions"] = "updateAppPermissions";
	BlockchainTransactionType$1["UpdateApp"] = "updateApp";
	BlockchainTransactionType$1["WithdrawAppFunds"] = "withdrawAppFunds";
	BlockchainTransactionType$1["RenewApp"] = "renewApp";
	BlockchainTransactionType$1["SetPrimaryWallet"] = "SetPrimaryWallet";
	BlockchainTransactionType$1["PromoteAgent"] = "promoteAgent";
	BlockchainTransactionType$1["GiveBotFeedback"] = "giveBotFeedback";
	BlockchainTransactionType$1["InstallAppOnUserAccount"] = "installAppOnUserAccount";
	BlockchainTransactionType$1["InstallAccountModulesAndApp"] = "installAccountModulesAndApp";
	BlockchainTransactionType$1["ChangeCurrencyAndPrice"] = "changeCurrencyAndPrice";
	return BlockchainTransactionType$1;
}({});

//#endregion
//#region src/base-writes/stores/BlockchainTransactionStore.ts
var BlockchainTransactionStore = class extends EventEmitter {
	transactions = {};
	spaceDapp;
	promiseQueue = [];
	abortController = new AbortController();
	constructor(spaceDapp) {
		super();
		this.spaceDapp = spaceDapp;
	}
	begin({ type, data }) {
		const id = `STORE_ID__${ethers.Wallet.createRandom().address}`;
		const tx = {
			id,
			type,
			data,
			status: "potential"
		};
		this.updateTransaction(tx);
		return ({ hashOrUserOpHash, error, transaction, eventListener }) => {
			if (hashOrUserOpHash && transaction) {
				this.moveToPending(id, {
					hashOrUserOpHash,
					transaction,
					type,
					data,
					eventListener
				});
				return;
			}
			if (error) {
				if (this.transactions[id].status !== "failure") this.updateTransaction({
					...tx,
					status: "failure",
					error
				});
			}
		};
	}
	async stop() {
		this.spaceDapp.provider?.removeAllListeners();
		this.abortController.abort();
		await Promise.all(this.promiseQueue);
		this.promiseQueue = [];
		this.transactions = {};
	}
	moveToPending(id, tx) {
		this.updateTransaction({
			...tx,
			id,
			status: "pending"
		});
		const _promise = new Promise((resolve) => {
			const blockchainListener = async () => {
				this.abortController.signal.addEventListener("abort", () => {
					return resolve();
				});
				const streamId = tx.data?.spaceStreamId;
				const type = tx.type;
				try {
					const transactionOrUserOp = tx.transaction;
					let hashToWaitFor = tx.hashOrUserOpHash;
					if (tx.eventListener) {
						const result = await tx.eventListener.wait();
						if (this.abortController.signal.aborted) return;
						this.updateTransaction({
							...tx,
							id,
							status: result.success ? "success" : "failure",
							receipt: result.receipt
						});
						return;
					} else {
						if (isUserOpResponse(transactionOrUserOp)) {
							const userOpEvent = await transactionOrUserOp.wait();
							if (this.abortController.signal.aborted) return;
							if (!userOpEvent || userOpEvent.args.success === false) {
								this.updateTransaction({
									...tx,
									id,
									status: "failure"
								});
								return;
							}
							hashToWaitFor = userOpEvent.transactionHash;
						}
						await this.spaceDapp.provider?.waitForTransaction(hashToWaitFor);
						if (this.abortController.signal.aborted) return;
						this.updateTransaction({
							...tx,
							id,
							status: "success"
						});
					}
				} catch (error) {
					const isWalletLink = type === BlockchainTransactionType.LinkWallet || type === BlockchainTransactionType.UnlinkWallet;
					const parsedError = isWalletLink ? this.spaceDapp.getWalletLink().parseError(error) : this.spaceDapp.parseSpaceError(streamId ?? "", error);
					this.updateTransaction({
						...tx,
						id,
						status: "failure",
						error: parsedError
					});
				} finally {
					this.promiseQueue = this.promiseQueue.filter((p) => p !== _promise);
					resolve();
				}
			};
			blockchainListener();
		});
		this.promiseQueue.push(_promise);
	}
	updateTransaction(tx) {
		this.transactions[tx.id] = {
			...this.transactions[tx.id],
			...tx
		};
		this.emit("updatedTransaction", this.transactions[tx.id]);
		this.emit("transactions", this.transactions);
	}
};

//#endregion
//#region src/SequenceTimeTracker.ts
var TimeTracker = class {
	townsAnalytics;
	metrics;
	constructor(analytics) {
		this.metrics = {};
		this.townsAnalytics = analytics;
		window.listSequenceTimes = this.getAllMetrics.bind(this);
	}
	startMeasurement(sequence, step, data) {
		try {
			if (!sequence) {
				console.error("Measurement name is required.");
				return;
			}
			const now = performance.now();
			if (!this.metrics[sequence]) this.metrics[sequence] = {
				count: 1,
				totalDurationMs: 0,
				totalDurationSec: 0,
				startTime: now,
				sequenceId: nanoid(),
				steps: { [step]: {
					count: 1,
					totalDurationMs: 0,
					totalDurationSec: 0,
					startTime: now
				} }
			};
			else if (!this.metrics[sequence].steps[step]) this.metrics[sequence].steps[step] = {
				count: 1,
				totalDurationMs: 0,
				totalDurationSec: 0,
				startTime: now
			};
			else {
				this.metrics[sequence].steps[step].count++;
				this.metrics[sequence].steps[step].startTime = now;
			}
			if (!this.metrics[sequence].startTime) {
				this.metrics[sequence].sequenceId = nanoid();
				this.metrics[sequence].startTime = now;
				this.metrics[sequence].count++;
			}
			return () => this.endMeasurement(sequence, step, data);
		} catch (error) {
			console.error("Error starting measurement:", error);
		}
	}
	addMeasurement(sequence, step, durationMs, data) {
		try {
			if (!sequence) {
				console.error("Measurement name is required.");
				return;
			}
			const now = performance.now();
			if (!this.metrics[sequence]) this.metrics[sequence] = {
				count: 1,
				totalDurationMs: 0,
				totalDurationSec: 0,
				startTime: now,
				sequenceId: nanoid(),
				steps: { [step]: {
					count: 1,
					totalDurationMs: durationMs,
					totalDurationSec: durationMs / 1e3,
					startTime: void 0
				} }
			};
			else {
				if (!this.metrics[sequence].steps[step]) this.metrics[sequence].steps[step] = {
					count: 1,
					totalDurationMs: durationMs,
					totalDurationSec: durationMs / 1e3,
					startTime: void 0
				};
				else {
					this.metrics[sequence].steps[step].count++;
					this.metrics[sequence].steps[step].totalDurationMs += durationMs;
					this.metrics[sequence].steps[step].totalDurationSec += durationMs / 1e3;
				}
				this.metrics[sequence].totalDurationMs += durationMs;
				this.metrics[sequence].totalDurationSec += durationMs / 1e3;
			}
			try {
				datadogLogs.logger.info(`sequence_time: ${sequence} ${step} ${durationMs}ms`, {
					sequence,
					step,
					sequenceId: this.metrics[sequence].sequenceId,
					durationMs,
					...data
				});
			} catch (error) {
				console.log("[SequenceTimeTracker] Error logging sequence time:", error);
			}
		} catch (error) {
			console.error("Error adding measurement:", error);
		}
	}
	endMeasurement(sequence, step, data) {
		try {
			if (!sequence || !this.metrics[sequence]) {
				console.error(`Measurement "${sequence}" not found or not started.`);
				return;
			}
			const stepStartTime = this.metrics[sequence].steps[step].startTime;
			if (stepStartTime === void 0) {
				console.error(`Measurement "${sequence}" not started.`);
				return;
			}
			const endTime = performance.now();
			const durationMs = endTime - stepStartTime;
			const durationSec = durationMs / 1e3;
			this.metrics[sequence].totalDurationMs += durationMs;
			this.metrics[sequence].totalDurationSec += durationSec;
			this.metrics[sequence].steps[step].totalDurationMs += durationMs;
			this.metrics[sequence].steps[step].totalDurationSec += durationSec;
			this.metrics[sequence].steps[step].startTime = void 0;
			try {
				datadogLogs.logger.info(`sequence_time: ${sequence} ${step} ${durationMs}ms`, {
					sequence,
					step,
					sequenceId: this.metrics[sequence].sequenceId,
					durationMs,
					...data
				});
			} catch (error) {
				console.log("[SequenceTimeTracker] Error logging sequence time:", error);
			}
		} catch (error) {
			console.error("Error ending measurement:", error);
		}
	}
	getMetric(sequence) {
		if (!sequence || !this.metrics[sequence]) {
			console.error(`Measurement "${sequence}" not found.`);
			return null;
		}
		const avgSequenceDurationMs = this.metrics[sequence].totalDurationMs / this.metrics[sequence].count;
		const steps = Object.entries(this.metrics[sequence].steps).map(([stepName, stepMetrics]) => {
			const stepDurationMs = stepMetrics.totalDurationMs;
			const averageDurationMs = stepMetrics.count > 0 ? stepDurationMs / stepMetrics.count : 0;
			return {
				stepName,
				count: stepMetrics.count,
				totalDurationMs: stepDurationMs,
				averageDurationMs
			};
		}).sort((a, b) => b.totalDurationMs - a.totalDurationMs);
		return {
			count: this.metrics[sequence].count,
			totalDurationMs: this.metrics[sequence].totalDurationMs,
			averageDurationMs: avgSequenceDurationMs,
			steps
		};
	}
	getAllMetrics() {
		return Object.keys(this.metrics).reduce((acc, sequence) => {
			const seq = this.getMetric(sequence);
			if (!seq) return acc;
			acc[sequence] = seq;
			return acc;
		}, {});
	}
	clearMetrics() {
		this.metrics = {};
	}
};
let instance;
const getTimeTracker = (analytics) => {
	if (!instance) instance = new TimeTracker(analytics);
	return instance;
};
const TimeTrackerEvents = {
	CREATE_SPACE: "CREATE_SPACE",
	JOIN_SPACE: "JOIN_SPACE",
	CREATE_DM_WITH_BOT: "CREATE_DM_WITH_BOT"
};

//#endregion
//#region src/utils/waitForTimeoutOrMembershipEvent.ts
var MembershipEventListenerTimeoutError = class extends Error {
	constructor(message) {
		super(message ?? "Membership event listener timed out");
		this.name = "MembershipEventListenerTimeoutError";
	}
};
function waitForTimeoutOrMembership({ hashOrUserOpHash, membershipListener, abortController, waitTimeout = 3e4 }) {
	return new Promise((resolve) => {
		const timeout = setTimeout(() => {
			console.log("[UserOperations] waitForTimeoutOrMembership timeout", { hashOrUserOpHash });
			abortController.abort();
			resolve({
				issued: false,
				tokenId: void 0,
				error: new MembershipEventListenerTimeoutError()
			});
		}, waitTimeout);
		membershipListener.then((result) => {
			clearTimeout(timeout);
			if (abortController.signal.aborted) return;
			console.log("[UserOperations] Received membership mint event", {
				hashOrUserOpHash,
				mintResult: result
			});
			resolve(result);
		}).catch((error) => {
			clearTimeout(timeout);
			abortController.abort();
			console.log("[UserOperations] Error waiting for membership mint event", {
				hashOrUserOpHash,
				error
			});
			resolve({
				issued: false,
				tokenId: void 0,
				error
			});
		});
	});
}

//#endregion
//#region src/base-writes/BaseTransactor.ts
var BaseTransactor = class {
	userOps;
	blockchainTransactionStore;
	createLegacySpaces;
	spaceDapp;
	appRegistryDapp;
	baseProvider;
	baseConfig;
	baseReads;
	constructor(args) {
		const { userOps, baseProvider, baseConfig, createLegacySpaces, baseReads } = args;
		this.blockchainTransactionStore = new BlockchainTransactionStore(baseReads.spaceDapp);
		this.userOps = userOps;
		this.createLegacySpaces = createLegacySpaces;
		this.spaceDapp = baseReads.spaceDapp;
		this.appRegistryDapp = baseReads.appRegistryDapp;
		this.baseProvider = baseProvider;
		this.baseConfig = baseConfig;
		this.baseReads = baseReads;
	}
	/************************************************
	* log
	*************************************************/
	log(message, ...optionalParams) {
		console.log(message, ...optionalParams);
	}
	isAccountAbstractionEnabled() {
		return !!this.userOps;
	}
	getAbstractAccountAddress({ rootKeyAddress }) {
		try {
			return this.userOps?.smartAccountSession.getAbstractAccountAddress({ rootKeyAddress });
		} catch (error) {
			this.log("[getAbstractAccountAddress]", error);
		}
	}
	async createSpaceTransaction(createSpaceInfo, membership, signer, onCreateSpaceFlowStatus) {
		if (!signer) throw new SignerUndefinedError();
		let senderAddress = await signer.getAddress();
		if (this.isAccountAbstractionEnabled()) {
			const aaAddress = await this.getAbstractAccountAddress({ rootKeyAddress: senderAddress });
			if (aaAddress) senderAddress = aaAddress;
		}
		return this.createTransaction({
			signer,
			contextData: {
				spaceName: createSpaceInfo.name,
				senderAddress
			},
			type: BlockchainTransactionType.CreateSpace,
			debugName: "createSpaceTransaction",
			preExecute: () => {
				onCreateSpaceFlowStatus?.(CreateSpaceFlowStatus.MintingSpace);
				const legacyParams = {
					spaceName: createSpaceInfo.name,
					uri: createSpaceInfo.uri ?? "",
					channelName: createSpaceInfo.defaultChannelName ?? "general",
					membership: {
						settings: membership.settings,
						permissions: membership.permissions,
						requirements: {
							everyone: membership.requirements.everyone,
							users: membership.requirements.users,
							ruleData: convertRuleDataV2ToV1(decodeRuleDataV2(membership.requirements.ruleData)),
							syncEntitlements: membership.requirements.syncEntitlements
						}
					},
					shortDescription: createSpaceInfo.shortDescription ?? "",
					longDescription: createSpaceInfo.longDescription ?? ""
				};
				const createParams = {
					spaceName: createSpaceInfo.name,
					uri: createSpaceInfo.uri ?? "",
					channelName: createSpaceInfo.defaultChannelName ?? "general",
					membership,
					shortDescription: createSpaceInfo.shortDescription ?? "",
					longDescription: createSpaceInfo.longDescription ?? ""
				};
				return Promise.resolve({
					createParams,
					legacyParams
				});
			},
			aaExecute: ({ createParams, legacyParams }) => {
				if (this.createLegacySpaces) return this.userOps?.sendCreateLegacySpaceOp([legacyParams, signer]);
				return this.userOps?.sendCreateSpaceOp([createParams, signer]);
			},
			eoaExecute: ({ createParams, legacyParams }) => {
				if (this.createLegacySpaces) return this.spaceDapp.createLegacySpace(legacyParams, signer);
				return this.spaceDapp.createSpace(createParams, signer);
			},
			errorDecoder: (error) => this.spaceDapp.parseSpaceFactoryError(error)
		});
	}
	async createChannelTransaction(createChannelInfo, signer) {
		if (!signer) throw new SignerUndefinedError();
		const roomId = makeUniqueChannelStreamId(createChannelInfo.parentSpaceId);
		const args = [
			createChannelInfo.parentSpaceId,
			createChannelInfo.name,
			createChannelInfo.topic ?? "",
			roomId,
			createChannelInfo.roles,
			signer
		];
		return this.createTransaction({
			debugName: "createChannelTransaction",
			type: BlockchainTransactionType.CreateChannel,
			signer,
			contextData: {
				spaceStreamId: createChannelInfo.parentSpaceId,
				channelStreamId: roomId
			},
			aaExecute: () => {
				return this.userOps?.sendCreateChannelOp([...args]);
			},
			eoaExecute: () => {
				return this.spaceDapp.createChannelWithPermissionOverrides(...args);
			},
			errorDecoder: (error) => this.getDecodedErrorForSpace(createChannelInfo.parentSpaceId, error)
		});
	}
	async createRoleTransaction(spaceNetworkId, roleName, permissions, users, ruleData, signer) {
		if (!signer) throw new SignerUndefinedError();
		return this.createTransaction({
			signer,
			contextData: {
				spaceNetworkId,
				roleName
			},
			type: BlockchainTransactionType.CreateRole,
			debugName: "createRoleTransaction",
			preExecute: async () => {
				const isLegacySpace = await this.spaceDapp.isLegacySpace(spaceNetworkId);
				return {
					legacySpaceArgs: isLegacySpace ? [
						spaceNetworkId,
						roleName,
						permissions,
						users,
						convertRuleDataV2ToV1(ruleData),
						signer
					] : void 0,
					spaceArgs: isLegacySpace ? void 0 : [
						spaceNetworkId,
						roleName,
						permissions,
						users,
						ruleData,
						signer
					]
				};
			},
			aaExecute: ({ legacySpaceArgs, spaceArgs }) => {
				if (legacySpaceArgs) return this.userOps?.sendLegacyCreateRoleOp([...legacySpaceArgs]);
				else if (spaceArgs) return this.userOps?.sendCreateRoleOp([...spaceArgs]);
				throw new Error("No args provided");
			},
			eoaExecute: ({ legacySpaceArgs, spaceArgs }) => {
				if (legacySpaceArgs) return this.spaceDapp.legacyCreateRole(...legacySpaceArgs);
				else if (spaceArgs) return this.spaceDapp.createRole(...spaceArgs);
				throw new Error("No args provided");
			},
			errorDecoder: (error) => this.getDecodedErrorForSpace(spaceNetworkId, error)
		});
	}
	async updateRoleTransaction(spaceNetworkId, roleId, roleName, permissions, users, ruleData, signer) {
		if (!signer) throw new SignerUndefinedError();
		return this.createTransaction({
			signer,
			contextData: {
				spaceNetworkId,
				roleName
			},
			type: BlockchainTransactionType.UpdateRole,
			debugName: "updateRoleTransaction",
			preExecute: async () => {
				const isLegacySpace = await this.spaceDapp.isLegacySpace(spaceNetworkId);
				return {
					legacySpaceArgs: isLegacySpace ? {
						spaceNetworkId,
						roleId,
						roleName,
						permissions,
						users,
						ruleData: convertRuleDataV2ToV1(ruleData)
					} : void 0,
					spaceArgs: isLegacySpace ? void 0 : {
						spaceNetworkId,
						roleId,
						roleName,
						permissions,
						users,
						ruleData
					}
				};
			},
			aaExecute: ({ legacySpaceArgs, spaceArgs }) => {
				if (legacySpaceArgs) return this.userOps?.sendLegacyUpdateRoleOp([legacySpaceArgs, signer]);
				else if (spaceArgs) return this.userOps?.sendUpdateRoleOp([spaceArgs, signer]);
				throw new Error("No args provided");
			},
			eoaExecute: ({ legacySpaceArgs, spaceArgs }) => {
				if (legacySpaceArgs) return this.spaceDapp.legacyUpdateRole(legacySpaceArgs, signer);
				else if (spaceArgs) return this.spaceDapp.updateRole(spaceArgs, signer);
				throw new Error("No args provided");
			},
			errorDecoder: (error) => this.getDecodedErrorForSpace(spaceNetworkId, error)
		});
	}
	async banTransaction(spaceId, userId, signer) {
		const walletAddressWithToken = await this.baseReads.walletAddressForMembership(spaceId, userId);
		if (!walletAddressWithToken) throw new Error("Membership token not found");
		return this.createTransaction({
			signer,
			type: BlockchainTransactionType.BanUser,
			contextData: {
				spaceId,
				userId,
				isBan: true
			},
			debugName: "banTransaction",
			aaExecute: () => {
				return this.userOps?.sendBanWalletAddressOp([
					spaceId,
					walletAddressWithToken,
					signer
				]);
			},
			eoaExecute: () => {
				return this.spaceDapp.banWalletAddress(spaceId, walletAddressWithToken, signer);
			},
			errorDecoder: (error) => this.getDecodedErrorForSpace(spaceId, error)
		});
	}
	async unbanTransaction(spaceId, userId, signer) {
		const walletAddressWithToken = await this.baseReads.walletAddressForMembership(spaceId, userId);
		if (!walletAddressWithToken) throw new Error("Membership token not found");
		return this.createTransaction({
			signer,
			type: BlockchainTransactionType.UnbanUser,
			contextData: {
				spaceId,
				userId,
				isBan: false
			},
			debugName: "unbanTransaction",
			aaExecute: () => {
				return this.userOps?.sendUnbanWalletAddressOp([
					spaceId,
					walletAddressWithToken,
					signer
				]);
			},
			eoaExecute: () => {
				return this.spaceDapp.unbanWalletAddress(spaceId, walletAddressWithToken, signer);
			},
			errorDecoder: (error) => this.getDecodedErrorForSpace(spaceId, error)
		});
	}
	async deleteRoleTransaction(spaceNetworkId, roleId, signer) {
		if (!signer) throw new SignerUndefinedError();
		return this.createTransaction({
			signer,
			type: BlockchainTransactionType.DeleteRole,
			debugName: "deleteRoleTransaction",
			aaExecute: () => {
				return this.userOps?.sendDeleteRoleOp([
					spaceNetworkId,
					roleId,
					signer
				]);
			},
			eoaExecute: () => {
				return this.spaceDapp.deleteRole(spaceNetworkId, roleId, signer);
			},
			errorDecoder: (error) => this.getDecodedErrorForSpace(spaceNetworkId, error)
		});
	}
	/************************************************
	* mintMembershipTransaction
	*
	* a special transaction because of xchain
	* while the initial tx may be confirmed, either userop or traditional, the confirmation of that tx does not represent successful join
	* xchain nodes vote on the tx, let base chain know, and events are emitted for pass/fail whether the nft was minted
	* this falls out of the typical pattern of being able to wait for a tx
	*************************************************/
	async mintMembershipTransaction(args) {
		const { spaceId, signer, onJoinFlowStatus, wallets, walletType } = args;
		const rootWallet = await signer.getAddress();
		const space = this.spaceDapp.getSpace(spaceId);
		if (!space) throw new Error("Space not found");
		const membershipStatus = await space.getMembershipStatus(wallets);
		if (membershipStatus.isMember) throw new MembershipAlreadyMemberError();
		this.log("[mintMembershipTransaction] start");
		let transaction = void 0;
		const continueStoreTx = this.blockchainTransactionStore.begin({ type: BlockchainTransactionType.JoinSpace });
		const timeTracker = getTimeTracker();
		try {
			const space$1 = this.spaceDapp.getSpace(spaceId);
			if (!space$1) throw new Error("Space not found");
			const hasMembership = membershipStatus.isMember;
			if (hasMembership) return;
			let membershipListener;
			const abortController = new AbortController();
			let recipient = void 0;
			if (this.isAccountAbstractionEnabled()) {
				const smClient = await this.userOps?.smartAccountSession.getSmartAccount({ signer });
				recipient = smClient?.address;
				if (!recipient) throw new Error("Abstract account address not found");
			} else recipient = rootWallet;
			const args$1 = [
				spaceId,
				recipient,
				signer
			];
			if (walletType === "eoa" || !this.isAccountAbstractionEnabled()) membershipListener = this.spaceDapp.joinSpace(...args$1);
			else {
				const blockNumber = await this.spaceDapp.provider.getBlockNumber();
				const startListener = space$1.Membership.listenForMembershipToken({
					receiver: recipient,
					startingBlock: blockNumber,
					providedAbortController: abortController
				});
				transaction = await this.userOps?.sendJoinSpaceOp([...args$1]);
				membershipListener = startListener();
			}
			const membershipOrTimeout = waitForTimeoutOrMembership({
				hashOrUserOpHash: getTransactionHashOrUserOpHash(transaction),
				membershipListener,
				abortController
			});
			continueStoreTx({
				hashOrUserOpHash: getTransactionHashOrUserOpHash(transaction),
				transaction,
				eventListener: { wait: async () => {
					const { issued: issued$1, tokenId: tokenId$1 } = await membershipOrTimeout;
					return {
						success: issued$1,
						tokenId: tokenId$1
					};
				} },
				error: void 0
			});
			this.log(`[mintMembershipTransaction] transaction created, starting membershipListener`, { transaction });
			const endWaitForMembership = timeTracker.startMeasurement(TimeTrackerEvents.JOIN_SPACE, "contract_wait_for_membership_issued", { userOpHash: getTransactionHashOrUserOpHash(transaction) });
			onJoinFlowStatus?.(JoinFlowStatus.WaitingForMembership);
			const { issued, tokenId, error } = await membershipOrTimeout;
			endWaitForMembership?.();
			this.log("[mintMembershipTransaction] membershipListener result", issued, tokenId);
			if (error) throw error;
			if (!issued) throw new MembershipRejectedError();
		} catch (error) {
			console.error("[mintMembershipTransaction] failed", error);
			let decodeError;
			if (error instanceof MembershipRejectedError) decodeError = error;
			else decodeError = this.getDecodedErrorForSpace(spaceId, error);
			console.error("[mintMembershipAndJoinRoom] failed", decodeError);
			continueStoreTx({
				hashOrUserOpHash: getTransactionHashOrUserOpHash(transaction),
				transaction,
				error: decodeError
			});
			throw decodeError;
		}
		return {
			status: TransactionStatus.Success,
			data: {
				spaceId,
				wallets
			},
			transaction,
			receipt: void 0,
			error: void 0
		};
	}
	async transferAsset(transferData, signer) {
		let type = void 0;
		if (transferData.value) type = BlockchainTransactionType.TransferBaseEth;
		else if (transferData.contractAddress && transferData.tokenId) type = BlockchainTransactionType.TransferNft;
		else if (transferData.contractAddress && transferData.erc20Amount) type = BlockchainTransactionType.Transfer;
		if (!type) throw new Error("Invalid transfer data");
		return this.createTransaction({
			signer,
			type,
			debugName: "transferAsset",
			contextData: transferData,
			aaExecute: async () => {
				let transaction = void 0;
				if (transferData.value) transaction = await this.userOps?.sendTransferEthOp({
					recipient: transferData.recipient,
					value: transferData.value
				}, signer);
				else if (transferData.contractAddress && transferData.tokenId) transaction = await this.userOps?.sendTransferAssetsOp({
					contractAddress: transferData.contractAddress,
					recipient: transferData.recipient,
					tokenId: transferData.tokenId
				}, signer);
				else if (transferData.contractAddress && transferData.erc20Amount) transaction = await this.userOps?.sendTransferERC20Op({
					recipient: transferData.recipient,
					contractAddress: transferData.contractAddress,
					amount: transferData.erc20Amount,
					signer
				});
				if (!transaction) throw new Error("Transaction not created");
				return transaction;
			},
			eoaExecute: () => {
				throw new Error("Not implemented");
			},
			errorDecoder: (error) => {
				return error;
			}
		});
	}
	/************************************************
	* Wallet linking
	*/
	async linkEOAToRootKey(rootKey, wallet) {
		const rootKeyAddress = await rootKey.getAddress();
		const walletAddress = await wallet.getAddress();
		const walletLink = this.spaceDapp.getWalletLink();
		return this.createTransaction({
			signer: rootKey,
			type: BlockchainTransactionType.LinkWallet,
			contextData: {
				rootKeyAddress,
				walletAddress
			},
			debugName: "linkEOAToRootKey",
			aaExecute: () => this.userOps?.sendLinkEOAToRootKeyOp([rootKey, wallet]),
			eoaExecute: () => walletLink.linkWalletToRootKey(rootKey, wallet),
			errorDecoder: (error) => walletLink.parseError(error)
		});
	}
	/**
	* Link the caller of the tx to the root key. For now, this is used to link a smart account to a root key.
	* @param rootKey
	* @param wallet - optional because if it's a user op, we only need the root key
	* @returns
	*/
	async linkCallerToRootKey(rootKey, wallet) {
		const rootKeyAddress = await rootKey.getAddress();
		let walletAddress = "";
		const walletLink = this.spaceDapp.getWalletLink();
		class NeedWalletError extends Error {
			constructor() {
				super("[linkCallerToRootKey] wallet address must be provided when account abstraction is disabled");
			}
		}
		if (this.isAccountAbstractionEnabled()) {
			walletAddress = await this.getAbstractAccountAddress({ rootKeyAddress }) ?? "";
			if (!walletAddress || walletAddress === "") throw new Error("Abstract account address not found");
			if (wallet) throw new Error("[linkCallerToRootKey] wallet address should not be provided when account abstraction is enabled");
		} else {
			if (!wallet) throw new NeedWalletError();
			walletAddress = await wallet.getAddress();
		}
		return this.createTransaction({
			signer: rootKey,
			type: BlockchainTransactionType.LinkWallet,
			contextData: {
				rootKeyAddress,
				walletAddress
			},
			debugName: "linkCallerToRootKey",
			aaExecute: () => {
				return this.userOps?.sendLinkSmartAccountToRootKeyOp(rootKey, walletAddress);
			},
			eoaExecute: () => {
				if (!wallet) throw new NeedWalletError();
				return walletLink.linkCallerToRootKey(rootKey, wallet);
			},
			errorDecoder: (error) => walletLink.parseError(error)
		});
	}
	async unlinkViaRootKey(rootKey, walletAddress) {
		const walletLink = this.spaceDapp.getWalletLink();
		return this.createTransaction({
			signer: rootKey,
			type: BlockchainTransactionType.UnlinkWallet,
			contextData: {
				rootKeyAddress: await rootKey.getAddress(),
				walletAddress
			},
			debugName: "unlinkViaRootKey",
			aaExecute: () => this.userOps?.sendRemoveWalletLinkOp([rootKey, walletAddress]),
			eoaExecute: () => walletLink.removeLink(rootKey, walletAddress),
			errorDecoder: (error) => walletLink.parseError(error)
		});
	}
	async unlinkViaCaller(caller) {
		const walletLink = this.spaceDapp.getWalletLink();
		return this.createTransaction({
			signer: caller,
			type: BlockchainTransactionType.UnlinkWallet,
			contextData: { walletAddress: await caller.getAddress() },
			debugName: "unlinkViaCaller",
			aaExecute: () => walletLink.removeCallerLink(caller),
			eoaExecute: () => walletLink.removeCallerLink(caller),
			errorDecoder: (error) => walletLink.parseError(error)
		});
	}
	async waitForBlockchainTransaction(context, sequenceName, confirmations) {
		if (!context?.transaction) return createTransactionContext({
			status: TransactionStatus.Failed,
			error: new Error(`[_waitForBlockchainTransaction] transaction is undefined`)
		});
		let transaction = void 0;
		let receipt = void 0;
		let error = void 0;
		transaction = context.transaction;
		try {
			if (isUserOpResponse(transaction)) {
				let endWaitForUserOpReceipt;
				if (sequenceName) endWaitForUserOpReceipt = getTimeTracker().startMeasurement(sequenceName, "userops_wait_for_user_operation_receipt", { userOpHash: transaction.userOpHash });
				const userOpReceipt = await transaction.getUserOperationReceipt();
				if (endWaitForUserOpReceipt) endWaitForUserOpReceipt();
				if (userOpReceipt) {
					if (userOpReceipt.success === false) throw new Error(`[_waitForBlockchainTransaction] user operation was not successful`);
					let endWaitForTxConfirmation;
					if (sequenceName) endWaitForTxConfirmation = getTimeTracker().startMeasurement(sequenceName, "userops_wait_for_ethers_receipt");
					receipt = await this.baseProvider?.waitForTransaction(userOpReceipt.receipt?.transactionHash, confirmations);
					if (endWaitForTxConfirmation) endWaitForTxConfirmation();
				} else throw new Error(`[_waitForBlockchainTransaction] userOpEvent is undefined`);
			} else receipt = await this.baseProvider?.waitForTransaction(transaction.hash, confirmations);
			if (receipt?.status === 1) {
				this.log("receipt", receipt);
				return createTransactionContext({
					status: TransactionStatus.Success,
					transaction,
					data: context.data,
					receipt
				});
			} else if (receipt?.status === 0) await this.throwTransactionError(receipt);
			else throw new Error(`[_waitForBlockchainTransaction] failed because receipt.status is undefined`);
		} catch (err) {
			if (err instanceof Error) error = err;
			else error = new Error(`$[_waitForBlockchainTransaction] failed: ${JSON.stringify(err)}`);
		}
		return createTransactionContext({
			status: TransactionStatus.Failed,
			transaction,
			receipt,
			error
		});
	}
	async tipTransaction(args) {
		const { signer, receiverUsername, receiverUserId, senderAddress, messageId, channelId, amount, currency, type } = args;
		const spaceId = args.type !== "any" ? args.spaceId : void 0;
		const txContextData = {
			receiverUsername,
			spaceId,
			senderAddress,
			messageId,
			channelId,
			receiverUserId,
			amount,
			currency,
			type
		};
		if (!/^[0-9a-fA-F]*$/.test(messageId)) throw new Error("Invalid hex string for messageId");
		if (!/^[0-9a-fA-F]*$/.test(channelId)) throw new Error("Invalid hex string for channelId");
		const receiver = args.receiverAddress.startsWith("0x") ? args.receiverAddress : `0x${args.receiverAddress}`;
		const currencyAddress = currency.startsWith("0x") ? currency : `0x${currency}`;
		const formattedMessageId = messageId.startsWith("0x") ? messageId : `0x${messageId}`;
		const formattedChannelId = channelId.startsWith("0x") ? channelId : `0x${channelId}`;
		const sendTipParams = (() => {
			if (args.type === "bot") return {
				type: "bot",
				spaceId: args.spaceId,
				receiver,
				appId: args.appId.startsWith("0x") ? args.appId : `0x${args.appId}`,
				currency: currencyAddress,
				amount,
				messageId: formattedMessageId,
				channelId: formattedChannelId
			};
			else if (args.type === "any") return {
				type: "any",
				receiver,
				currency: currencyAddress,
				amount,
				messageId: formattedMessageId,
				channelId: formattedChannelId,
				sender: senderAddress
			};
			else return {
				type: "member",
				spaceId: args.spaceId,
				receiver,
				tokenId: args.tokenId,
				currency: currencyAddress,
				amount,
				messageId: formattedMessageId,
				channelId: formattedChannelId
			};
		})();
		return this.createTransaction({
			signer,
			walletType: args.walletType,
			type: BlockchainTransactionType.Tip,
			contextData: txContextData,
			debugName: "tipTransaction",
			aaExecute: () => this.userOps?.sendTipOp({
				tipParams: sendTipParams,
				signer
			}),
			eoaExecute: () => this.spaceDapp.sendTip({
				tipParams: sendTipParams,
				signer
			}),
			errorDecoder: (error) => {
				if (isInsufficientTipBalanceException(error)) return error;
				if (!spaceId) {
					const accountModulesAddress = this.spaceDapp.config.addresses.accountModules;
					if (!accountModulesAddress) throw new Error("Account module is not deployed for this environment");
					const tippingShim = new ITippingShim(accountModulesAddress, this.spaceDapp.provider);
					return tippingShim.parseError(error);
				}
				return this.getDecodedErrorForSpace(spaceId, error);
			}
		});
	}
	async updateAppPricing(args) {
		const { signer, appAddress, installPrice, accessDuration } = args;
		return this.createTransaction({
			signer,
			type: BlockchainTransactionType.UpdateAppPricing,
			contextData: {
				appAddress,
				installPrice,
				accessDuration
			},
			debugName: "app_updatePricing",
			aaExecute: () => this.userOps?.sendUpdateAppPricingOp(args),
			eoaExecute: () => this.appRegistryDapp.callApp(args.appAddress).write(signer).updatePricing(args.installPrice, args.accessDuration),
			errorDecoder: (error) => {
				return this.getDecodedErrorForSimpleApp(error) ?? new Error("Failed to update app pricing", { cause: error });
			}
		});
	}
	async withdrawAppFunds(args) {
		const { signer, appAddress, recipient, currencyAddress, balance } = args;
		return this.createTransaction({
			signer,
			type: BlockchainTransactionType.WithdrawAppFunds,
			contextData: {
				appAddress,
				recipient
			},
			debugName: "app_withdrawFunds",
			aaExecute: () => this.userOps?.sendWithdrawAppFundsOp(args),
			eoaExecute: () => {
				if (currencyAddress) {
					const transferData = encodeFunctionData({
						abi: erc20Abi,
						functionName: "transfer",
						args: [recipient, balance]
					});
					const executeCalldata = encodeERC7821Execute([{
						to: currencyAddress,
						data: transferData
					}]);
					return signer.sendTransaction({
						to: appAddress,
						data: executeCalldata
					});
				}
				return this.appRegistryDapp.callApp(args.appAddress).write(signer).withdrawETH(recipient);
			},
			errorDecoder: (error) => {
				return this.getDecodedErrorForSimpleApp(error) ?? new Error("Failed to withdraw app funds", { cause: error });
			}
		});
	}
	async promoteAgent(args) {
		const { signer, appAddress, agentUri } = args;
		return this.createTransaction({
			signer,
			type: BlockchainTransactionType.PromoteAgent,
			contextData: {
				appAddress,
				agentUri
			},
			debugName: "app_promoteAgent",
			aaExecute: () => {
				const userOps = this.userOps;
				if (!userOps) throw new Error("User operations client is not initialized");
				const promoteArgs = {
					appAddress,
					agentUri,
					signer
				};
				const op = userOps.sendPromoteAgentOp(promoteArgs);
				return op;
			},
			eoaExecute: () => this.appRegistryDapp.callApp(args.appAddress).write(signer).promoteAgent(agentUri, []),
			errorDecoder: (error) => {
				try {
					const simpleApp = this.appRegistryDapp.callApp(appAddress);
					const hasRevertData = (err) => {
						if (typeof err !== "object" || err === null) return false;
						const obj = err;
						const valueObj = obj["value"];
						if (typeof valueObj !== "object" || valueObj === null) return false;
						const valueRec = valueObj;
						return typeof valueRec["revertData"] === "string";
					};
					const normalized = hasRevertData(error) === true ? {
						data: error.value.revertData,
						message: typeof error.message === "string" ? error.message : void 0
					} : error;
					return simpleApp.parseError(normalized);
				} catch (e) {
					return this.tryDecodeError(e, "[promoteAgent] cannot decode error");
				}
			}
		});
	}
	async uninstallApp(args) {
		const { signer, target } = args;
		if (target === "space") {
			const { spaceAddress, appAddress: appAddress$1, appUserId } = args;
			return this.createTransaction({
				signer,
				type: BlockchainTransactionType.UninstallApp,
				contextData: {
					target,
					spaceAddress,
					appAddress: appAddress$1,
					appUserId
				},
				debugName: "uninstallApp",
				aaExecute: () => this.userOps?.sendUninstallAppOp({
					target: "space",
					spaceAddress,
					appAddress: appAddress$1,
					signer
				}),
				eoaExecute: () => this.appRegistryDapp.uninstallApp(signer, appAddress$1, spaceAddress),
				errorDecoder: (error) => this.getDecodedErrorForAppRegistry(error) ?? new Error("Failed to uninstall app", { cause: error })
			});
		}
		const { userAddress, appAddress } = args;
		return this.createTransaction({
			signer,
			type: BlockchainTransactionType.UninstallApp,
			contextData: {
				target,
				userAddress,
				appAddress
			},
			debugName: "uninstallApp",
			aaExecute: () => this.userOps?.sendUninstallAppOp({
				target: "userAccount",
				userAddress,
				appAddress,
				signer
			}),
			eoaExecute: () => {
				throw new Error("EOA execution not supported for uninstalling apps from user accounts");
			},
			errorDecoder: (error) => this.getDecodedErrorForAppRegistry(error) ?? new Error("Failed to uninstall app", { cause: error })
		});
	}
	async updateAppPermissions(args) {
		const { signer, appAddress, permissions, appId, clientAddress } = args;
		return this.createTransaction({
			signer,
			type: BlockchainTransactionType.UpdateAppPermissions,
			contextData: {
				appAddress,
				appId,
				clientAddress,
				permissions
			},
			debugName: "updateAppPermissions",
			aaExecute: () => {
				return this.userOps?.sendUpdateAppPermissionsOp(args);
			},
			eoaExecute: () => {
				throw new Error("Not implemented");
			},
			errorDecoder: (error) => this.getDecodedErrorForSimpleApp(error) ?? new Error("Failed to update app permissions", { cause: error })
		});
	}
	async updateApp(args) {
		const { signer, appAddress, spaceAddress, latestAppId } = args;
		return this.createTransaction({
			signer,
			type: BlockchainTransactionType.UpdateApp,
			contextData: {
				appAddress,
				spaceAddress,
				latestAppId
			},
			debugName: "updateApp",
			aaExecute: () => {
				return this.userOps?.sendUpdateAppOp(args);
			},
			eoaExecute: () => {
				throw new Error("Not implemented");
			},
			errorDecoder: (error) => this.getDecodedErrorForAppRegistry(error) ?? new Error("Failed to update app permissions", { cause: error })
		});
	}
	async renewApp(args) {
		const { signer, appAddress, spaceAddress, installPrice } = args;
		return this.createTransaction({
			signer,
			type: BlockchainTransactionType.RenewApp,
			contextData: {
				appAddress,
				spaceAddress,
				installPrice
			},
			debugName: "renewApp",
			aaExecute: () => {
				return this.userOps?.sendRenewAppOp(args);
			},
			eoaExecute: () => {
				throw new Error("Not implemented");
			},
			errorDecoder: (error) => this.getDecodedErrorForAppRegistry(error) ?? new Error("Failed to renew app", { cause: error })
		});
	}
	async installAppOnUserAccountTransaction(args) {
		const { appAddress, price, signer } = args;
		const smartAccount = await this.userOps?.smartAccountSession.getSmartAccount({ signer });
		if (!smartAccount) throw new Error("Smart account not available");
		const userAccountAddress = smartAccount.address;
		return this.createTransaction({
			signer,
			type: BlockchainTransactionType.InstallAppOnUserAccount,
			contextData: {
				appAddress,
				price
			},
			debugName: "installAppOnUserAccountTransaction",
			aaExecute: () => {
				return this.userOps?.sendInstallAppOp({
					appAddress,
					targetAddress: userAccountAddress,
					targetType: "userAccount",
					price,
					signer
				});
			},
			eoaExecute: () => {
				throw new Error("EOA execution not supported for installing apps on user accounts");
			},
			errorDecoder: (error) => {
				return this.getDecodedErrorForAppRegistry(error) ?? new Error("Failed to install app on user account");
			}
		});
	}
	async installAccountModulesAndAppTransaction(args) {
		const { appAddress, price, signer } = args;
		const accountModulesAddress = this.spaceDapp.config.addresses.accountModules;
		if (!accountModulesAddress) throw new Error("AccountModules address not configured");
		return this.createTransaction({
			signer,
			type: BlockchainTransactionType.InstallAccountModulesAndApp,
			contextData: {
				appAddress,
				price
			},
			debugName: "installAccountModulesAndAppTransaction",
			aaExecute: () => {
				return this.userOps?.sendInstallAccountModulesAndAppOp({
					accountModulesAddress,
					appAddress,
					price,
					signer
				});
			},
			eoaExecute: () => {
				throw new Error("EOA execution not supported for installing AccountModules yet");
			},
			errorDecoder: (error) => {
				return this.getDecodedErrorForAppRegistry(error) ?? new Error("Failed to install AccountModules and app");
			}
		});
	}
	async throwTransactionError(receipt) {
		try {
			const code = await this.baseProvider?.call(receipt, receipt.blockNumber);
			const reason = toUtf8String(`0x${code?.substring(138) || ""}`);
			throw new Error(reason);
		} catch (error) {
			const deepMessage = error.error?.error?.message;
			if (deepMessage) throw new Error(deepMessage);
			throw error;
		}
	}
	getDecodedErrorForSpace(spaceId, error) {
		if (skipErrorDecoding(error)) return error;
		try {
			const fakeError = this.spaceDapp.parseSpaceError(spaceId, error);
			const realError = new Error(fakeError.message);
			realError.name = fakeError.name;
			if ("code" in error) realError.code = error.code;
			return realError;
		} catch (e) {
			return this.tryDecodeError(e, `[getDecodedErrorForSpace] cannot decode error`);
		}
	}
	getDecodedErrorForSpaceFactory(error) {
		if (skipErrorDecoding(error)) return error;
		try {
			return this.spaceDapp.parseSpaceFactoryError(error);
		} catch (e) {
			return this.tryDecodeError(e, `[getDecodedErrorForSpaceFactory] cannot decode error`);
		}
	}
	getDecodedErrorForRiverPoints(error) {
		if (skipErrorDecoding(error)) return error;
		try {
			return this.spaceDapp.airdrop?.riverPoints?.parseError(error);
		} catch (e) {
			return this.tryDecodeError(e, `[getDecodedErrorForRiverPoints] cannot decode error`);
		}
	}
	getDecodedErrorForRewardsDistributionV2(error) {
		if (skipErrorDecoding(error)) return error;
		try {
			return this.spaceDapp.baseRegistry.rewardsDistributionV2?.parseError(error);
		} catch (e) {
			return this.tryDecodeError(e, `[getDecodedErrorForRewardsDistributionV2] cannot decode error`);
		}
	}
	getDecodedErrorForAppRegistry(error) {
		if (skipErrorDecoding(error)) return error;
		try {
			return this.appRegistryDapp.registry.parseError(error);
		} catch (e) {
			return this.tryDecodeError(e, `[getDecodedErrorForAppRegistry] cannot decode error`);
		}
	}
	getDecodedErrorForSimpleApp(error) {
		if (skipErrorDecoding(error)) return error;
		try {
			return this.appRegistryDapp.callApp(error).parseError(error);
		} catch (e) {
			return this.tryDecodeError(e, `[getDecodedErrorForSimpleApp] cannot decode error`);
		}
	}
	tryDecodeError(e, defaultText) {
		if (e instanceof Error) return e;
		if (typeof e === "object" && e !== null && "name" in e && typeof e.name === "string" && "message" in e && typeof e.message === "string" && e.message !== void 0) {
			const newErr = new Error(e.message);
			newErr.name = e.name;
			if ("code" in e) newErr.code = e.code;
			return newErr;
		} else return new Error(defaultText);
	}
	parseSpaceDelegationError(args) {
		const { message, originalError } = args;
		const iface = this.spaceDapp.baseRegistry.spaceDelegation.interface;
		const tryParseError = (data) => {
			const selector = data.slice(0, 10);
			for (const frag of Object.values(iface.errors)) {
				const sig = iface.getSighash(frag);
				if (sig === selector) return { name: frag.name };
			}
			return null;
		};
		const newError = new Error(message);
		const errorSelector = tryParseError(originalError.data);
		newError.name = errorSelector?.name ?? "UNKNOWN_ERROR";
		newError.data = errorSelector?.name;
		return newError;
	}
	async createTransaction(args) {
		const { signer, type, debugName, contextData, preExecute, aaExecute, eoaExecute, errorDecoder, walletType = "smartAccount" } = args;
		if (!signer) throw new SignerUndefinedError();
		let transaction = void 0;
		let error = void 0;
		const PreExecuteReturnType = preExecute ? await preExecute() : void 0;
		const continueStoreTx = this.blockchainTransactionStore.begin({
			type,
			data: contextData ?? void 0
		});
		try {
			if (walletType === "eoa" || !this.isAccountAbstractionEnabled()) transaction = await eoaExecute(PreExecuteReturnType);
			else transaction = await aaExecute(PreExecuteReturnType);
			this.log(`[${debugName}] transaction created`);
		} catch (err) {
			error = errorDecoder(err);
			addCategoryToError(error, getErrorCategory(err) ?? "userop");
		}
		continueStoreTx({
			hashOrUserOpHash: getTransactionHashOrUserOpHash(transaction),
			transaction,
			error
		});
		return {
			transaction,
			receipt: void 0,
			status: transaction ? TransactionStatus.Pending : TransactionStatus.Failed,
			data: transaction ? contextData : void 0,
			error
		};
	}
};

//#endregion
//#region src/base-writes/BaseWritesProvider.tsx
const BaseWritesContext = createContext(void 0);
const BaseWritesProvider = ({ children, accountAbstractionConfig, baseChainConfig, createLegacySpaces, analytics, enableUsdc }) => {
	const baseReads = useBaseReads();
	const userOps = useRef((() => {
		if (!accountAbstractionConfig) return void 0;
		return new UserOps({
			...accountAbstractionConfig,
			provider: baseReads.baseProvider,
			config: baseChainConfig,
			spaceDapp: baseReads.spaceDapp,
			appRegistryDapp: baseReads.appRegistryDapp,
			timeTracker: getTimeTracker(analytics),
			enableUsdc: enableUsdc ?? false
		});
	})());
	const baseTransactorRef = useRef(new BaseTransactor({
		createLegacySpaces: !!createLegacySpaces,
		baseProvider: baseReads.baseProvider,
		baseConfig: baseChainConfig,
		baseReads,
		userOps: userOps.current
	}));
	useEffect(() => {
		if (!userOps.current && accountAbstractionConfig) (async function() {
			await baseTransactorRef.current.blockchainTransactionStore.stop();
			userOps.current = new UserOps({
				...accountAbstractionConfig,
				provider: baseReads.baseProvider,
				config: baseChainConfig,
				spaceDapp: baseReads.spaceDapp,
				appRegistryDapp: baseReads.appRegistryDapp,
				timeTracker: getTimeTracker(analytics),
				enableUsdc: enableUsdc ?? false
			});
			baseTransactorRef.current = new BaseTransactor({
				createLegacySpaces: !!createLegacySpaces,
				baseProvider: baseReads.baseProvider,
				baseConfig: baseChainConfig,
				baseReads,
				userOps: userOps.current
			});
		})();
	}, [
		accountAbstractionConfig,
		analytics,
		baseChainConfig,
		baseReads,
		createLegacySpaces,
		enableUsdc
	]);
	return /* @__PURE__ */ jsx(BaseWritesContext.Provider, {
		value: {
			userOpsInstance: userOps.current,
			createLegacySpaces,
			baseTransactor: baseTransactorRef.current
		},
		children
	});
};
const useBaseWrites = () => {
	const context = useContext(BaseWritesContext);
	if (context === void 0) throw new Error("useBaseWrites must be used within a BaseWritesProvider");
	return context;
};

//#endregion
//#region src/base-writes/stores/primaryWallet.store.ts
const primaryWalletStore = create()(persist((set, get) => ({
	users: {},
	setPrimaryWallet: (userId, type, address) => {
		set((state) => ({ users: {
			...state.users,
			[userId]: {
				type,
				address: address.toLowerCase()
			}
		} }));
	},
	getPrimaryWallet: (userId) => {
		return get().users[userId];
	}
}), {
	name: "towns/primary-wallet",
	version: 1
}));

//#endregion
//#region src/hooks/use-transaction.ts
function useTransaction(config$1) {
	const [transactionContext, setTransactionContext] = useState(void 0);
	const isTransacting = useRef(false);
	const queryClient$1 = useQueryClient$1();
	const { clientSingleton } = useTownsClient();
	const { baseTransactor } = useBaseWrites();
	const defaultWaitForTransaction = useCallback((context) => {
		if (!clientSingleton) throw new Error("Towns client not initialized");
		return baseTransactor.waitForBlockchainTransaction(context);
	}, [clientSingleton, baseTransactor]);
	const { execute: executeTransaction, waitForTransaction, onPending, onSuccess, onError, invalidateQueries, optimisticUpdate, debugName = "useTransaction" } = config$1;
	const _waitForTransaction = waitForTransaction || defaultWaitForTransaction;
	const { data, isLoading, isSuccess, isError, transactionHash, transactionStatus, error } = useMemo(() => {
		return {
			data: transactionContext?.data,
			isLoading: transactionContext?.status === TransactionStatus.Pending,
			isSuccess: transactionContext?.status === TransactionStatus.Success,
			isError: transactionContext?.status === TransactionStatus.Failed,
			transactionHash: getTransactionHashOrUserOpHash(transactionContext?.transaction),
			transactionStatus: transactionContext?.status ?? TransactionStatus.None,
			error: transactionContext?.error
		};
	}, [transactionContext]);
	const reset = useCallback(() => {
		setTransactionContext(void 0);
		isTransacting.current = false;
	}, []);
	const execute = useCallback(async function(params) {
		if (isTransacting.current) {
			console.warn(`[${debugName}]`, "Transaction already in progress");
			return void 0;
		}
		let transactionResult;
		if (!params.signer) {
			console.error(`[${debugName}]`, "Signer is undefined");
			transactionResult = createTransactionContext({
				status: TransactionStatus.Failed,
				error: new SignerUndefinedError()
			});
			setTransactionContext(transactionResult);
			return transactionResult;
		}
		isTransacting.current = true;
		try {
			transactionResult = createTransactionContext({ status: TransactionStatus.Pending });
			setTransactionContext(transactionResult);
			await onPending?.(params);
			const executionResult = await executeTransaction(params);
			if (!executionResult) throw new Error("Transaction execution returned undefined");
			transactionResult = executionResult;
			setTransactionContext(transactionResult);
			if (transactionResult?.status === TransactionStatus.Pending) {
				optimisticUpdate?.apply(params);
				const waitResult = await _waitForTransaction(transactionResult, params);
				if (waitResult) {
					transactionResult = waitResult;
					setTransactionContext(transactionResult);
				}
			}
			if (transactionResult?.status === TransactionStatus.Success) {
				await onSuccess?.(params, transactionResult);
				if (invalidateQueries) {
					const queryKeys = invalidateQueries(params, transactionResult.data);
					for (const queryKey of queryKeys) await queryClient$1.invalidateQueries({ queryKey });
				}
			} else if (transactionResult?.status === TransactionStatus.Failed) {
				console.error(`[${debugName}]`, "Transaction failed", transactionResult.error);
				optimisticUpdate?.rollback?.(params, transactionResult.error);
				await onError?.(transactionResult.error || new Error("Transaction failed"), params, transactionResult);
			}
		} catch (e) {
			console.error(`[${debugName}]`, e);
			const error$1 = toError(e);
			optimisticUpdate?.rollback?.(params, error$1);
			transactionResult = createTransactionContext({
				status: TransactionStatus.Failed,
				error: error$1
			});
			setTransactionContext(transactionResult);
			await onError?.(error$1, params, transactionResult);
		} finally {
			isTransacting.current = false;
		}
		return transactionResult;
	}, [
		debugName,
		optimisticUpdate,
		onPending,
		executeTransaction,
		_waitForTransaction,
		onSuccess,
		invalidateQueries,
		queryClient$1,
		onError
	]);
	return {
		execute,
		isLoading,
		isSuccess,
		isError,
		data,
		error,
		transactionHash,
		transactionStatus,
		transactionContext,
		reset
	};
}

//#endregion
//#region src/base-writes/hooks/use-set-primary-wallet.ts
function useSetPrimaryWallet({ onSuccess, onError }) {
	const { spaceDapp } = useBaseReads();
	const { baseTransactor, userOpsInstance } = useBaseWrites();
	const setPrimaryWalletInStore = primaryWalletStore((state) => state.setPrimaryWallet);
	return useTransaction({
		debugName: "useSetPrimaryWalletTransaction",
		execute: async ({ userId, address, signer, type }) => {
			if (!isAddress(userId)) throw new Error("userId is not a valid address");
			return baseTransactor.createTransaction({
				debugName: "setPrimaryWalletTransaction",
				type: BlockchainTransactionType.SetPrimaryWallet,
				signer,
				contextData: {
					type,
					address
				},
				aaExecute: () => {
					if (!userOpsInstance) throw new Error("userOps not found");
					return spaceDapp.walletLink.setDefaultWallet({
						signer,
						walletAddress: address,
						overrideExecution: async ({ toAddress, calldata }) => {
							const smartAccount = await userOpsInstance.smartAccountSession.getSmartAccount({ signer });
							return smartAccount.sendUserOp({
								toAddress,
								callData: calldata,
								functionHashForPaymasterProxy: "setPrimaryWallet",
								spaceId: void 0,
								signer
							});
						}
					});
				},
				eoaExecute: async () => {
					return spaceDapp.walletLink.setDefaultWallet({
						signer,
						walletAddress: address
					});
				},
				errorDecoder: (error) => new Error(error)
			});
		},
		onSuccess: ({ userId, address, type }) => {
			onSuccess?.({
				userId,
				address,
				type
			});
			setPrimaryWalletInStore(userId, type, address);
		},
		onError: (error) => {
			onError?.(error);
		},
		invalidateQueries: (params) => {
			return [blockchainKeys.primaryWallet(params.userId)];
		}
	});
}
function useLinkSmartAccountAndEOAAndSetDefaultWallet({ onSuccess, onError } = {}) {
	const { baseTransactor, userOpsInstance } = useBaseWrites();
	const setPrimaryWalletInStore = primaryWalletStore((state) => state.setPrimaryWallet);
	return useTransaction({
		debugName: "useSetPrimaryWalletTransaction",
		execute: async ({ userId, signer, smartAccountAddress, externalWalletSigner }) => {
			if (!isAddress(userId)) throw new Error("userId is not a valid address");
			const externalWalletAddress = await externalWalletSigner.getAddress();
			if (!isAddress(externalWalletAddress)) throw new Error("externalWalletAddress is not a valid address");
			return baseTransactor.createTransaction({
				debugName: "setPrimaryWalletTransaction",
				type: BlockchainTransactionType.SetPrimaryWallet,
				signer,
				contextData: {
					type: "eoa",
					address: externalWalletAddress
				},
				aaExecute: () => {
					if (!userOpsInstance) throw new Error("userOps not found");
					return userOpsInstance.sendLinkSmartAccountAndEOAAndSetDefaultWalletOp({
						rootKeySigner: signer,
						smartAccountAddress,
						externalWalletSigner
					});
				},
				eoaExecute: () => {
					throw new Error("EOA not supported for sending linkSmartAccountAndEOAAndSetDefaultWalletOp");
				},
				errorDecoder: (error) => new Error(error)
			});
		},
		onSuccess: async ({ userId, externalWalletSigner }) => {
			const externalWalletAddress = await externalWalletSigner.getAddress();
			if (!isAddress(externalWalletAddress)) throw new Error("externalWalletAddress is not a valid address");
			onSuccess?.({
				userId,
				type: "eoa",
				address: externalWalletAddress
			});
			setPrimaryWalletInStore(userId, "eoa", externalWalletAddress);
		},
		onError: (error) => {
			onError?.(error);
		},
		invalidateQueries: (params) => {
			return [blockchainKeys.linkedWallets(params.userId), blockchainKeys.primaryWallet(params.userId)];
		}
	});
}

//#endregion
//#region src/base-reads/hooks/use-primary-wallet.ts
/**
* Grab the primary (default) wallet for a root key
*/
function usePrimaryWallet({ rootWallet }) {
	const { getPrimaryWallet: getPrimaryWallet$1, spaceDapp } = useBaseReads();
	const getPrimaryWalletFromStore = primaryWalletStore((state) => state.getPrimaryWallet);
	const setPrimaryWalletInStore = primaryWalletStore((state) => state.setPrimaryWallet);
	return useQuery$1(blockchainKeys.primaryWallet(rootWallet ?? "waitingForUserId"), async () => {
		if (!rootWallet) return;
		const cachedPrimaryWallet = getPrimaryWalletFromStore(rootWallet);
		const remotePrimaryWallet = await getPrimaryWallet$1(rootWallet);
		if (!isAddress(remotePrimaryWallet)) throw new Error("remotePrimaryWallet is not a valid address");
		const isRemoteZeroAddress = remotePrimaryWallet === zeroAddress;
		let resultAddress = void 0;
		let resultType = "smartAccount";
		if (isRemoteZeroAddress) resultType = "smartAccount";
		else if (cachedPrimaryWallet?.address?.toLowerCase() !== remotePrimaryWallet.toLowerCase()) {
			resultAddress = remotePrimaryWallet;
			const code = await spaceDapp.provider.getCode(remotePrimaryWallet);
			resultType = !code || code === "0x" ? "eoa" : "smartAccount";
		} else {
			resultAddress = cachedPrimaryWallet.address;
			resultType = cachedPrimaryWallet.type;
		}
		if (resultAddress) setPrimaryWalletInStore(rootWallet, resultType, resultAddress);
		return {
			type: resultType,
			address: resultAddress
		};
	}, {
		enabled: !!rootWallet && isAddress(rootWallet),
		staleTime: staleTime24Hours
	});
}

//#endregion
//#region src/base-reads/hooks/use-installed-apps.ts
function useInstalledApps(accountAddress) {
	const { getInstalledApps: getInstalledApps$1, appRegistryDapp, publicClient } = useBaseReads();
	return useQuery$1(blockchainKeys.accountInstalledApps(accountAddress ?? ""), () => getInstalledApps$1(accountAddress), {
		enabled: !!accountAddress && !!appRegistryDapp && !!publicClient,
		refetchOnMount: true
	});
}

//#endregion
//#region src/base-reads/swap-activity.ts
const GetSwapsPaginatedDocument = `
query GetSwapsPaginated($recipient: String, $limit: Int = 100) {  
  swaps(where: {recipient: $recipient}, limit: $limit) {  
    items {  
      txHash  
      spaceId  
      recipient  
      tokenIn  
      tokenOut  
      amountIn  
      amountOut  
      poster  
      blockTimestamp
      space {  
        id  
        name  
      }  
      swapRouter {  
        txHash  
        spaceFactory  
        createdAt  
      }  
      swapRouterSwap {  
        txHash  
        router  
        caller  
        tokenIn  
        tokenOut  
        amountIn  
        amountOut  
        recipient  
        createdAt  
      }  
    }  
    pageInfo {  
      endCursor  
      hasNextPage  
    }  
  }  
}
`;
async function getSwapsPaginated(args) {
	const { subgraphEndpoint, recipient, limit = 100 } = args;
	if (!subgraphEndpoint) {
		console.warn("[getSwapsPaginated] subgraphEndpoint is not set");
		return void 0;
	}
	try {
		const data = await request(subgraphEndpoint, GetSwapsPaginatedDocument, {
			recipient,
			limit
		});
		if (!data || !data.swaps) throw new Error("No data returned from subgraph for swaps");
		return {
			items: data.swaps.items,
			pageInfo: data.swaps.pageInfo
		};
	} catch (err) {
		console.error("[getSwapsPaginated] Error fetching swaps:", err);
		return void 0;
	}
}

//#endregion
//#region src/hooks/use-bot-data.ts
const createBotDataQueryOptions = (appAddress, townsEnv, appRegistryDapp, environmentId) => {
	return queryOptions$1({
		queryKey: blockchainKeys.botData(appAddress),
		queryFn: async () => {
			const latestAppId = await appRegistryDapp.getLatestAppId(appAddress);
			const app = await appRegistryDapp.getAppById(latestAppId);
			const [installPriceBN, accessDuration] = await Promise.all([appRegistryDapp.getAppPrice(app.module), appRegistryDapp.getAppDuration(app.module)]);
			const installPrice = installPriceBN.toBigInt();
			const appRegistryRpcClient = makeAppRegistryRpcClient(townsEnv.getAppRegistryUrl(environmentId), "");
			const { metadata } = await appRegistryRpcClient.getAppMetadata({ appId: bin_fromHexString(app.client) });
			const onchainAppData = {
				latestAppId: app.appId,
				appAddress: app.module,
				owner: app.owner,
				clientAddress: app.client,
				permissions: app.permissions.map((p) => fromHex(p, {
					to: "string",
					size: 32
				})),
				manifest: app.manifest,
				duration: app.duration
			};
			return {
				...onchainAppData,
				installPrice,
				accessDuration,
				metadata
			};
		},
		enabled: !!appAddress
	});
};
const useBotData = (appAddress) => {
	const { appRegistryDapp } = useBaseReads();
	const { environmentId, townsEnv } = useTownsContext();
	return useQuery(createBotDataQueryOptions(appAddress, townsEnv, appRegistryDapp, environmentId));
};

//#endregion
//#region src/store/use-user-lookup-store.ts
const dlog$1 = dlogger("csb:store:user-lookup");
const db = new Dexie("userlookup");
db.version(1).stores({ userLookup: "key,spaceUsers,channelUsers,fallbackUserLookups" });
const debouncedSave = debounce(async (name, value) => {
	const spaceUsers = value.state.spaceUsers;
	const channelUsers = value.state.channelUsers;
	const fallbackUserLookups = value.state.fallbackUserLookups;
	const allUsers = value.state.allUsers;
	try {
		await db.table("userLookup").put({
			key: name,
			spaceUsers,
			channelUsers,
			fallbackUserLookups,
			allUsers
		});
	} catch (e) {
		dlog$1.error(`problem saving to persistence`, e);
	}
}, 1e3, { maxWait: 5e3 });
const userLookupStorage = {
	async getItem(name) {
		const item = await db.table("userLookup").get({ key: name });
		return { state: item ?? {
			spaceUsers: {},
			channelUsers: {},
			fallbackUserLookups: {},
			allUsers: {}
		} };
	},
	async setItem(name, value) {
		await debouncedSave(name, value);
	},
	async removeItem(name) {
		dlog$1.info("removeItem", name);
		await db.table("userLookup").delete(name);
	}
};
function setSpaceUser(state, userId, user, spaceId) {
	const { allUsers, spaceUsers, fallbackUserLookups } = state;
	if (!spaceId) return state;
	if (allUsers[userId] && spaceUsers[spaceId]?.[userId] && fallbackUserLookups[userId] && isEqual(allUsers[userId][spaceId], user) && isEqual(spaceUsers[spaceId][userId], user) && isEqual(fallbackUserLookups[userId], user)) return state;
	const newAllUsers = { ...allUsers };
	if (!newAllUsers[userId]) newAllUsers[userId] = {};
	newAllUsers[userId] = {
		...newAllUsers[userId],
		[spaceId]: user
	};
	const newSpaceUsers = { ...spaceUsers };
	if (!newSpaceUsers[spaceId]) newSpaceUsers[spaceId] = {};
	newSpaceUsers[spaceId] = {
		...newSpaceUsers[spaceId],
		[userId]: user
	};
	const newFallbackUserLookups = { ...fallbackUserLookups };
	if (typeof user?.username === "string" && user.username.length > 0 && !newFallbackUserLookups[userId]?.username) newFallbackUserLookups[userId] = user;
	return {
		...state,
		allUsers: newAllUsers,
		spaceUsers: newSpaceUsers,
		fallbackUserLookups: newFallbackUserLookups
	};
}
function setSpaceUsers(state, records) {
	const { allUsers, spaceUsers, fallbackUserLookups } = state;
	const newAllUsers = { ...allUsers };
	const newSpaceUsers = { ...spaceUsers };
	const newFallbackUserLookups = { ...fallbackUserLookups };
	for (const record of records) {
		const { userId, user, spaceId } = record;
		if (!spaceId) continue;
		if (!newAllUsers[userId]) newAllUsers[userId] = {};
		else if (newAllUsers[userId] === allUsers[userId]) newAllUsers[userId] = { ...allUsers[userId] };
		newAllUsers[userId][spaceId] = user;
		if (!newSpaceUsers[spaceId]) newSpaceUsers[spaceId] = {};
		else if (newSpaceUsers[spaceId] === spaceUsers[spaceId]) newSpaceUsers[spaceId] = { ...spaceUsers[spaceId] };
		newSpaceUsers[spaceId][userId] = user;
		if (typeof user?.username === "string" && user.username.length > 0 && !newFallbackUserLookups[userId]?.username) newFallbackUserLookups[userId] = user;
	}
	return {
		...state,
		allUsers: newAllUsers,
		spaceUsers: newSpaceUsers,
		fallbackUserLookups: newFallbackUserLookups
	};
}
function setChannelUser(state, userId, user, channelId) {
	if (!channelId) return state;
	if (state.channelUsers[channelId] && isEqual(state.channelUsers[channelId], user)) return state;
	const newChannelUsers = { ...state.channelUsers };
	if (!newChannelUsers[channelId]) newChannelUsers[channelId] = {};
	newChannelUsers[channelId] = {
		...newChannelUsers[channelId],
		[userId]: { ...user }
	};
	return {
		...state,
		channelUsers: newChannelUsers
	};
}
function setGlobalUser(state, userId, user) {
	if (state.fallbackUserLookups[userId] && isEqual(state.fallbackUserLookups[userId], user)) return state;
	if (typeof user?.username !== "string" || user.username.length === 0) return state;
	const newFallbackUserLookups = { ...state.fallbackUserLookups };
	newFallbackUserLookups[userId] = user;
	return {
		...state,
		fallbackUserLookups: newFallbackUserLookups
	};
}
const useUserLookupStore = createWithEqualityFn()(persist((set, get) => ({
	spaceUsers: {},
	channelUsers: {},
	fallbackUserLookups: {},
	allUsers: {},
	setSpaceUser: (userId, user, spaceId) => {
		set((state) => {
			return setSpaceUser(state, userId, user, spaceId);
		});
	},
	setSpaceUsers: (records) => {
		set((state) => {
			return setSpaceUsers(state, records);
		});
	},
	setChannelUser: (userId, user, channelId) => {
		set((state) => {
			return setChannelUser(state, userId, user, channelId);
		});
	},
	setChannelUsers: (records) => {
		set((state) => {
			for (const record of records) state = setChannelUser(state, record.userId, record.user, record.channelId);
			return state;
		});
	},
	setGlobalUser: (userId, user) => {
		set((state) => {
			return setGlobalUser(state, userId, user);
		});
	},
	lookupUser: (userId, spaceId, channelId) => {
		const state = get();
		return userLookupFn(state, userId, spaceId, channelId);
	},
	updateUserEverywhere: (userId, updater) => {
		set((state) => {
			const newSpaceUsers = { ...state.spaceUsers };
			const newChannelUsers = { ...state.channelUsers };
			const newFallbackUserLookups = { ...state.fallbackUserLookups };
			const newAllUsers = { ...state.allUsers };
			for (const spaceId in newSpaceUsers) if (newSpaceUsers[spaceId][userId]) newSpaceUsers[spaceId] = {
				...newSpaceUsers[spaceId],
				[userId]: updater(newSpaceUsers[spaceId][userId])
			};
			for (const channelId in newChannelUsers) if (newChannelUsers[channelId][userId]) newChannelUsers[channelId] = {
				...newChannelUsers[channelId],
				[userId]: updater(newChannelUsers[channelId][userId])
			};
			if (newFallbackUserLookups[userId]) newFallbackUserLookups[userId] = updater(newFallbackUserLookups[userId]);
			if (newAllUsers[userId]) newAllUsers[userId] = Object.fromEntries(Object.entries(newAllUsers[userId]).map(([streamId, user]) => [streamId, updater(user)]));
			return {
				...state,
				spaceUsers: newSpaceUsers,
				channelUsers: newChannelUsers,
				fallbackUserLookups: newFallbackUserLookups,
				allUsers: newAllUsers
			};
		});
	}
}), {
	name: "user-lookup-store",
	storage: userLookupStorage,
	partialize: (state) => {
		const { allUsers, channelUsers, spaceUsers, fallbackUserLookups } = state;
		return {
			allUsers,
			channelUsers,
			spaceUsers,
			fallbackUserLookups
		};
	},
	onRehydrateStorage: () => {
		return (_state, error) => {
			if (error) dlog$1.error("an error happened during hydration", error);
			else dlog$1.info("rehydration complete");
		};
	}
}), shallow);
const userLookupFn = (state, userId, spaceId, channelId) => {
	if (channelId && state.channelUsers[channelId]?.[userId]) return state.channelUsers[channelId][userId];
	if (spaceId && state.spaceUsers[spaceId] && state.spaceUsers[spaceId]?.[userId]) return state.spaceUsers[spaceId][userId];
	if (state.fallbackUserLookups[userId]) return state.fallbackUserLookups[userId];
	return void 0;
};

//#endregion
//#region src/hooks/use-bot-lookup-updater.tsx
function isBot(appAddress) {
	return appAddress !== void 0 && appAddress !== zeroAddress;
}
function createBotLookup(bot, appId) {
	return {
		userId: bot.clientAddress,
		username: bot.metadata?.username ?? bot.clientAddress,
		usernameConfirmed: true,
		usernameEncrypted: false,
		displayName: bot.metadata?.displayName ?? bot.clientAddress,
		displayNameEncrypted: false,
		isBot: true,
		appId,
		appAddress: bot.appAddress
	};
}
function invalidateInstalledApps(address) {
	return queryClient.invalidateQueries({ queryKey: blockchainKeys.accountInstalledApps(address) });
}
function useBotLookupUpdater(client, abstractAccountAddress) {
	const invalidateBotData = useCallback((streamId, userId) => {
		const stream = client?.streams.get(streamId);
		const member = stream?.view.getMembers()?.joined.get(userId);
		if (!isBot(member?.appAddress)) return;
		if (isSpaceStreamId(streamId)) invalidateInstalledApps(SpaceAddressFromSpaceId(streamId));
		else if (abstractAccountAddress) invalidateInstalledApps(abstractAccountAddress);
	}, [client, abstractAccountAddress]);
	const initializeBotLookup = useCallback((streamId) => {
		const stream = client?.streams.get(streamId);
		const members = stream?.view.getMembers().joined.values() ?? [];
		const hasBot = Array.from(members).some((member) => isBot(member.appAddress));
		if (!hasBot) return;
		if (isSpaceStreamId(streamId)) invalidateInstalledApps(SpaceAddressFromSpaceId(streamId));
		else if (isDMChannelStreamId(streamId) && abstractAccountAddress) invalidateInstalledApps(abstractAccountAddress);
	}, [client, abstractAccountAddress]);
	useEffect(() => {
		if (!client) return;
		client.on("streamInitialized", initializeBotLookup);
		client.on("streamNewUserJoined", invalidateBotData);
		client.on("streamUserLeft", invalidateBotData);
		return () => {
			client.off("streamInitialized", initializeBotLookup);
			client.off("streamNewUserJoined", invalidateBotData);
			client.off("streamUserLeft", invalidateBotData);
		};
	}, [
		client,
		initializeBotLookup,
		invalidateBotData
	]);
}
function InstalledAppsUpdater(props) {
	const { casablancaClient } = useTownsContext();
	const abstractAccountAddress = props.context === "dm" ? props.abstractAccountAddress : void 0;
	useBotLookupUpdater(casablancaClient, abstractAccountAddress);
	if (props.context === "space") return /* @__PURE__ */ jsx(SpaceBotUpdaters, { spaceId: props.spaceId });
	return /* @__PURE__ */ jsx(DmChannelBotDetector, { channelId: props.channelId });
}
function SpaceBotUpdaters({ spaceId }) {
	const spaceAddress = spaceId ? SpaceAddressFromSpaceId(spaceId) : void 0;
	const { data: installedApps } = useInstalledApps(spaceAddress);
	const apps = Array.from(installedApps?.values() ?? []);
	return /* @__PURE__ */ jsx(Fragment, { children: apps.map(({ appAddress, appId }) => /* @__PURE__ */ jsx(BotDataLookupSync, {
		appAddress,
		appId
	}, appId)) });
}
function DmChannelBotDetector({ channelId }) {
	const { casablancaClient } = useTownsContext();
	const [botAppAddress, setBotAppAddress] = useState(null);
	const findBotInChannel = useCallback(() => {
		if (!channelId || !casablancaClient) return null;
		const stream = casablancaClient.streams.get(channelId);
		const joined = stream?.view.getMembers()?.joined;
		if (!joined) return null;
		for (const member of joined.values()) if (isBot(member?.appAddress)) return member.appAddress;
		return null;
	}, [channelId, casablancaClient]);
	useEffect(() => {
		if (!casablancaClient || !channelId) return;
		const updateBotAppAddress = (streamId) => {
			if (streamId !== channelId) return;
			const appAddress$1 = findBotInChannel();
			if (appAddress$1) setBotAppAddress(appAddress$1);
		};
		const appAddress = findBotInChannel();
		if (appAddress) setBotAppAddress(appAddress);
		casablancaClient.on("streamInitialized", updateBotAppAddress);
		casablancaClient.on("streamNewUserJoined", updateBotAppAddress);
		return () => {
			casablancaClient.off("streamInitialized", updateBotAppAddress);
			casablancaClient.off("streamNewUserJoined", updateBotAppAddress);
		};
	}, [
		casablancaClient,
		channelId,
		findBotInChannel
	]);
	if (!botAppAddress) return null;
	return /* @__PURE__ */ jsx(BotDataLookupSync, {
		appAddress: botAppAddress,
		appId: ""
	});
}
function BotDataLookupSync({ appAddress, appId }) {
	const { data } = useBotData(appAddress);
	useEffect(() => {
		if (!data) return;
		const lookup = createBotLookup(data, appId);
		useUserLookupStore.getState().setGlobalUser(data.clientAddress, lookup);
	}, [data, appId]);
	return null;
}

//#endregion
//#region src/components/SpaceContextProvider.tsx
const SpaceContext = createContext(void 0);
/**
* use instead of React.useContext
*/
function useSpaceContext() {
	const spaceContext = useContext(SpaceContext);
	return useMemo(() => spaceContext ?? {}, [spaceContext]);
}
function SpaceContextProvider(props) {
	const { spaceId, children, shouldFetchBots } = props;
	return /* @__PURE__ */ jsx(SpaceContext.Provider, {
		value: { spaceId },
		children: /* @__PURE__ */ jsxs(UserLookupContext.Provider, {
			value: { spaceId },
			children: [children, shouldFetchBots && /* @__PURE__ */ jsx(InstalledAppsUpdater, {
				context: "space",
				spaceId
			})]
		})
	});
}

//#endregion
//#region src/hooks/use-space-data.ts
const SPACE_DATA_STORE_NAME = "towns/spaceDataStore";
const EMPTY_SPACE_INFOS = [];
async function getChannelsMetadata(spaceDapp, spaceId, channels) {
	let metadata = [];
	for (const { channelId, updatedAtKey } of channels) {
		const cached = useOfflineStore.getState().offlineChannelMetadataMap[channelId];
		if (cached && (!updatedAtKey || cached.updatedAtKey === updatedAtKey)) metadata.push(cached);
		else break;
	}
	if (metadata.length === channels.length) return metadata;
	metadata = [];
	const channelMetadata = await spaceDapp.getChannels(spaceId);
	const updatedAtKeys = channels.reduce((acc, c) => {
		acc[c.channelId] = c.updatedAtKey ?? "0";
		return acc;
	}, {});
	for (const channel of channelMetadata) {
		const offlineChannelMetadata = {
			channel,
			updatedAtKey: updatedAtKeys[channel.channelNetworkId] ?? "0"
		};
		useOfflineStore.getState().setOfflineChannelInfo(offlineChannelMetadata);
		metadata.push(offlineChannelMetadata);
	}
	return metadata;
}
async function getSpaceInfoWithStore(getSpaceInfo$1, spaceId) {
	const cachedSpaceInfo = useOfflineStore.getState().offlineSpaceInfoMap[spaceId];
	if (cachedSpaceInfo) return cachedSpaceInfo;
	const spaceInfo = await getSpaceInfo$1(spaceId);
	if (spaceInfo) useOfflineStore.getState().setOfflineSpaceInfo(spaceInfo);
	return spaceInfo;
}
const useSpaceDataStore = create()(persist((set) => ({
	spaceDataMap: void 0,
	setSpaceData: (spaceData) => set((state) => {
		if (!state.spaceDataMap) return { spaceDataMap: { [spaceData.id]: spaceData } };
		if (isEqual(state.spaceDataMap[spaceData.id], spaceData)) return state;
		return { spaceDataMap: {
			...state.spaceDataMap,
			[spaceData.id]: spaceData
		} };
	})
}), {
	name: SPACE_DATA_STORE_NAME,
	storage: createJSONStorage(() => localStorage),
	version: 2
}));
function useSpaceData() {
	const { spaceId: contextSpaceId } = useSpaceContext();
	return useSpaceDataWithId(contextSpaceId, "useSpaceData");
}
function useSpaceDataWithId(inSpaceId, _fromTag = void 0) {
	const spaceData = useSpaceDataStore((state) => inSpaceId ? state.spaceDataMap?.[inSpaceId] : void 0);
	return spaceData;
}
function spaceInfoWithChannelsQueryConfig({ spaceId, initialData, enabled = true, getSpaceInfo: getSpaceInfo$1 }) {
	return {
		queryKey: blockchainKeys.spaceInfo(spaceId ?? ""),
		queryFn: async (client, spaceId$1) => {
			if (!spaceId$1 || spaceId$1.length === 0 || !client || !getSpaceInfo$1) return void 0;
			const spaceInfo = await getSpaceInfo$1(spaceId$1);
			if (!spaceInfo) return useOfflineStore.getState().offlineSpaceInfoMap[spaceId$1];
			useOfflineStore.getState().setOfflineSpaceInfo(spaceInfo);
			return spaceInfo;
		},
		options: {
			enabled: enabled && !!spaceId && !!getSpaceInfo$1,
			initialData,
			refetchOnMount: false,
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
			staleTime: defaultStaleTime
		}
	};
}
function invalidateSpaceInfoQuery(spaceId) {
	return queryClient.invalidateQueries({ queryKey: blockchainKeys.spaceInfo(spaceId) });
}
function useContractSpaceInfos(opts, spaceIds, client) {
	const { spaceDapp } = useBaseReads();
	const { getSpaceInfo: getSpaceInfo$1 } = useBaseReads();
	const offlineSpaceInfoMap = useOfflineStore((s) => s.offlineSpaceInfoMap);
	const isEnabled = spaceDapp && client && client.streams.size() > 0;
	const queryData = useQueries$1({
		queries: spaceIds.map((id) => {
			const queryConfig = spaceInfoWithChannelsQueryConfig({
				spaceId: id,
				initialData: offlineSpaceInfoMap[id],
				enabled: isEnabled && !!id,
				getSpaceInfo: getSpaceInfo$1
			});
			return {
				queryKey: queryConfig.queryKey,
				queryFn: () => {
					return queryConfig.queryFn(client, id);
				},
				...queryConfig.options,
				refetchOnMount: true
			};
		}),
		combine: (results) => {
			return {
				data: results.map((r) => r.data).filter(isDefined),
				isLoading: results.some((r) => r.isLoading)
			};
		}
	});
	const spaceInfos = queryData.data.length > 0 ? queryData.data : EMPTY_SPACE_INFOS;
	return useMemo(() => {
		return {
			data: spaceInfos,
			isLoading: queryData.isLoading
		};
	}, [spaceInfos, queryData.isLoading]);
}
const useContractSpaceInfo = (spaceId) => {
	const offlineSpaceInfoMap = useOfflineStore((s) => s.offlineSpaceInfoMap);
	const { getSpaceInfo: getSpaceInfo$1 } = useBaseReads();
	const { casablancaClient: client } = useTownsContext();
	const queryConfig = spaceInfoWithChannelsQueryConfig({
		spaceId: spaceId ?? "",
		initialData: spaceId ? offlineSpaceInfoMap[spaceId] : void 0,
		enabled: !!spaceId && !!getSpaceInfo$1 && !!client,
		getSpaceInfo: getSpaceInfo$1
	});
	const { data: spaceInfo, isLoading, error } = useQuery$1(queryConfig.queryKey, () => {
		const res = queryConfig.queryFn(client, spaceId);
		return res;
	}, { ...queryConfig.options });
	return useMemo(() => {
		return {
			data: spaceInfo,
			isLoading,
			error
		};
	}, [
		spaceInfo,
		isLoading,
		error
	]);
};
/**
* a hook to grab space data without any of the channels or side effects that require client
* b/c it should use the same query key and return the same data in the query fn, it's typed to match useContractSpaceInfos
* @param spaceId
* @returns
*/
function useContractSpaceInfoWithoutClient(spaceId, _enabled = true) {
	const offlineSpaceInfoMap = useOfflineStore((s) => s.offlineSpaceInfoMap);
	const { getSpaceInfo: getSpaceInfo$1 } = useBaseReads();
	const queryFn$1 = useCallback(async () => {
		if (!spaceId || spaceId.length === 0) return void 0;
		const spaceInfo = await getSpaceInfo$1(spaceId);
		if (!spaceInfo) return useOfflineStore.getState().offlineSpaceInfoMap[spaceId];
		return spaceInfo;
	}, [getSpaceInfo$1, spaceId]);
	return useQuery$1(blockchainKeys.spaceInfo(spaceId ?? ""), queryFn$1, {
		initialData: spaceId ? offlineSpaceInfoMap[spaceId] : void 0,
		enabled: !!spaceId && _enabled,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		staleTime: defaultStaleTime
	});
}
function getSpaceInfoQueryData(spaceId) {
	return queryClient.getQueryData(blockchainKeys.spaceInfo(spaceId));
}
function useSpaceRollups(casablancaClient) {
	const { spaceDapp } = useBaseReads();
	const { getSpaceInfo: getSpaceInfo$1 } = useBaseReads();
	const { setSpaceData } = useSpaceDataStore();
	const debounceRef = useRef({});
	useEffect(() => {
		if (!casablancaClient || !spaceDapp) return;
		const update = (spaceId) => {
			const userStreamId = casablancaClient.userStreamId;
			if (!userStreamId) return;
			const userStream = casablancaClient.streams.get(userStreamId);
			if (!userStream || !userStream.view.isInitialized) return;
			const stream = casablancaClient.streams.get(spaceId);
			if (!stream || !stream.view.isInitialized) return;
			(async () => {
				const spaceChannels = mapSpaceChannels(stream);
				const channelMetadata = await getChannelsMetadata(spaceDapp, spaceId, spaceChannels);
				const spaceInfo = await getSpaceInfoWithStore(getSpaceInfo$1, spaceId);
				const spaceName = spaceInfo?.name ?? "";
				const membership = toMembership(userStream.view.userContent.getMembership(stream.streamId)?.op);
				const newSpace = rollupSpace(stream, membership, spaceChannels, channelMetadata.reduce((acc, c) => {
					acc[c.channel.channelNetworkId] = c;
					return acc;
				}, {}), spaceName);
				if (newSpace) setSpaceData(newSpace);
			})();
		};
		const onStreamUpdated = (streamId) => {
			if (isSpaceStreamId(streamId)) {
				if (!debounceRef.current[streamId]) debounceRef.current[streamId] = debounce(() => update(streamId), 3e3, { maxWait: 3e3 });
				debounceRef.current[streamId]();
			}
		};
		casablancaClient.streams.getStreamIds().forEach(onStreamUpdated);
		casablancaClient.on("streamInitialized", onStreamUpdated);
		casablancaClient.on("spaceChannelCreated", onStreamUpdated);
		casablancaClient.on("spaceChannelUpdated", onStreamUpdated);
		casablancaClient.on("spaceChannelAutojoinUpdated", onStreamUpdated);
		casablancaClient.on("spaceChannelHideUserJoinLeaveEventsUpdated", onStreamUpdated);
		casablancaClient.on("userStreamMembershipChanged", onStreamUpdated);
		return () => {
			casablancaClient.off("streamInitialized", onStreamUpdated);
			casablancaClient.off("spaceChannelCreated", onStreamUpdated);
			casablancaClient.off("spaceChannelUpdated", onStreamUpdated);
			casablancaClient.off("spaceChannelAutojoinUpdated", onStreamUpdated);
			casablancaClient.off("spaceChannelHideUserJoinLeaveEventsUpdated", onStreamUpdated);
			casablancaClient.off("userStreamMembershipChanged", onStreamUpdated);
		};
	}, [
		casablancaClient,
		setSpaceData,
		spaceDapp,
		getSpaceInfo$1
	]);
}
function rollupSpace(stream, membership, spaceChannels, channelMetadata, spaceName) {
	if (stream.view.contentKind !== "spaceContent") throw new Error("stream is not a space");
	const streamChannelMetadata = stream.view.spaceContent.spaceChannelsMetadata;
	return {
		id: stream.view.streamId,
		name: spaceName ?? "",
		channelGroups: [{
			label: "Channels",
			channels: spaceChannels.sort((a, b) => a.channelId.localeCompare(b.channelId)).map((c) => ({
				id: c.channelId,
				label: channelMetadata[c.channelId]?.channel.name ?? c.channelId,
				private: false,
				topic: channelMetadata[c.channelId]?.channel.description ?? "",
				disabled: channelMetadata[c.channelId]?.channel.disabled ?? false,
				isAutojoin: streamChannelMetadata[c.channelId]?.isAutojoin ?? false,
				hideUserJoinLeaveEvents: streamChannelMetadata[c.channelId]?.hideUserJoinLeaveEvents ?? false,
				isDefault: streamChannelMetadata[c.channelId]?.isDefault ?? false
			}))
		}],
		membership,
		isLoadingChannels: false,
		hasLoadedMemberships: stream.view.isInitialized
	};
}
const mapSpaceChannels = (stream) => {
	const channelIds = stream ? Object.keys(stream.view.spaceContent.spaceChannelsMetadata) : [];
	return channelIds.map((id) => {
		return {
			channelId: id,
			updatedAtKey: stream?.view.spaceContent.spaceChannelsMetadata[id]?.updatedAtEventNum.toString() ?? "0"
		};
	});
};

//#endregion
//#region src/hooks/use-observable.ts
const isDev = true;
function useObservable(observable) {
	const subscribeFn = useCallback((subFn) => {
		return observable.subscribe(subFn);
	}, [observable]);
	const getSnapshotFn = useCallback(() => {
		return observable.value;
	}, [observable]);
	return useSyncExternalStore(subscribeFn, getSnapshotFn);
}
const DEV_MAX_COUNT = 3;
const DEV_MAX_TIME = 5e3;
function useObservableWith(observable, selector) {
	const monitorRef = useRef({
		selector,
		count: 0,
		bucketAt: performance.now()
	});
	const newObservable = useMemo(() => {
		const monitor = monitorRef.current;
		if (isDev && monitor.selector !== selector && monitor.count < DEV_MAX_COUNT) if (performance.now() - monitor.bucketAt > DEV_MAX_TIME) {
			monitor.selector = selector;
			monitor.count = 0;
			monitor.bucketAt = performance.now();
		} else {
			monitor.count++;
			if (monitor.count === DEV_MAX_COUNT) console.error("Selector must be memoized with useCallback. Use: const selector = useCallback((state) => ..., [deps])");
		}
		return observable.map(selector);
	}, [observable, selector]);
	return useObservable(newObservable);
}

//#endregion
//#region src/hooks/use-space-ids.ts
const DEFAULT_SPACE_IDS = new Observable([]);
function useSpacesIdsFrom(casablancaClient) {
	const mySpaceIds = casablancaClient?.streamsView.my.spaceIds ?? DEFAULT_SPACE_IDS;
	return useObservable(mySpaceIds);
}
function useSpacesIds() {
	const { casablancaClient } = useTownsContext();
	const spaceIds = useSpacesIdsFrom(casablancaClient);
	return { spaceIds };
}

//#endregion
//#region src/hooks/CasablancClient/useCasablancaSpaces.ts
function useCasablancaSpaces(opts, casablancaClient) {
	const spaceIds = useSpacesIdsFrom(casablancaClient);
	const { data: spaceInfos } = useContractSpaceInfos(opts, spaceIds, casablancaClient);
	const { offlineSpaceInfoMap: spaceNamesInLocalStore } = useOfflineStore();
	return useMemo(() => {
		const streams = spaceIds.map((streamId) => {
			const spaceInfo = spaceInfos?.find((i) => i.networkId == streamId);
			const spaceName = spaceInfo?.name ?? spaceNamesInLocalStore[streamId]?.name ?? "";
			let address = contractAddressFromSpaceId(streamId);
			try {
				address = getAddress(address);
			} catch (e) {
				console.error(`error getting address for space ${streamId}`, e);
			}
			return {
				id: streamId,
				streamId,
				owner: spaceInfo?.owner ?? "",
				address,
				name: spaceName
			};
		});
		return streams;
	}, [
		spaceIds,
		spaceInfos,
		spaceNamesInLocalStore
	]);
}

//#endregion
//#region src/hooks/TownsContext/useSpaces.ts
function useSpaces(opts, casablancaClient) {
	const casablancaSpaces = useCasablancaSpaces(opts, casablancaClient);
	return { spaces: casablancaSpaces };
}

//#endregion
//#region src/utils/towns-utils.ts
function sleep(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}
async function sleepUntil(thisObject, condition, timeoutMs = 2e3, checkEveryMs = 100) {
	let currentMs = 0;
	while (currentMs <= timeoutMs) {
		const result = condition(thisObject);
		if (result) return result;
		await sleep(checkEveryMs);
		currentMs += checkEveryMs;
	}
	return void 0;
}
function invariant(cond, message) {
	if (!cond) throw new Error(message);
}
function staticAssertNever(x) {
	throw new Error("Unexpected object: ", x);
}
function getChainName(chainId) {
	const chain = Object.values(allChains).find((x) => x.id === chainId);
	if (chain) return chain.name;
	throw new Error(`ChainId ${chainId} not found`);
}
async function retryOperation(operation, options = {}) {
	const maxRetries = options.maxRetries ?? 3;
	const getRetryDelay$1 = options.getRetryDelay ?? ((retryCount$1) => {
		return Math.min(1e3 * 2 ** retryCount$1, 2e4);
	});
	let retryCount = 0;
	while (true) try {
		return await operation();
	} catch (error) {
		retryCount++;
		options.onError?.(error, retryCount);
		if (retryCount > maxRetries) throw error;
		const retryDelay = getRetryDelay$1(retryCount);
		options.onRetry?.(retryCount);
		await new Promise((resolve) => setTimeout(resolve, retryDelay));
	}
}

//#endregion
//#region src/client/casablanca/CasablancaUtils.ts
function toStreamView(stream, membership) {
	const metadata = stream.view.getMemberMetadata();
	const members = Array.from(stream.view.getMembers().joined.values()).map((streamMember) => {
		const info = metadata.userInfo(streamMember.userId);
		return {
			userId: streamMember.userId,
			membership: Membership.Join,
			username: info?.username ?? "",
			usernameConfirmed: info?.usernameConfirmed ?? false,
			usernameEncrypted: info?.usernameEncrypted ?? false,
			displayName: info?.displayName ?? "",
			displayNameEncrypted: info?.displayNameEncrypted ?? false,
			disambiguate: false
		};
	});
	return {
		id: stream.streamId,
		membership,
		members
	};
}

//#endregion
//#region src/client/LocalStorageNotificationStore.ts
var LocalStorageNotificationStore = class {
	keyPrefix;
	constructor(userId, environmentId) {
		this.keyPrefix = `RNS-${environmentId}-${userId}`;
	}
	getItem(schema, key) {
		const data = localStorage.getItem(`${this.keyPrefix}-${key}`);
		if (!data) return void 0;
		try {
			return fromBinary(schema, bin_fromBase64(data));
		} catch (error) {
			console.error("TNS PUSH: error parsing local storage", error);
			return void 0;
		}
	}
	setItem(schema, key, value) {
		const bytes = toBinary(schema, value);
		localStorage.setItem(`${this.keyPrefix}-${key}`, bin_toBase64(bytes));
	}
};

//#endregion
//#region src/client/TownsClient.ts
var TownsClient = class extends EventEmitter {
	opts;
	baseReads;
	name;
	spaceDapp;
	casablancaClient;
	_signerContext;
	_eventHandlers;
	constructor(opts, { baseReads }, name) {
		super();
		this.opts = opts;
		this.baseReads = baseReads;
		this.name = name || Math.random().toString(36).substring(7);
		console.log("~~~ new TownsClient ~~~", this.name, this.opts);
		this.spaceDapp = baseReads.spaceDapp;
		this._eventHandlers = opts.eventHandlers;
	}
	get signerContext() {
		return this._signerContext;
	}
	/************************************************
	* getServerVersions
	*************************************************/
	async getServerVersions() {
		return {
			versions: [],
			unstable_features: {},
			release_version: "0.0.0"
		};
	}
	/************************************************
	* logout
	*************************************************/
	async logout() {
		this.log("logout");
		await this.logoutFromCasablanca();
	}
	/************************************************
	* logoutFromCasablanca
	*************************************************/
	async logoutFromCasablanca() {
		if (!this._signerContext) return;
		this.log("logoutFromCasablanca");
		await this.stopCasablancaClient();
		this._eventHandlers?.onLogout?.({ userId: this._signerContext?.creatorAddress.toString() ?? "unset" });
		this._signerContext = void 0;
	}
	async makeUnauthenticatedClient() {
		if (!this.opts.riverProvider) throw new Error("riverChainProvider is required");
		const rpcClient = await makeRiverRpcClient(this.opts.riverProvider, this.opts.riverConfig);
		return new UnauthenticatedClient(rpcClient);
	}
	/************************************************
	* localStartCasablancaClientWithRetries
	* for use in joining or creating a space for a new user, or creating a DM with a bot
	* a few things can happen:
	* - the network is down
	* - the isEntitled call can race during space creation and fail even though the space was created (alchemy isn't up to date on the node)
	*************************************************/
	async localStartCasablancaClientWithRetries(context, metadata, sequenceName) {
		const maxRetries = 3;
		let retryCount = 0;
		const getRetryDelay$1 = (retryCount$1) => {
			return Math.min(1e3 * 2 ** retryCount$1, 2e4);
		};
		while (true) try {
			return await this.startCasablancaClient(context, metadata, sequenceName);
		} catch (e) {
			retryCount++;
			console.error("[localStartCasablancaClientWithRetries] error starting casablanca client", {
				error: e,
				retryCount
			});
			await this.logoutFromCasablanca();
			if (retryCount > maxRetries) throw e;
			const retryDelay = getRetryDelay$1(retryCount);
			console.log("[localStartCasablancaClientWithRetries] retrying", {
				retryDelay,
				retryCount
			});
			await new Promise((resolve) => setTimeout(resolve, retryDelay));
		}
	}
	/************************************************
	* startCasablancaClient
	*************************************************/
	async startCasablancaClient(context, metadata, sequenceName) {
		this.log("startCasablancaClient", context);
		if (this.casablancaClient) throw new Error("already started casablancaClient");
		if (!this.opts.riverProvider) throw new Error("riverChainProvider is required");
		this._signerContext = context;
		let rpcClient;
		let endMakeRiverRpc;
		if (sequenceName) endMakeRiverRpc = getTimeTracker().startMeasurement(sequenceName, "river_make_rpc_client");
		if (localStorage.getItem("RIVER_RPC_URL")) rpcClient = makeStreamRpcClient(localStorage.getItem("RIVER_RPC_URL"));
		else rpcClient = await makeRiverRpcClient(this.opts.riverProvider, this.opts.riverConfig);
		if (endMakeRiverRpc) endMakeRiverRpc();
		const userId = userIdFromAddress(context.creatorAddress);
		const cryptoDbName = this.cryptoDbName(context);
		const persistenceDbName = this.persistenceDbName(context);
		const cryptoStore = RiverDbManager.getCryptoDb(userId, cryptoDbName);
		this.casablancaClient = new Client(context, rpcClient, cryptoStore, this, {
			persistenceStoreName: persistenceDbName,
			logNamespaceFilter: this.opts.logNamespaceFilter,
			highPriorityStreamIds: this.opts.highPriorityStreamIds,
			unpackEnvelopeOpts: this.opts.unpackEnvelopeOpts,
			decryptionExtensionsOpts: { enableEphemeralKeySolicitations: this.opts.enableEphemeralKeySolicitations ?? false },
			notifications: this.opts.riverNotificationServiceUrl ? {
				url: this.opts.riverNotificationServiceUrl,
				store: new LocalStorageNotificationStore(userId, this.opts.environmentId)
			} : void 0,
			syncMode: this.opts.syncMode
		});
		this.casablancaClient.setMaxListeners(100);
		let endIntializeUser;
		if (sequenceName) endIntializeUser = getTimeTracker().startMeasurement(sequenceName, "river_initialize_user");
		const initTimes = await this.casablancaClient.initializeUser(metadata);
		if (sequenceName) Object.entries(initTimes).forEach(([key, ms]) => {
			const snakeCaseKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
			getTimeTracker().addMeasurement(sequenceName, `river_${snakeCaseKey}`, ms);
		});
		if (endIntializeUser) endIntializeUser();
		this._eventHandlers?.onRegister?.({ userId: this.casablancaClient.userId });
		this.casablancaClient.startSync();
		this.emit("onCasablancaClientCreated", this.casablancaClient);
		return this.casablancaClient;
	}
	persistenceDbName(signerContext) {
		const userId = userIdFromAddress(signerContext.creatorAddress);
		const envSuffix = `-${this.opts.environmentId}`;
		const persistenceDbName = `persistence-${userId}${envSuffix}`;
		return persistenceDbName;
	}
	cryptoDbName(signerContext) {
		const userId = userIdFromAddress(signerContext.creatorAddress);
		const envSuffix = `-${this.opts.environmentId}`;
		const cryptoDbName = `database-${userId}${envSuffix}`;
		return cryptoDbName;
	}
	/************************************************
	* stopCasablancaClient
	*************************************************/
	async stopCasablancaClient() {
		if (this.casablancaClient) {
			this.log("Stopped casablanca client");
			await this.casablancaClient.stop();
			this.casablancaClient = void 0;
		}
	}
	/**
	* Create Towns protocol space (after the space has been created on Base)
	* @param context - The context of the space creation transaction
	* @param signerContext - The signer context of the space creation transaction
	* @param defaultUsernames - The default usernames of the space
	* @param onCreateFlowStatus - The status of the space creation flow
	* @returns The context of the space creation transaction
	*/
	async createTownsSpace(context, signerContext, defaultUsernames = [], onCreateFlowStatus) {
		if (!context) throw new Error("Context not found");
		const txContext = context;
		if (txContext.status === TransactionStatus.Success && txContext.data) {
			this.log("[createTownsSpace] space created on chain", txContext.data);
			try {
				onCreateFlowStatus?.(CreateSpaceFlowStatus.CreatingSpace);
				const spaceAddress = this.spaceDapp.getSpaceAddress(txContext.receipt, txContext.data.senderAddress);
				if (!spaceAddress) throw new Error("Space address not found");
				const spaceId = makeSpaceStreamId(spaceAddress);
				const channelId = makeDefaultChannelStreamId(spaceAddress);
				txContext.data.spaceId = spaceId;
				txContext.data.channelId = channelId;
				const timeTracker = getTimeTracker();
				if (!this.casablancaClient && signerContext) await this.localStartCasablancaClientWithRetries(signerContext, { spaceId }, TimeTrackerEvents.CREATE_SPACE);
				if (!this.casablancaClient) throw new Error("casablancaClient not started");
				const endCsbCreateSpace = timeTracker.startMeasurement(TimeTrackerEvents.CREATE_SPACE, "river_create_space");
				const result = await this.casablancaClient.createSpace(spaceId);
				endCsbCreateSpace?.();
				const endCsbWaitForStream = timeTracker.startMeasurement(TimeTrackerEvents.CREATE_SPACE, "river_wait_for_stream");
				onCreateFlowStatus?.(CreateSpaceFlowStatus.CreatingChannel);
				await this.casablancaClient.waitForStream(spaceId);
				this.log("[createTownsSpace] Space stream created", {
					result,
					spaceId
				});
				endCsbWaitForStream?.();
				if (defaultUsernames.length > 0) {
					const endCsbSetUsername = timeTracker.startMeasurement(TimeTrackerEvents.CREATE_SPACE, "river_set_username");
					onCreateFlowStatus?.(CreateSpaceFlowStatus.CreatingUser);
					await this.casablancaClient.setUsername(spaceId, defaultUsernames[0]);
					endCsbSetUsername?.();
					this.log("[createTownsSpace] Set default username", {
						defaultUsername: defaultUsernames[0],
						spaceId
					});
				}
				const endCsbCreateDefaultChannel = timeTracker.startMeasurement(TimeTrackerEvents.CREATE_SPACE, "river_create_default_channel");
				await this.createSpaceDefaultChannelRoom(spaceId, "general", channelId);
				endCsbCreateDefaultChannel?.();
				this.log("[createTownsSpace] default channel stream created", channelId);
				this._eventHandlers?.onCreateSpace?.(spaceId);
			} catch (error) {
				addCategoryToError(error, "river");
				throw error;
			}
		}
		return txContext;
	}
	/************************************************
	* createChannelWithId
	* Should only be used in cases where the channel is already created on-chain
	*************************************************/
	async createChannelWithId(createInfo, networkId) {
		if (!this.casablancaClient) throw new Error("createChannel: Casablanca client doesn't exist");
		if (networkId === void 0) throw new Error("createChannel: networkId is undefined");
		const { streamId } = await this.casablancaClient.createChannel(createInfo.parentSpaceId, createInfo.name, createInfo.topic ? createInfo.topic : "", networkId, createInfo.streamSettings, createInfo.channelSettings);
		await this.casablancaClient.waitForStream(streamId);
		return streamId;
	}
	async createSpaceDefaultChannelRoom(parentSpaceId, channelName, channelId) {
		const channelInfo = {
			name: channelName ?? "general",
			parentSpaceId,
			roles: []
		};
		return await this.createChannelWithId(channelInfo, channelId);
	}
	async createTownsChannel(createChannelInfo, context) {
		if (!context) throw new Error("Context not found");
		const txnContext = context;
		let riverError = void 0;
		if (txnContext.status === TransactionStatus.Success) {
			if (txnContext?.data) {
				const roomId = txnContext.data.channelStreamId;
				try {
					await this.createChannelWithId(createChannelInfo, roomId);
				} catch (error) {
					console.error("[waitForCreateChannelTransaction] river error", error);
					riverError = error;
				}
				this.log("[waitForCreateChannelTransaction] Channel stream created", roomId);
			}
		}
		if (riverError) {
			txnContext.error = riverError;
			txnContext.status = TransactionStatus.Failed;
		}
		logTxnResult("waitForCreateChannelTransaction", txnContext);
		return txnContext;
	}
	async updateChannelRoom(updateChannelInfo) {
		if (!this.casablancaClient) throw new Error("updatedChannel: Casablanca client doesn't exist");
		if (updateChannelInfo.channelId === void 0) throw new Error("updateChannel: channelId is undefined");
		await this.casablancaClient.updateChannel(updateChannelInfo.parentSpaceId, updateChannelInfo.channelId, "", "");
	}
	/************************************************
	* DMs
	*************************************************/
	async createDMChannel(userId, appAddress) {
		const client = this.casablancaClient;
		if (!client) throw new Error("No casablanca client");
		const { streamId } = await client.createDMChannel(userId, appAddress);
		return streamId;
	}
	/**
	* Create a DM channel with a bot, initializing the user if needed.
	* This is similar to createTownsSpace/joinTown in that it will start the
	* casablanca client if it hasn't been started yet.
	*/
	async createDMWithBot(userId, appAddress, signerContext) {
		if (!this.casablancaClient && signerContext) await this.localStartCasablancaClientWithRetries(signerContext, { installedAppAddress: appAddress }, TimeTrackerEvents.CREATE_DM_WITH_BOT);
		if (!this.casablancaClient) throw new Error("casablancaClient not started");
		const { streamId } = await this.casablancaClient.createDMChannel(userId, appAddress);
		return streamId;
	}
	/************************************************
	* GDMs
	*************************************************/
	async createGDMChannel(userIds) {
		const client = this.casablancaClient;
		if (!client) throw new Error("No casablanca client");
		const { streamId } = await client.createGDMChannel(userIds);
		return streamId;
	}
	/************************************************
	* Media
	*************************************************/
	async createMediaStream(channelId, spaceId, userId, chunkCount, firstChunk, firstChunkIv, perChunkEncryption) {
		if (!this.casablancaClient) throw new Error("Casablanca client doesn't exist");
		return await this.casablancaClient.createMediaStream(channelId, spaceId, userId, chunkCount, firstChunk, firstChunkIv, void 0, perChunkEncryption);
	}
	/************************************************
	* isEntitled
	*************************************************/
	async isEntitled(spaceId, channelId, user, permission, { invalidateCache } = {}) {
		return this.baseReads.isEntitled({
			spaceId,
			channelId,
			user,
			permission,
			invalidateCache
		});
	}
	/************************************************
	* getSpaceInfoBySpaceId
	*************************************************/
	async getSpaceInfoBySpaceId(spaceNetworkId) {
		return this.baseReads.getSpaceInfo(spaceNetworkId);
	}
	/************************************************
	* inviteUser
	* if it's GDM, invite and auto join the user
	*************************************************/
	async inviteUser(roomId, userId) {
		if (!this.casablancaClient) throw new Error("Casablanca client not initialized");
		if (isGDMChannelStreamId(roomId)) await this.casablancaClient.joinUser(roomId, userId);
		else await this.casablancaClient.inviteUser(roomId, userId);
	}
	async joinUser(roomId, userId) {
		if (!this.casablancaClient) throw new Error("Casablanca client not initialized");
		return this.casablancaClient.joinUser(roomId, userId);
	}
	/************************************************
	* leave
	* ************************************************/
	async leave(roomId, _parentNetworkId) {
		if (!this.casablancaClient) throw new Error("Casablanca client not initialized");
		await this.casablancaClient.leaveStream(roomId);
		const userId = this._signerContext?.creatorAddress.toString();
		if (userId) {
			const { bustMembershipStatus: bustMembershipStatus$1 } = await import("./use-membership-status-CmaQflZP.js");
			await bustMembershipStatus$1({
				userId,
				spaceId: roomId
			});
			const { useSpaceDataStore: useSpaceDataStore$1 } = await import("./use-space-data-C8lrPqtY.js");
			const spaceDataStore = useSpaceDataStore$1.getState();
			if (spaceDataStore.spaceDataMap && spaceDataStore.spaceDataMap[roomId]) {
				const { spaceDataMap } = spaceDataStore;
				const updatedMap = { ...spaceDataMap };
				delete updatedMap[roomId];
				useSpaceDataStore$1.setState({ spaceDataMap: updatedMap });
			}
		}
	}
	/************************************************
	* joinRoom
	* - this function can handle joining both spaces and channels BUT should be used for channels only
	* - for spaces, use joinTown
	* @todo deprecate this in favor of separate joinTown and joinChannel functions
	*************************************************/
	async joinRoom(roomId, _parentNetworkId, opts) {
		if (!this.casablancaClient) throw new Error("Casablanca client not initialized");
		const stream = await this.casablancaClient.joinStream(roomId, opts);
		let parentId = roomId;
		if (stream.view.contentKind === "channelContent" && stream.view.channelContent.spaceId) parentId = stream.view.channelContent.spaceId;
		this._eventHandlers?.onJoinRoom?.(roomId, parentId);
		return toStreamView(stream, this.getMembership(roomId));
	}
	async removeUser(streamId, userId) {
		if (!this.casablancaClient) throw new Error("Casablanca client not initialized");
		await this.casablancaClient.removeUser(streamId, userId);
	}
	async joinRiverSpaceAndDefaultChannels(spaceId, signerContext, onJoinFlowStatus, defaultUsername) {
		if (!this.casablancaClient && signerContext) await this.localStartCasablancaClientWithRetries(signerContext, { spaceId }, TimeTrackerEvents.JOIN_SPACE);
		onJoinFlowStatus?.(JoinFlowStatus.JoiningRoom);
		const endJoinSpace = getTimeTracker().startMeasurement(TimeTrackerEvents.JOIN_SPACE, "river_joinroom_space");
		const room = await this.joinRoom(spaceId, void 0, {
			skipWaitForMiniblockConfirmation: true,
			skipWaitForUserStreamUpdate: true
		});
		endJoinSpace?.();
		this.log("[joinTown] room", room);
		const spaceContent = this.casablancaClient?.streams.get(spaceId)?.view.spaceContent;
		if (spaceContent) {
			const defaultUsernamePromise = (async () => {
				if (!defaultUsername) {
					this.log("[joinTown] no default username provided");
					return;
				}
				const isAvailable = await this.getIsUsernameAvailable(spaceId, defaultUsername);
				if (!isAvailable) {
					this.log("[joinTown] default usename already taken");
					return;
				}
				try {
					this.log("[joinTown] setting default username");
					await this.casablancaClient?.setUsername(spaceId, defaultUsername);
					this.log("[joinTown] default usename set");
				} catch (e) {
					console.error("[joinTown] fail to set default username", e);
				}
			})();
			await Promise.all([defaultUsernamePromise, ...Object.entries(spaceContent.spaceChannelsMetadata).filter(([_, value]) => value.isDefault || value.isAutojoin).map(async ([key]) => {
				onJoinFlowStatus?.(JoinFlowStatus.JoiningAutojoinChannels);
				const endJoinChannel = getTimeTracker().startMeasurement(TimeTrackerEvents.JOIN_SPACE, "river_joinroom_channel");
				try {
					await this.joinRoom(key, void 0, {
						skipWaitForMiniblockConfirmation: true,
						skipWaitForUserStreamUpdate: true
					});
				} catch (e) {
					console.error("[joinTown] Failed auto-joining channel", e);
				}
				endJoinChannel?.();
			})]);
		} else this.log("[joinTown] Error no space content found");
		onJoinFlowStatus?.(JoinFlowStatus.Done);
		return room;
	}
	/************************************************
	* pinMessage
	*************************************************/
	async pinMessage(roomId, eventId) {
		if (!this.casablancaClient) throw new Error("Casablanca client not initialized");
		await this.casablancaClient.pin(roomId, eventId);
		this.log("sendPin");
	}
	async unpinMessage(roomId, eventId) {
		if (!this.casablancaClient) throw new Error("Casablanca client not initialized");
		await this.casablancaClient.unpin(roomId, eventId);
		this.log("sendUnpin");
	}
	/************************************************
	* sendMessage
	*************************************************/
	async sendMessage(roomId, message, options) {
		if (!this.casablancaClient) throw new Error("Casablanca client not initialized");
		switch (options?.messageType) {
			case void 0:
			case MessageType.Text:
				{
					const mentions = options?.mentions?.map((x) => {
						if (x.atChannel) return create$1(ChannelMessage_Post_MentionSchema, { mentionBehavior: {
							case: "atChannel",
							value: {}
						} });
						else return create$1(ChannelMessage_Post_MentionSchema, x);
					}) ?? [];
					await this.casablancaClient.sendChannelMessage_Text(roomId, {
						threadId: options?.threadId,
						threadPreview: options?.threadPreview,
						replyId: options?.replyId,
						replyPreview: options?.replyPreview,
						content: {
							body: message,
							mentions,
							attachments: transformAttachments(options?.attachments)
						}
					}, {
						appClientAddress: options?.appClientAddress,
						beforeSendEventHook: options?.beforeSendEventHook,
						onLocalEventAppended: options?.onLocalEventAppended
					});
				}
				break;
			case MessageType.Image:
				await this.casablancaClient.sendChannelMessage_Image(roomId, {
					threadId: options?.threadId,
					threadPreview: options?.threadPreview,
					replyId: options?.replyId,
					replyPreview: options?.replyPreview,
					content: {
						title: message,
						info: options?.info,
						thumbnail: options?.thumbnail
					}
				}, { beforeSendEventHook: options?.beforeSendEventHook });
				break;
			case MessageType.GM:
				await this.casablancaClient.sendChannelMessage_GM(roomId, {
					threadId: options?.threadId,
					threadPreview: options?.threadPreview,
					replyId: options?.replyId,
					replyPreview: options?.replyPreview,
					content: { typeUrl: message }
				}, { beforeSendEventHook: options?.beforeSendEventHook });
				break;
			default: staticAssertNever(options);
		}
		this._eventHandlers?.onSendMessage?.(roomId, message, options);
	}
	async sendMediaPayload(creationCookie, last, data, chunkIndex, iv) {
		if (!this.casablancaClient) throw new Error("Casablanca client not initialized");
		return await this.casablancaClient.sendMediaPayload(creationCookie, last, data, chunkIndex, iv);
	}
	/************************************************
	* sendReaction
	*************************************************/
	async sendReaction(roomId, eventId, reaction, _threadId) {
		if (!this.casablancaClient) throw new Error("Casablanca client not initialized");
		const beforeSendEventHooks = [];
		const beforeSendEventHook = beforeSendEventHooks.length ? Promise.all(beforeSendEventHooks).then(() => void 0) : void 0;
		await this.casablancaClient.sendChannelMessage_Reaction(roomId, {
			reaction,
			refEventId: eventId
		}, { beforeSendEventHook });
		this.log("sendReaction");
	}
	async sendTokenTransfer(chainId, receipt, event) {
		if (!this.casablancaClient) throw new Error("Casablanca client not initialized");
		await this.casablancaClient.addTransaction_Transfer(chainId, receipt, event);
	}
	/************************************************
	* sendInteractionResponse
	*************************************************/
	async sendInteractionResponse(roomId, recipient, response, toUserDevice, threadParentId) {
		if (!this.casablancaClient) throw new Error("Casablanca client not initialized");
		if (!toUserDevice) throw new Error("toUserDevice is undefined");
		return await this.casablancaClient.sendInteractionResponse(roomId, recipient, response, toUserDevice, { threadId: threadParentId });
	}
	/************************************************
	* retrySendMessage
	*************************************************/
	async retrySendMessage(roomId, localEventId) {
		if (!this.casablancaClient) throw new Error("Casablanca client not initialized");
		try {
			await this.casablancaClient.retrySendMessage(roomId, localEventId);
		} catch (err) {
			console.error("retrySendMessage failed", err);
		}
	}
	/************************************************
	* editMessage
	*************************************************/
	async editMessage(roomId, eventId, originalEventContent, message, options) {
		if (!this.casablancaClient) throw new Error("casablanca client is undefined");
		const event = this.casablancaClient.stream(roomId)?.view.timeline.find((x) => x.eventId === eventId);
		if (!event) throw new Error(`ref event not found: ${eventId}`);
		if (event.sender.id !== this.casablancaClient.userId) throw new Error(`you can only send channelMessageEdit for your own messages: ${eventId} userId: ${this.casablancaClient.userId}`);
		await this.casablancaClient.sendChannelMessage_Edit_Text(roomId, eventId, {
			threadId: originalEventContent.threadId,
			threadPreview: originalEventContent.threadPreview,
			replyId: originalEventContent.replyId,
			replyPreview: originalEventContent.replyPreview,
			content: {
				body: message,
				mentions: options?.mentions?.map((x) => create$1(ChannelMessage_Post_MentionSchema, x)) ?? [],
				attachments: transformAttachments(options?.attachments)
			}
		});
	}
	/************************************************
	* redactEvent
	*************************************************/
	async redactEvent(roomId, eventId, reason) {
		if (!this.casablancaClient) throw new Error("casablanca client is undefined");
		const event = this.casablancaClient.stream(roomId)?.view.timeline.find((x) => x.eventId === eventId);
		if (!event) throw new Error(`ref event not found: ${eventId}`);
		if (event.sender.id !== this.casablancaClient.userId) throw new Error(`you can only send channelMessageRedaction for your own messages: ${eventId} userId: ${this.casablancaClient.userId}`);
		await this.casablancaClient.sendChannelMessage_Redaction(roomId, {
			refEventId: eventId,
			reason
		});
	}
	/************************************************
	* setRoomFullyReadData
	************************************************/
	async setRoomFullyReadData(channelId, content) {
		if (!this.casablancaClient) throw new Error("Casablanca client is undefined");
		await this.casablancaClient.sendFullyReadMarkers(channelId, content);
	}
	/************************************************
	* updateUserBlock
	************************************************/
	async updateUserBlock(userId, isBlocked) {
		if (!this.casablancaClient) throw new Error("Casablanca client is undefined");
		await this.casablancaClient.updateUserBlock(userId, isBlocked);
	}
	/************************************************
	* getMembership
	************************************************/
	getMembership(streamId) {
		if (!this.casablancaClient) throw new Error("casablanca client is undefined");
		const userStreamId = this.casablancaClient.userStreamId;
		if (!userStreamId) return Membership.None;
		const membershipOp = this.casablancaClient.stream(userStreamId)?.view.userContent.getMembership(streamId)?.op;
		return toMembership(membershipOp);
	}
	/************************************************
	* getStreamMember
	* - This function is primarily intended for testing purposes
	* **********************************************/
	getStreamMember(roomId, userId) {
		const roomData = this.getRoomData(roomId);
		return roomData?.members.find((x) => x.userId === userId);
	}
	/************************************************
	* getRoomData
	* - This function is primarily intended for testing purposes
	************************************************/
	getRoomData(streamId) {
		if (!this.casablancaClient) throw new Error("casablanca client is undefined");
		const membership = this.getMembership(streamId);
		const stream = this.casablancaClient.stream(streamId);
		if (stream) return toStreamView(stream, membership);
		else if (membership !== Membership.None) return {
			id: streamId,
			membership,
			members: []
		};
		else return void 0;
	}
	/************************************************
	* getIsUsernameAvailable
	************************************************/
	getIsUsernameAvailable(streamId, username) {
		if (!this.casablancaClient) throw new Error("No casablanca client");
		return Promise.resolve(this.casablancaClient.isUsernameAvailable(streamId, username));
	}
	async adminRedactMessage(streamId, eventId) {
		if (!this.casablancaClient) throw new Error("No casablanca client");
		await this.casablancaClient.redactMessage(streamId, eventId);
	}
	async setSpaceImage(spaceStreamId, chunkedMedia) {
		if (!this.casablancaClient) throw new Error("No casablanca client");
		const response = await this.casablancaClient.setSpaceImage(spaceStreamId, chunkedMedia);
		return response;
	}
	async setUserProfileImage(chunkedMedia, userId) {
		if (!this.casablancaClient) throw new Error("No casablanca client");
		await this.casablancaClient.setUserProfileImage(chunkedMedia, userId);
	}
	async getUserProfileImage(userId) {
		if (!this.casablancaClient) throw new Error("No casablanca client");
		return this.casablancaClient.getUserProfileImage(userId);
	}
	async setUserBio(bio) {
		if (!this.casablancaClient) throw new Error("No casablanca client");
		await this.casablancaClient.setUserBio(bio);
	}
	/************************************************
	* setDisplayName
	************************************************/
	async setDisplayName(streamId, displayName) {
		if (!this.casablancaClient) throw new Error("No casablanca client");
		await this.casablancaClient.setDisplayName(streamId, displayName);
	}
	/************************************************
	* avatarUrl
	************************************************/
	async setAvatarUrl(url) {
		console.error("not implemented for casablanca", url);
	}
	async setRoomProperties(roomId, title, topic) {
		if (!this.casablancaClient) throw new Error("casablanca client is undefined");
		await this.casablancaClient.updateGDMChannelProperties(roomId, title, topic);
	}
	async setChannelAutojoin(parentSpaceId, roomId, autojoin) {
		if (!this.casablancaClient) throw new Error("casablanca client is undefined");
		await this.casablancaClient.updateChannelAutojoin(parentSpaceId, roomId, autojoin);
	}
	async setChannelHideUserJoinLeaveEvents(parentSpaceId, roomId, hideUserJoinLeaveEvents) {
		if (!this.casablancaClient) throw new Error("casablanca client is undefined");
		await this.casablancaClient.updateChannelHideUserJoinLeaveEvents(parentSpaceId, roomId, hideUserJoinLeaveEvents);
	}
	async setHighPriorityStreams(streamIds) {
		if (!this.casablancaClient) throw new Error("casablanca client is undefined");
		this.casablancaClient.setHighPriorityStreams(streamIds);
	}
	/************************************************
	* scrollback
	************************************************/
	async scrollback(roomId) {
		if (!this.casablancaClient) throw new Error("casablanca client is undefined");
		const result = await this.casablancaClient.scrollback(roomId);
		return {
			terminus: result.terminus,
			fromInclusiveMiniblockNum: result.fromInclusiveMiniblockNum
		};
	}
	async scrollbackToEvent(roomId, eventId, limit) {
		if (!this.casablancaClient) throw new Error("casablanca client is undefined");
		for (let i = 0; i < limit; i++) {
			await this.casablancaClient.scrollback(roomId);
			if (this.casablancaClient.stream(roomId)?.view.timeline.find((x) => x.eventId === eventId) !== void 0) return true;
		}
		return false;
	}
	async addSpaceReview(txnContext) {
		const receipt = txnContext.receipt;
		const chainId = this.spaceDapp?.config.chainId;
		if (txnContext.status === TransactionStatus.Success && receipt && chainId && txnContext.data?.spaceId) {
			console.log("waitForReviewTransaction::success", {
				status: txnContext.status,
				spaceId: txnContext.data.spaceId,
				receipt
			});
			try {
				const reviewEvent = getSpaceReviewEventData(receipt.logs, txnContext.data.senderAddress);
				console.log("addSpaceReview::reviewEvent", reviewEvent);
				if (reviewEvent) {
					console.log("addSpaceReview::success::reviewEvent", {
						reviewEvent,
						spaceId: txnContext.data.spaceId
					});
					await retryOperation(async () => {
						const client = this.casablancaClient;
						await client?.addTransaction_SpaceReview(chainId, receipt, reviewEvent, txnContext.data.spaceId);
					}, {
						maxRetries: 3,
						onError: (error, retryCount) => {
							console.error("Error in addTransaction_SpaceReview:", error);
							this.log("Retrying addTransaction_SpaceReview", { retryCount });
						},
						getRetryDelay: () => 1e3
					});
				} else console.error("addSpaceReview::error: No review event found in receipt");
			} catch (error) {
				console.error("addSpaceReview::error processing review event:", error);
			}
		} else console.error("addSpaceReview::error: Invalid transaction context", {
			status: txnContext.status,
			hasReceipt: !!receipt,
			hasChainId: !!chainId,
			hasSpaceId: !!txnContext.data?.spaceId
		});
		return txnContext;
	}
	log(message, ...optionalParams) {
		console.log(message, ...optionalParams);
	}
	debug(message, ...optionalParams) {
		console.debug(message, ...optionalParams);
	}
	setEventHandlers(eventHandlers) {
		this._eventHandlers = eventHandlers;
	}
	onLogin({ userId }) {
		this._eventHandlers?.onLogin?.({ userId });
	}
};

//#endregion
//#region src/hooks/use-network-status.tsx
const useNetworkStatus = () => {
	const [isOffline, setIsOffline] = useState(!navigator.onLine);
	useEffect(() => {
		const onOnlineStatusChange = () => {
			setIsOffline(!navigator.onLine);
		};
		window.addEventListener("online", onOnlineStatusChange);
		window.addEventListener("offline", onOnlineStatusChange);
		return () => {
			window.removeEventListener("online", onOnlineStatusChange);
			window.removeEventListener("offline", onOnlineStatusChange);
		};
	}, []);
	return { isOffline };
};

//#endregion
//#region src/hooks/use-towns-client-listener.ts
const useTownsClientListener = (opts) => {
	const { setAuthStatus: setCasablancaAuthStatus, setAuthError: setCasablancaAuthError } = useCasablancaStore();
	const { casablancaCredentialsMap, clearCasablancaCredentials } = useCredentialStore();
	const casablancaCredentials = casablancaCredentialsMap[opts.environmentId ?? ""];
	const [casablancaClient, setCasablancaClient] = useState();
	const [signerContext, setSignerContext] = useState();
	const clientSingleton = useRef(void 0);
	const { isOffline } = useNetworkStatus();
	const baseReads = useBaseReads();
	if (!clientSingleton.current) {
		const townsClient = new TownsClient(opts, { baseReads });
		clientSingleton.current = new ClientStateMachine(townsClient);
	}
	logServerUrlMismatch(opts.environmentId, clientSingleton);
	useEffect(() => {
		if (!casablancaClient) return;
		const isOnline = !isOffline;
		if (isOnline) casablancaClient.streams.onNetworkStatusChanged(isOnline);
	}, [casablancaClient, isOffline]);
	useEffect(() => {
		const stateMachine = clientSingleton.current;
		if (!stateMachine) return;
		const onStateMachineUpdated = (state) => {
			setCasablancaClient(state.casablancaClient);
			setCasablancaAuthStatus(state.authStatus);
			setSignerContext(state.signerContext);
		};
		const onClearCredentials = (oldCredendials) => {
			clearCasablancaCredentials(opts.environmentId ?? "", oldCredendials);
		};
		const onErrorUpdated = (e) => {
			if (!e) {
				setCasablancaClient(void 0);
				return;
			}
			const _error = e;
			setCasablancaAuthError({
				code: _error?.code ?? 0,
				message: _error?.message ?? `${JSON.stringify(e)}`,
				error: e
			});
		};
		stateMachine.on("onStateUpdated", onStateMachineUpdated);
		stateMachine.on("onClearCredentials", onClearCredentials);
		stateMachine.on("onErrorUpdated", onErrorUpdated);
		stateMachine.update(casablancaCredentials ?? void 0);
		onStateMachineUpdated(stateMachine.state);
		return () => {
			stateMachine.off("onStateUpdated", onStateMachineUpdated);
			stateMachine.off("onClearCredentials", onClearCredentials);
			stateMachine.off("onErrorUpdated", onErrorUpdated);
		};
	}, [
		casablancaCredentials,
		opts.environmentId,
		setCasablancaClient,
		clearCasablancaCredentials,
		setCasablancaAuthError,
		setCasablancaAuthStatus
	]);
	return {
		client: casablancaClient ? clientSingleton.current.client : void 0,
		clientSingleton: clientSingleton.current.client,
		casablancaClient,
		signerContext
	};
};
var ConnectedToRiver = class {
	authStatus = AuthStatus.ConnectedToRiver;
	constructor(credentials, casablancaClient, signerContext) {
		this.credentials = credentials;
		this.casablancaClient = casablancaClient;
		this.signerContext = signerContext;
	}
};
var None = class {
	authStatus = AuthStatus.None;
	credentials = void 0;
	signerContext = void 0;
	casablancaClient = void 0;
};
var Credentialed = class {
	authStatus = AuthStatus.Credentialed;
	casablancaClient = void 0;
	constructor(credentials, signerContext) {
		this.credentials = credentials;
		this.signerContext = signerContext;
	}
};
var EvaluatingCredentials = class {
	authStatus = AuthStatus.EvaluatingCredentials;
	casablancaClient = void 0;
	signerContext = void 0;
	constructor(credentials) {
		this.credentials = credentials;
	}
};
var DisconnectingFromRiver = class {
	authStatus = AuthStatus.DisconnectingFromRiver;
	credentials = void 0;
	signerContext = void 0;
	constructor(casablancaClient) {
		this.casablancaClient = casablancaClient;
	}
};
var Deauthenticating = class {
	authStatus = AuthStatus.Deauthenticating;
	casablancaClient = void 0;
	credentials = void 0;
	signerContext = void 0;
};
function isSituation(state) {
	return state.authStatus === AuthStatus.ConnectedToRiver || state.authStatus === AuthStatus.None;
}
var ClientStateMachine = class extends EventEmitter {
	state = new None();
	client;
	next = {};
	constructor(client) {
		super();
		this.client = client;
		this.client.on("onCasablancaClientCreated", (client$1) => {
			this.updateClient(client$1);
		});
	}
	update(nextCredentials) {
		this.next = { credentials: nextCredentials };
		this.tick();
	}
	updateClient(client) {
		if (this.state instanceof Credentialed) {
			console.log("[use-towns-client-listener] connected to river after client update");
			this.state = new ConnectedToRiver(this.state.credentials, client, this.state.signerContext);
			this.emit("onStateUpdated", this.state);
		}
	}
	async tick() {
		if (!isSituation(this.state)) return;
		const transition = getTransition(this.state, this.next);
		if (!transition) return;
		logTick(this.client, this.state, transition);
		const currentSituation = this.state;
		this.state = transition;
		this.emit("onStateUpdated", this.state);
		this.state = await this.execute(currentSituation, transition);
		logTick(this.client, this.state);
		this.emit("onStateUpdated", this.state);
		this.tick();
	}
	async execute(currentSituation, transition) {
		switch (transition.authStatus) {
			case AuthStatus.DisconnectingFromRiver:
				check(currentSituation instanceof ConnectedToRiver);
				await this.client.stopCasablancaClient();
				return new None();
			case AuthStatus.Deauthenticating:
				check(currentSituation instanceof Credentialed);
				return new None();
			case AuthStatus.EvaluatingCredentials: {
				check(currentSituation instanceof None);
				this.emit("onErrorUpdated", void 0);
				const { credentials } = transition;
				const newState = await logInWithRetries(credentials, this.client, this);
				return newState;
			}
			default: staticAssertNever(transition);
		}
	}
};
async function logInWithRetries(credentials, client, emitter) {
	let retryCount = 0;
	const MAX_RETRY_COUNT = 20;
	while (true) {
		const context = credentialsToSignerContext(credentials);
		try {
			console.log("[use-towns-client-listener] logging in");
			const userId = userIdFromAddress(context.creatorAddress);
			const isRegistered = useOfflineStore.getState().skipIsRegisteredCheck[userId] || await fetchIsRegistered(client, context);
			if (!isRegistered) {
				console.log("[use-towns-client-listener] user authenticated, hasn't joined a space");
				return new Credentialed(credentials, context);
			} else {
				console.log("[use-towns-client-listener] user authenticated, starting casablanca client");
				useOfflineStore.getState().setSkipIsRegisteredCheck(userId);
				const casablancaClient = await client.startCasablancaClient(context);
				return new ConnectedToRiver(credentials, casablancaClient, context);
			}
		} catch (e) {
			retryCount++;
			console.error("[use-towns-client-listener] casablanca client encountered exception", e);
			try {
				await client.logoutFromCasablanca();
			} catch (e$1) {
				console.error("error while logging out", e$1);
			}
			if (errorContains(e, Err.BAD_STREAM_CREATION_PARAMS)) return new Credentialed(credentials, context);
			if (retryCount >= MAX_RETRY_COUNT) {
				emitter.emit("onClearCredentials", credentials);
				emitter.emit("onErrorUpdated", e);
				return new None();
			} else {
				const retryDelay = getRetryDelay(retryCount);
				console.log("[use-towns-client-listener] retrying", {
					retryDelay,
					retryCount
				});
				await new Promise((resolve) => setTimeout(resolve, retryDelay));
			}
		}
	}
}
function getRetryDelay(retryCount) {
	return Math.min(1e3 * 2 ** retryCount, 2e4);
}
function getTransition(current, next) {
	switch (current.authStatus) {
		case AuthStatus.None:
			if (next.credentials !== void 0) return new EvaluatingCredentials(next.credentials);
			break;
		case AuthStatus.ConnectedToRiver:
			if (next.credentials !== current.credentials) return new DisconnectingFromRiver(current.casablancaClient);
			break;
		case AuthStatus.Credentialed:
			if (next.credentials != current.credentials) return new Deauthenticating();
			break;
		default: staticAssertNever(current);
	}
	return void 0;
}
function logTick(client, situation, transition) {
	console.log("$$$ tick update", {
		client: client.name,
		current: situation.authStatus,
		transition: transition?.authStatus
	});
}
function logServerUrlMismatch(environmentId, clientSingleton) {
	if (environmentId !== clientSingleton.current?.client.opts.environmentId) console.error("$$$ use towns client listener: environmentId doesn't match", {
		environmentId,
		client: clientSingleton.current?.client.opts.environmentId
	});
}
async function fetchIsRegistered(client, context) {
	console.log("[use-towns-client-listener] fetching Is registered State for", context.creatorAddress);
	const unauthedClient = await client.makeUnauthenticatedClient();
	return await unauthedClient.userWithAddressExists(context.creatorAddress);
}

//#endregion
//#region src/components/QueryProvider.tsx
function QueryProvider({ children }) {
	return /* @__PURE__ */ jsx(QueryClientProvider, {
		client: queryClient,
		children
	});
}

//#endregion
//#region src/hooks/TownsContext/useClientInitStatus.ts
function useClientInitStatus(client) {
	const [clientStatus, setClientStatus] = useState(() => ({
		isLocalDataLoaded: false,
		isRemoteDataLoaded: false,
		streamSyncActive: false,
		isHighPriorityDataLoaded: false
	}));
	useEffect(() => {
		if (!client) return;
		const updateStreamSyncActive = (active) => {
			setClientStatus((prev) => {
				if (prev.streamSyncActive === active) return prev;
				else return {
					...prev,
					streamSyncActive: active
				};
			});
		};
		const updateClientInitStatus = (status) => {
			setClientStatus((prev) => {
				if (status.isLocalDataLoaded === prev.isLocalDataLoaded && status.isRemoteDataLoaded === prev.isRemoteDataLoaded && status.isHighPriorityDataLoaded === prev.isHighPriorityDataLoaded) return prev;
				else return {
					isLocalDataLoaded: status.isLocalDataLoaded,
					isRemoteDataLoaded: status.isRemoteDataLoaded,
					streamSyncActive: prev.streamSyncActive,
					isHighPriorityDataLoaded: status.isHighPriorityDataLoaded
				};
			});
		};
		updateClientInitStatus(client.clientInitStatus);
		updateStreamSyncActive(client.streamSyncActive);
		client.on("streamSyncActive", updateStreamSyncActive);
		client.on("clientInitStatusUpdated", updateClientInitStatus);
		return () => {
			client.off("streamSyncActive", updateStreamSyncActive);
			client.off("clientInitStatusUpdated", updateClientInitStatus);
		};
	}, [client]);
	return clientStatus;
}

//#endregion
//#region src/hooks/use-user-lookup-updater.ts
const dlog = dlogger("csb:hooks:user-lookup-updater");
const SnapshotCaseTypeValues = {
	spaceContent: "spaceContent",
	channelContent: "channelContent",
	userContent: "userContent",
	userSettingsContent: "userSettingsContent",
	userDeviceKeyContent: "userDeviceKeyContent",
	mediaContent: "mediaContent",
	dmChannelContent: "dmChannelContent",
	gdmChannelContent: "gdmChannelContent",
	userInboxContent: "userInboxContent"
};
const meaningfulInfo = (maybeInfo) => {
	return !!(maybeInfo && maybeInfo.username !== "");
};
const isUserInfo = (maybeInfo) => {
	return !!(!!maybeInfo && typeof maybeInfo.username === "string" && typeof maybeInfo.displayName === "string");
};
const createUserLookup = (userId, info, ens) => ({
	userId,
	username: info.username,
	usernameConfirmed: info.usernameConfirmed,
	usernameEncrypted: info.usernameEncrypted,
	displayName: info.displayName,
	displayNameEncrypted: info.displayNameEncrypted,
	ensAddress: ens?.ensAddress,
	nft: info.nft,
	ensName: ens?.ensName
});
const useUserLookupUpdater = (townsOpts, client, townsClient) => {
	const { setSpaceUsers: setSpaceUsers$1, setSpaceUser: setSpaceUser$1, setChannelUser: setChannelUser$1, setChannelUsers, updateUserEverywhere } = useUserLookupStore(useShallow((s) => ({
		setSpaceUsers: s.setSpaceUsers,
		setSpaceUser: s.setSpaceUser,
		setChannelUser: s.setChannelUser,
		setChannelUsers: s.setChannelUsers,
		updateUserEverywhere: s.updateUserEverywhere
	})));
	const onStreamMetadataUpdated = useCallback((streamId, userId) => {
		dlog.log("onStreamMetadataUpdated", streamId, userId);
		if (isSpaceStreamId(streamId)) {
			const stream = client?.streams.get(streamId);
			const joined = stream?.view.getMembers()?.joined;
			const appAddress = joined?.get(userId)?.appAddress;
			const isBot$1 = appAddress !== void 0 && appAddress !== zeroAddress;
			if (isBot$1) return;
			const metadata = stream?.view.getMemberMetadata();
			const info = metadata?.userInfo(userId);
			if (isUserInfo(info)) {
				const update = () => {
					const lookupUser = createUserLookup(userId, info);
					setSpaceUser$1(userId, lookupUser, streamId);
				};
				update();
			}
		} else if (isDMChannelStreamId(streamId) || isGDMChannelStreamId(streamId)) {
			const stream = client?.streams.get(streamId);
			const joined = stream?.view.getMembers()?.joined;
			const appAddress = joined?.get(userId)?.appAddress;
			const isBot$1 = appAddress !== void 0 && appAddress !== zeroAddress;
			if (isBot$1) return;
			const metadata = stream?.view.getMemberMetadata();
			const info = metadata?.userInfo(userId);
			if (meaningfulInfo(info)) {
				const update = () => {
					const lookupUser = createUserLookup(userId, info);
					setChannelUser$1(userId, lookupUser, streamId);
				};
				update();
			}
		}
	}, [
		client?.streams,
		setChannelUser$1,
		setSpaceUser$1
	]);
	const onStreamInitialized = useCallback((streamId, contentKind) => {
		dlog.log("onStreamInitialized", streamId, contentKind);
		const stream = client?.streams.get(streamId);
		const metadata = stream?.view.getMemberMetadata();
		if (!stream || !metadata) return;
		const spaceRecords = [];
		const channelRecords = [];
		for (const userId of stream.view.getMembers().participants() ?? []) {
			const joined = stream.view.getMembers()?.joined;
			const appAddress = joined?.get(userId)?.appAddress;
			const isBot$1 = appAddress !== void 0 && appAddress !== zeroAddress;
			if (isBot$1) continue;
			const info = metadata.userInfo(userId);
			if (isUserInfo(info)) {
				const update = () => {
					const lookupUser = createUserLookup(userId, info);
					switch (contentKind) {
						case SnapshotCaseTypeValues.spaceContent:
							spaceRecords.push({
								userId,
								user: lookupUser,
								spaceId: streamId
							});
							break;
						case SnapshotCaseTypeValues.dmChannelContent:
						case SnapshotCaseTypeValues.gdmChannelContent:
							if (meaningfulInfo(info)) channelRecords.push({
								userId,
								user: lookupUser,
								channelId: streamId
							});
							break;
					}
				};
				update();
			}
		}
		if (spaceRecords.length > 0) setSpaceUsers$1(spaceRecords);
		if (channelRecords.length > 0) setChannelUsers(channelRecords);
	}, [
		client?.streams,
		setChannelUsers,
		setSpaceUsers$1
	]);
	const onWalletUnlinked = useCallback((userId, walletUnlinked) => {
		updateUserEverywhere(userId, (user) => {
			if (user.ensAddress === walletUnlinked) return {
				...user,
				ensName: void 0,
				ensAddress: void 0
			};
			return user;
		});
	}, [updateUserEverywhere]);
	const oldClient = useRef(client);
	useEffect(() => {
		if (oldClient.current === client) {
			dlog.log("useUserLookupUpdater, client changed", oldClient.current, client);
			oldClient.current = client;
		}
		if (!client) return;
		client.on("streamInitialized", onStreamInitialized);
		client.on("streamMembershipUpdated", onStreamMetadataUpdated);
		client.on("streamDisplayNameUpdated", onStreamMetadataUpdated);
		client.on("streamPendingDisplayNameUpdated", onStreamMetadataUpdated);
		client.on("streamUsernameUpdated", onStreamMetadataUpdated);
		client.on("streamPendingUsernameUpdated", onStreamMetadataUpdated);
		client.on("streamNewUserJoined", onStreamMetadataUpdated);
		client.on("streamUserLeft", onStreamMetadataUpdated);
		client.on("streamEnsAddressUpdated", onStreamMetadataUpdated);
		client.on("streamNftUpdated", onStreamMetadataUpdated);
		townsClient?.on("onWalletUnlinked", onWalletUnlinked);
		return () => {
			client.off("streamInitialized", onStreamInitialized);
			client.off("streamMembershipUpdated", onStreamMetadataUpdated);
			client.off("streamDisplayNameUpdated", onStreamMetadataUpdated);
			client.off("streamPendingDisplayNameUpdated", onStreamMetadataUpdated);
			client.off("streamUsernameUpdated", onStreamMetadataUpdated);
			client.off("streamPendingUsernameUpdated", onStreamMetadataUpdated);
			client.off("streamNewUserJoined", onStreamMetadataUpdated);
			client.off("streamUserLeft", onStreamMetadataUpdated);
			client.off("streamEnsAddressUpdated", onStreamMetadataUpdated);
			client.off("streamNftUpdated", onStreamMetadataUpdated);
			townsClient?.on("onWalletUnlinked", onWalletUnlinked);
		};
	}, [
		client,
		onStreamInitialized,
		onStreamMetadataUpdated,
		onWalletUnlinked,
		townsClient
	]);
};

//#endregion
//#region src/store/use-image-store.tsx
const useImageStore = create((set) => ({
	loadedResource: {},
	erroredResources: {},
	addErroredResource: (resourceId) => {
		set((state) => {
			if (state.erroredResources[resourceId]) return state;
			return { erroredResources: {
				...state.erroredResources,
				[resourceId]: true
			} };
		});
	},
	removeErroredResource: (resourceId) => {
		set((state) => {
			const errors = { ...state.erroredResources };
			Object.keys(errors).forEach((key) => {
				if (key.startsWith(resourceId)) delete errors[key];
			});
			return { erroredResources: errors };
		});
	},
	setLoadedResource: (resourceId, resource) => {
		set((state) => {
			return { loadedResource: {
				...state.loadedResource,
				[resourceId]: resource
			} };
		});
	},
	removeLoadedResource: (resourceId) => {
		set((state) => {
			const loadedResource = { ...state.loadedResource };
			delete loadedResource[resourceId];
			return { loadedResource };
		});
	}
}));

//#endregion
//#region src/hooks/use-stream-metadata-updater.ts
const buildImageUrl = (mediaStreamId, key, iv, streamMetadataUrl) => {
	const url = new URL(streamMetadataUrl);
	url.pathname = `/media/${mediaStreamId}`;
	url.searchParams.set("key", key);
	url.searchParams.set("iv", iv);
	return url.toString();
};
const useStreamMetadataUpdater = (client, townsClient, streamMetadataUrl) => {
	useEffect(() => {
		if (!client || !streamMetadataUrl) return;
		const onSpaceImageUpdated = (spaceId) => {
			console.log("onSpaceImageUpdated", spaceId);
			client.stream(spaceId)?.view.spaceContent.getSpaceImage().then((image) => {
				if (!image || !image.info || !image.encryption?.value) return;
				const imageUrl = buildImageUrl(image.streamId, bin_toHexString(image.encryption.value.secretKey), bin_toHexString(image.encryption.value.iv), streamMetadataUrl);
				useImageStore.getState().setLoadedResource(spaceId, { imageUrl });
			});
		};
		const onMyUserProfileImageUpdated = (streamId) => {
			console.log("onMyUserProfileImageUpdated", streamId);
			const userId = getUserIdFromStreamId(streamId);
			client.stream(streamId)?.view.userMetadataContent.getProfileImage().then((image) => {
				if (!image || !image.info || !image.encryption?.value) return;
				const imageUrl = buildImageUrl(image.streamId, bin_toHexString(image.encryption.value.secretKey), bin_toHexString(image.encryption.value.iv), streamMetadataUrl);
				useImageStore.getState().setLoadedResource(userId, { imageUrl });
			});
		};
		client.on("spaceImageUpdated", onSpaceImageUpdated);
		client.on("userProfileImageUpdated", onMyUserProfileImageUpdated);
		return () => {
			client.off("spaceImageUpdated", onSpaceImageUpdated);
			client.off("userProfileImageUpdated", onMyUserProfileImageUpdated);
		};
	}, [
		client,
		townsClient,
		streamMetadataUrl
	]);
};

//#endregion
//#region src/hooks/use-channels.ts
function getChannelsFromSpaceData(channelGroups) {
	return channelGroups.reduce((channels, group) => {
		return [...channels, ...group.channels];
	}, []);
}

//#endregion
//#region src/hooks/use-space-id.ts
function useSpaceId() {
	const { spaceId } = useSpaceContext();
	return spaceId;
}

//#endregion
//#region src/hooks/use-space-thread-roots.ts
const EMPTY_THREADS = [];
const useSpaceThreadRootsStore = create((set) => ({
	threads: {},
	setThreads: (spaceId, threads) => set((state) => {
		const prevThreads = state.threads[spaceId];
		if (isEqual(prevThreads, threads)) return state;
		return { threads: {
			...state.threads,
			[spaceId]: threads
		} };
	})
}));
function useCalculateSpaceThreadRoots(_opts, casablancaClient) {
	const timelinesView = casablancaClient?.streamsView.timelinesView;
	const fullyReadMarkers = casablancaClient?.streamsView.my.unreadMarkers;
	useEffect(() => {
		if (!timelinesView || !fullyReadMarkers) return;
		const runUpdate = () => {
			const unreadMarkers = fullyReadMarkers.value.markers;
			const threadsStats = timelinesView.value.threadsStats;
			const spaceData = useSpaceDataStore.getState().spaceDataMap;
			for (const spaceId in spaceData) {
				const space = spaceData[spaceId];
				if (!space) continue;
				const channels = getChannelsFromSpaceData(space.channelGroups);
				const threads = [];
				channels.forEach((channel) => {
					const channelThreadStats = threadsStats[channel.id] || {};
					const channelThreads = Object.values(channelThreadStats).filter((thread) => thread.isParticipating).map((thread) => ({
						type: "thread",
						isNew: isNew(unreadMarkers[thread.parentId]),
						isUnread: unreadMarkers[thread.parentId]?.isUnread === true,
						fullyReadMarker: unreadMarkers[thread.parentId],
						thread,
						channel: {
							id: channel.id,
							label: channel.label
						},
						timestamp: thread.latestTs
					}));
					threads.push(...channelThreads);
				});
				threads.sort((a, b) => {
					if (a.isUnread && !b.isUnread) return -1;
					else if (!a.isUnread && b.isUnread) return 1;
					else if (a.timestamp > b.timestamp) return -1;
					else if (a.timestamp < b.timestamp) return 1;
					else return 0;
				});
				useSpaceThreadRootsStore.getState().setThreads(spaceId, threads);
			}
		};
		const debouncedRunUpdate = debounce(runUpdate, 1e3, { maxWait: 1500 });
		const unsub1 = useSpaceDataStore.subscribe(debouncedRunUpdate);
		const unsub2 = timelinesView.subscribe(debouncedRunUpdate);
		const unsub3 = fullyReadMarkers.subscribe(debouncedRunUpdate);
		return () => {
			unsub1();
			unsub2();
			unsub3();
		};
	}, [timelinesView, fullyReadMarkers]);
}
function useSpaceThreadRootsForId(spaceId) {
	return useSpaceThreadRootsStore((state) => spaceId ? state.threads[spaceId] : void 0);
}
function useSpaceThreadRoots() {
	const spaceId = useSpaceId();
	const threads = useSpaceThreadRootsForId(spaceId);
	return threads ?? EMPTY_THREADS;
}
function useSpaceThreadRootsUnreadCount() {
	const roots = useSpaceThreadRoots();
	return roots.filter((t) => t.isUnread).length;
}
function isNew(marker) {
	if (!marker) return false;
	const now = Date.now();
	return marker.isUnread || !marker.isUnread && BigInt(now) - marker.markedReadAtTs < 4e3;
}

//#endregion
//#region src/components/TownsContextProvider.tsx
const log = dlogger("towns:context");
const TownsContext = createContext(void 0);
/**
* use instead of React.useContext, throws if not in a Provider
*/
function useTownsContext() {
	const context = useContext(TownsContext);
	if (!context) throw new Error("useTownsContext must be used in a TownsContextProvider");
	return context;
}
function TownsContextProvider({ QueryClientProvider: QueryClientProvider$1 = QueryProvider,...props }) {
	return /* @__PURE__ */ jsx(QueryClientProvider$1, { children: /* @__PURE__ */ jsx(TownsContextImplMemo, { ...props }) });
}
const TownsContextImpl = (props) => {
	const { townsEnv, environmentId, baseConfig, baseChain, riverConfig, riverChain, streamMetadataUrl } = props;
	const previousProps = useRef(void 0);
	useEffect(() => {
		if (previousProps.current) Object.keys(previousProps.current).forEach((key, i) => {
			if (previousProps.current[key] !== props[key]) log.info("prop changed", i, key);
		});
	});
	previousProps.current = props;
	const { baseProvider } = useBaseReads();
	const riverProvider = useMemo(() => {
		return makeProviderFromConfig(riverChain);
	}, [riverChain]);
	const townsOpts = useMemo(() => {
		return {
			environmentId,
			baseChainId: baseChain.id,
			baseConfig,
			baseProvider,
			riverChainId: riverChain.chainId,
			riverConfig,
			riverProvider,
			accountAbstractionConfig: props.accountAbstractionConfig,
			highPriorityStreamIds: props.highPriorityStreamIds,
			unpackEnvelopeOpts: props.unpackEnvelopeOpts,
			ethMainnetRpcUrl: props.ethMainnetRpcUrl,
			enableSharedSyncer: props.enableSharedSyncer,
			enableEphemeralKeySolicitations: props.enableEphemeralKeySolicitations,
			riverNotificationServiceUrl: props.riverNotificationServiceUrl,
			syncMode: props.syncMode
		};
	}, [
		environmentId,
		baseChain.id,
		baseConfig,
		baseProvider,
		riverChain.chainId,
		riverConfig,
		riverProvider,
		props.accountAbstractionConfig,
		props.highPriorityStreamIds,
		props.unpackEnvelopeOpts,
		props.ethMainnetRpcUrl,
		props.enableSharedSyncer,
		props.enableEphemeralKeySolicitations,
		props.riverNotificationServiceUrl,
		props.syncMode
	]);
	const { client, clientSingleton, casablancaClient, signerContext } = useTownsClientListener(townsOpts);
	const notificationSettingsClient = casablancaClient?.notifications;
	const clientStatus = useClientInitStatus(casablancaClient);
	const { spaces } = useSpaces(townsOpts, casablancaClient);
	useUserLookupUpdater(townsOpts, casablancaClient, client);
	useStreamMetadataUpdater(casablancaClient, client, streamMetadataUrl);
	useCalculateSpaceThreadRoots(townsOpts, casablancaClient);
	useSpaceRollups(casablancaClient);
	const value = useMemo(() => {
		const newValue = {
			townsEnv,
			environmentId,
			client,
			clientSingleton,
			casablancaClient,
			notificationSettingsClient,
			baseConfig,
			baseChain,
			baseProvider,
			riverConfig,
			riverChain,
			riverProvider,
			spaces,
			clientStatus,
			signerContext
		};
		return newValue;
	}, [
		townsEnv,
		environmentId,
		client,
		clientSingleton,
		casablancaClient,
		notificationSettingsClient,
		baseConfig,
		baseChain,
		baseProvider,
		riverConfig,
		riverChain,
		riverProvider,
		spaces,
		clientStatus,
		signerContext
	]);
	const valueRef = useRef(void 0);
	useEffect(() => {
		if (valueRef.current) {}
		valueRef.current = value;
	}, [value]);
	return /* @__PURE__ */ jsx(TownsContext.Provider, {
		value,
		children: props.children
	});
};
const TownsContextImplMemo = React.memo(TownsContextImpl, (prevProps, nextProps) => {
	let result = true;
	Object.keys(prevProps).forEach((key, i) => {
		if (prevProps[key] !== nextProps[key]) {
			log.info("context impl props changed", i, key);
			result = false;
		}
	});
	return result;
});

//#endregion
export { AuthStatus, BaseReadsProvider, BaseTransactor, BaseWritesContext, BaseWritesProvider, BlockchainTransactionType, CreateSpaceFlowStatus, HOUR_MS, InstalledAppsUpdater, JoinFlowStatus, MINUTE_MS, MembershipAlreadyMemberError, MembershipRejectedError, NULL_ADDRESS, QueryProvider, SECOND_MS, SignerUndefinedError, SpaceContext, SpaceContextProvider, TownsContext, TownsContextProvider, TransactionStatus, UserLookupContext, WalletDoesNotMatchSignedInAccountError, WalletStatus, addCategoryToError, addCategoryToErrorIfNotExists, blockchainKeys, bustMembershipStatus, createBaseReadsContext, createBotDataQueryOptions, createTransactionContext, defaultStaleTime, fetchGuardianStatus, fetchMembershipStatus, generateOfflineUserKey, getAppExpiration, getBannedWalletAddresses, getChainName, getChannelPermissionOverrides, getChannelSettings, getChannelSettingsQueryData, getErrorCategory, getInstalledApps, getMembershipFreeAllocation, getMembershipInfo, getMembershipInfoQueryData, getMembershipPrice, getMultipleRoleDetails, getPermissionsByRoleId, getPlatformMembershipFee, getPlatformMembershipFeeFromQueryCache, getPlatformMinMembershipPrice, getPlatformMinMembershipPriceFromQueryCache, getPlatformMintLimit, getPricingModuleQueryData, getPricingModules, getProtocolFee, getRiverPoints, getRoleDetails, getRoleDetailsQueryData, getRoles, getRootKeyFromLinkedWalletCached, getSafeOfflineMembershipStatus, getSingleSubscription, getSpaceDelegationOperator, getSpaceDelegationsByOperator, getSpaceInfo, getSpaceInfoQueryData, getSpaceOwner, getSpaceTips, getStakingDepositDetails, getStakingDepositsByOwner, getStakingRewards, getSubscriptions, getSwapsPaginated, getTokenIdOfOwner, getTownsTokenBalance, invalidateGuardianStatus, invalidateHasPermissionQueriesForSpace, invalidateSpaceInfoQuery, invalidateSpaceOwner, invariant, isDefined, isMembershipExpired, isNullAddress, makeProviderFromChain, makeProviderFromConfig, primaryWalletStore, queryClient, queryOptions$1 as queryOptions, retryOperation, skipErrorDecoding, sleep, sleepUntil, staleTime24Hours, staticAssertNever, toError, useBannedWalletAddresses, useBaseReads, useBaseWrites, useBotData, useCasablancaCredentials, useCasablancaStore, useCasablancaStream, useChannelSettings, useConnectivity, useContractSpaceInfo, useContractSpaceInfoWithoutClient, useContractSpaceInfos, useDefaultOwnershipCooldown, useFetchHasJoinPermission, useGetRootKeyFromLinkedWallet, useGetSingleSubscription, useGetSubscriptions, useGuardianStatus, useHasPermission, useImageStore, useInstalledApps, useIsSpaceOwner, useLinkSmartAccountAndEOAAndSetDefaultWallet, useLinkedAndDelegatedWallets, useMembershipFreeAllocation, useMembershipInfo, useMembershipInfoWithPricingModule, useMembershipPrice, useMembershipRenewalPrice, useMembershipStatus, useMultipleMembershipStatus, useMultipleRoleDetails, useMutation$1 as useMutation, useMyLinkedWallets, useMyWallets, useNetworkStatus, useObservable, useObservableWith, useOfflineStore, useOnHasPermissionQueryUpdate, usePermissionOverrides, usePlatformMembershipFee, usePlatformMembershipPriceForSupplyInEth, usePlatformMinMembershipPrice, usePlatformMintLimit, usePrefetchMultipleRoleDetails, usePricingModules, usePrimaryWallet, useProtocolFee, useQueries$1 as useQueries, useQuery$1 as useQuery, useQueryClient$1 as useQueryClient, useReadAppExpiration, useRiverPointsDetails, useRoleDetails, useRoles, useSetPrimaryWallet, useSpaceContext, useSpaceData, useSpaceDataStore, useSpaceDataWithId, useSpaceDelegationOperator, useSpaceDelegationsByOperator, useSpaceId, useSpaceRollups, useSpaceThreadRoots, useSpaceThreadRootsUnreadCount, useSpaceTips, useSpacesIds, useSpacesIdsFrom, useStakingDepositDetailsQueries, useStakingDepositsByOwnerQuery, useStakingRewardsQuery, useStreamMetadataUpdater, useTokenIdOfOwner, useTownsClient, useTownsContext, useTownsTokenBalance, useTransaction, useUserLookupStore, userLookupFn };
//# sourceMappingURL=TownsContextProvider-Crse5qmy.js.map